import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { BrandFormShell, FormSection, FormStepper } from '../components/BrandFormShell';
import BrandLoadingScreen from '../components/BrandLoadingScreen';
import PasswordConfirmModal from '../components/PasswordConfirmModal';
import { openDocEnhanced, setApiUrl } from '../utils/openDocEnhanced';

const API_URL_FOR_DOCS = process.env.REACT_APP_API_URL || 'http://localhost:5000';
setApiUrl(API_URL_FOR_DOCS);

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Paystack Direct Debit supported banks — CBN-licensed commercial banks
// connected to NIBSS for standing mandates. Fintechs excluded.
const BANK_OPTIONS = [
  { name: 'Access Bank',              code: '044' },
  { name: 'Citibank Nigeria',         code: '023' },
  { name: 'Ecobank Nigeria',          code: '050' },
  { name: 'Fidelity Bank',            code: '070' },
  { name: 'First Bank of Nigeria',    code: '011' },
  { name: 'First City Monument Bank', code: '214' },
  { name: 'Globus Bank',              code: '103' },
  { name: 'Guaranty Trust Bank',      code: '058' },
  { name: 'Heritage Bank',            code: '030' },
  { name: 'Jaiz Bank',                code: '301' },
  { name: 'Keystone Bank',            code: '082' },
  { name: 'Polaris Bank',             code: '076' },
  { name: 'Providus Bank',            code: '101' },
  { name: 'Stanbic IBTC Bank',        code: '221' },
  { name: 'Standard Chartered Bank',  code: '068' },
  { name: 'Sterling Bank',            code: '232' },
  { name: 'Union Bank of Nigeria',    code: '032' },
  { name: 'United Bank for Africa',   code: '033' },
  { name: 'Unity Bank',               code: '215' },
  { name: 'Wema Bank',                code: '035' },
  { name: 'Zenith Bank',              code: '057' },
];

const APPLICATION_STAGES = [
  { key: 'application', label: 'Application', hint: 'Submitted and awaiting review' },
  { key: 'verification', label: 'Verification', hint: 'Admin review and tenant checks' },
  { key: 'upfront_payment', label: 'Pay 30%', hint: 'Initial move-in payment required' },
  { key: 'direct_debit', label: 'Direct Debit', hint: 'Monthly debit authorization is being finalized' },
  { key: 'active', label: 'Active', hint: 'Rental is live and monthly records are active' },
  { key: 'rejected', label: 'Declined', hint: 'Application could not proceed' },
];

const formatCurrency = (value) => `₦${Number(value || 0).toLocaleString('en-NG', { maximumFractionDigits: 2 })}`;
const getStageIndex = (stage) => APPLICATION_STAGES.findIndex((item) => item.key === stage);
const getStageMeta = (stage) => APPLICATION_STAGES.find((item) => item.key === stage) || APPLICATION_STAGES[0];

const PROFILE_WIZARD_STEPS = [
  { key: 'personal', label: 'Personal' },
  { key: 'address', label: 'Address' },
  { key: 'identity', label: 'Identity' },
  { key: 'work', label: 'Work' },
  { key: 'banking', label: 'Banking' },
  { key: 'contacts', label: 'Contacts' },
  { key: 'documents', label: 'Documents' },
  { key: 'review', label: 'Review' },
];

const sharedProfileFields = [
  'applicantType',
  'fullName', 'phone', 'email', 'dateOfBirth', 'gender', 'maritalStatus', 'numberOfDependents',
  'currentAddress', 'durationAtCurrentAddressYears', 'previousAddress',
  'nin', 'bvn',
  'bankName', 'accountNumber', 'bankAccountName',
  'nextOfKinFullName', 'nextOfKinPhone', 'nextOfKinRelationship', 'nextOfKinAddress',
  'emergencyContactName', 'emergencyContactPhone', 'emergencyContactRelationship',
  'guarantor1FullName', 'guarantor1Phone', 'guarantor1Relationship', 'guarantor1Occupation', 'guarantor1Employer', 'guarantor1Address',
  'guarantor2FullName', 'guarantor2Phone', 'guarantor2Relationship', 'guarantor2Occupation', 'guarantor2Employer', 'guarantor2Address',
  'idDocUrl', 'passportPhotoUrl', 'utilityBillUrl', 'guarantor1IdUrl', 'guarantor2IdUrl',
];

const employedProfileFields = [
  'employmentStatus', 'employerName', 'jobTitle', 'monthlyNetIncome',
  'proofOfIncomeUrl', 'bankStatementUrl', 'employmentLetterUrl',
];

const studentProfileFields = [
  'institutionName', 'institutionType', 'faculty', 'department', 'studentLevel', 'matricNumber',
  'parentFullName', 'parentPhone', 'parentEmail', 'parentRelationship', 'parentOccupation', 'parentEmployer',
  'parentAddress', 'parentNin', 'parentBvn', 'parentBankName', 'parentAccountNumber', 'parentBankAccountName',
  'studentIdUrl', 'schoolFeesReceiptUrl', 'admissionLetterUrl', 'parentIdUrl', 'parentConsentUrl',
  'parentProofOfEmploymentUrl', 'parentBankStatementUrl',
];

const nyscProfileFields = [
  'nyscStateCode', 'nyscCallUpNumber', 'nyscBatch', 'nyscServiceState', 'nyscServiceLga',
  'nyscPpaName', 'nyscPpaAddress', 'nyscPpaType',
  'nyscAllaweeBank', 'nyscAllaweeAccount', 'nyscAllaweeName',
  'nyscCallUpLetterUrl', 'nyscDeploymentLetterUrl', 'nyscIdCardUrl',
];

const getRequiredProfileFields = (profileData = {}) => {
  const atype = profileData.applicantType || 'employed';
  if (atype === 'student') return [...sharedProfileFields, ...studentProfileFields];
  if (atype === 'nysc') return [...sharedProfileFields, ...nyscProfileFields];
  return [...sharedProfileFields, ...employedProfileFields];
};

const getProfileDocumentsForType = (atype = 'employed') => {
  const base = [
    ['idDocUrl', '🪪 Government ID'],
    ['passportPhotoUrl', '🤳 Passport Photo'],
    ['utilityBillUrl', '🏠 Utility Bill'],
    ['guarantor1IdUrl', '🤝 Guarantor 1 ID'],
    ['guarantor2IdUrl', '🤝 Guarantor 2 ID'],
  ];
  if (atype === 'student') {
    return [
      ...base,
      ['studentIdUrl', '🎓 Student ID'],
      ['admissionLetterUrl', '📄 Admission Letter'],
      ['schoolFeesReceiptUrl', '🧾 School Fees / Course Reg.'],
      ['parentIdUrl', '👪 Parent/Guardian ID'],
      ['parentConsentUrl', '✍️ Parent/Guardian Consent'],
      ['parentProofOfEmploymentUrl', '💼 Parent Employment / Business Proof'],
      ['parentBankStatementUrl', '🏦 Parent Bank Statement'],
    ];
  }
  if (atype === 'nysc') {
    return [
      ...base,
      ['nyscCallUpLetterUrl', '🟢 NYSC Call-Up Letter'],
      ['nyscDeploymentLetterUrl', '📍 Deployment Letter'],
      ['nyscIdCardUrl', '🪪 NYSC ID Card'],
    ];
  }
  return [
    ...base,
    ['proofOfIncomeUrl', '💰 Income Proof'],
    ['bankStatementUrl', '🏦 Bank Statement'],
    ['employmentLetterUrl', '📄 Employment / Business Letter'],
  ];
};

const isMaskedSensitiveValue = (value) => {
  const str = String(value || '');
  return str.includes('•') || str.includes('\u00e2\u20ac\u00a2');
};

const WizardInput = ({ label, hint, required, ...props }) => (
  <label className="block">
    <span className="block text-sm font-semibold text-[#294c79] mb-2">{label}{required ? <span className="text-red-600"> *</span> : null}</span>
    <input
      {...props}
      className={`w-full px-4 py-3 rounded-2xl border border-[#d9e5ef] bg-white focus:outline-none focus:ring-4 focus:ring-[#294c79]/10 focus:border-[#294c79] transition ${props.className || ''}`}
    />
    {hint ? <span className="mt-1.5 block text-xs text-[#1a1a1a]/55">{hint}</span> : null}
  </label>
);

const WizardSelect = ({ label, required, children, ...props }) => (
  <label className="block">
    <span className="block text-sm font-semibold text-[#294c79] mb-2">{label}{required ? <span className="text-red-600"> *</span> : null}</span>
    <select
      {...props}
      className={`w-full px-4 py-3 rounded-2xl border border-[#d9e5ef] bg-white focus:outline-none focus:ring-4 focus:ring-[#294c79]/10 focus:border-[#294c79] transition ${props.className || ''}`}
    >
      {children}
    </select>
  </label>
);

// ─── Receipt PDF Generator ─────────────────────────────────────
const generateReceiptHTML = (payment, profileData) => {
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8"/>
      <title>Payment Receipt - ${payment.receipt_number}</title>
      <style>
        body { font-family: 'Helvetica Neue', Arial, sans-serif; margin: 0; padding: 40px; color: #1a1a1a; background: #fff; }
        .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 40px; padding-bottom: 24px; border-bottom: 3px solid #294c79; }
        .brand { display: flex; flex-direction: column; }
        .brand-name { font-size: 28px; font-weight: 800; color: #294c79; letter-spacing: -0.5px; }
        .brand-sub { font-size: 12px; color: #666; margin-top: 2px; }
        .receipt-badge { background: #a5c250; color: white; padding: 8px 20px; border-radius: 30px; font-weight: 700; font-size: 14px; text-transform: uppercase; letter-spacing: 1px; }
        .receipt-title { font-size: 22px; font-weight: 700; color: #294c79; margin-bottom: 6px; }
        .receipt-num { font-size: 14px; color: #666; margin-bottom: 4px; }
        .section { margin-bottom: 28px; }
        .section-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1.5px; color: #999; margin-bottom: 12px; }
        .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .field { background: #f8f9fa; padding: 12px 16px; border-radius: 8px; }
        .field-label { font-size: 11px; color: #888; margin-bottom: 4px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
        .field-value { font-size: 14px; font-weight: 600; color: #1a1a1a; }
        .amount-box { background: #294c79; color: white; padding: 24px 32px; border-radius: 12px; text-align: center; margin: 28px 0; }
        .amount-label { font-size: 13px; opacity: 0.8; margin-bottom: 6px; }
        .amount-value { font-size: 36px; font-weight: 800; letter-spacing: -1px; }
        .status-paid { display: inline-block; background: #d1fae5; color: #065f46; padding: 6px 16px; border-radius: 20px; font-weight: 700; font-size: 13px; margin-top: 10px; }
        .footer { margin-top: 48px; padding-top: 24px; border-top: 1px solid #e5e7eb; display: flex; justify-content: space-between; align-items: center; }
        .footer-note { font-size: 12px; color: #999; max-width: 400px; }
        .footer-logo { font-size: 18px; font-weight: 800; color: #294c79; }
        @media print { body { padding: 20px; } }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="brand">
          <div class="brand-name">LiteRent</div>
          <div class="brand-sub">The Easier Way</div>
        </div>
        <div style="text-align:right">
          <div class="receipt-badge">Official Receipt</div>
        </div>
      </div>
      <div class="section">
        <div class="receipt-title">Payment Receipt</div>
        <div class="receipt-num">Receipt No: <strong>${payment.receipt_number || 'N/A'}</strong></div>
        <div class="receipt-num">Date: <strong>${payment.paid_at ? new Date(payment.paid_at).toLocaleDateString('en-NG', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}</strong></div>
      </div>
      <div class="amount-box">
        <div class="amount-label">Amount Paid</div>
        <div class="amount-value">₦${parseFloat(payment.amount).toLocaleString('en-NG', { minimumFractionDigits: 2 })}</div>
        <div class="status-paid">✓ PAYMENT CONFIRMED</div>
      </div>
      <div class="section">
        <div class="section-title">Tenant Information</div>
        <div class="grid">
          <div class="field"><div class="field-label">Full Name</div><div class="field-value">${payment.full_name || profileData?.fullName || 'N/A'}</div></div>
          <div class="field"><div class="field-label">Phone</div><div class="field-value">${payment.phone || profileData?.phone || 'N/A'}</div></div>
          <div class="field"><div class="field-label">Email</div><div class="field-value">${payment.email || profileData?.email || 'N/A'}</div></div>
          <div class="field"><div class="field-label">Residential Address</div><div class="field-value">${payment.current_address || profileData?.currentAddress || 'N/A'}</div></div>
        </div>
      </div>
      <div class="section">
        <div class="section-title">Property Information</div>
        <div class="grid">
          <div class="field"><div class="field-label">Property</div><div class="field-value">${payment.house_title || 'N/A'}</div></div>
          <div class="field"><div class="field-label">Location</div><div class="field-value">${payment.location || 'N/A'}</div></div>
          <div class="field"><div class="field-label">Payment Type</div><div class="field-value">${payment.description || payment.type || 'Rent'}</div></div>
          <div class="field"><div class="field-label">Instalment</div><div class="field-value">${payment.instalment_number ? `Month ${payment.instalment_number} of 12` : 'N/A'}</div></div>
        </div>
      </div>
      <div class="section">
        <div class="section-title">Transaction Details</div>
        <div class="grid">
          <div class="field"><div class="field-label">Paystack Reference</div><div class="field-value" style="font-size:12px">${payment.paystack_reference || 'N/A'}</div></div>
          <div class="field"><div class="field-label">Transaction Date</div><div class="field-value">${payment.paid_at ? new Date(payment.paid_at).toLocaleString('en-NG') : 'N/A'}</div></div>
        </div>
      </div>
      <div class="footer">
        <div class="footer-note">This is an official payment receipt from LiteRent. Please keep this for your records. For enquiries, contact support@literent.com.ng</div>
        <div class="footer-logo">LiteRent</div>
      </div>
    </body>
    </html>
  `;
};

// ─── DocumentRequests — shows admin-requested docs + upload in tenant app card ─
function DocumentRequests({ appId, API_URL, getHeaders }) {
  const [requests, setRequests] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [uploading, setUploading] = React.useState({});

  const load = React.useCallback(() => {
    if (!appId) return;
    axios.get(`${API_URL}/api/applications/${appId}/document-requests`, { headers: getHeaders() })
      .then(r => setRequests(r.data.requests || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [appId]);

  React.useEffect(() => { load(); }, [load]);

  const handleUpload = async (reqId, file) => {
    if (!file) return;
    if (file.size > 20971520) { alert('Please upload a file smaller than 20MB.'); return; }
    setUploading(p => ({ ...p, [reqId]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API_URL}/api/document-requests/${reqId}/upload`, formData, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Upload failed. Please try again.');
    } finally {
      setUploading(p => ({ ...p, [reqId]: false }));
    }
  };

  const pending = requests.filter(r => r.status === 'pending');
  const uploaded = requests.filter(r => r.status === 'uploaded');

  if (loading || requests.length === 0) return null;

  return (
    <div className="space-y-3">
      {pending.length > 0 && (
        <div className="p-4 rounded-2xl border-2 border-amber-300 bg-amber-50">
          <p className="font-semibold text-amber-800 mb-3">📎 {pending.length} Additional Document{pending.length > 1 ? 's' : ''} Required by LiteRent</p>
          <div className="space-y-3">
            {pending.map(req => (
              <div key={req.id} className="bg-white rounded-xl border border-amber-200 p-3">
                <p className="font-semibold text-sm text-[#294c79]">{req.document_name}</p>
                {req.description && <p className="text-xs text-gray-500 mt-0.5 mb-2">{req.description}</p>}
                <label className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold cursor-pointer transition ${uploading[req.id] ? 'bg-gray-200 text-gray-500' : 'bg-[#294c79] text-white hover:bg-[#1f3a5c]'}`}>
                  {uploading[req.id] ? '⏳ Uploading…' : '📤 Upload Document'}
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.heic,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.bmp,.tiff" className="hidden" disabled={uploading[req.id]}
                    onChange={e => handleUpload(req.id, e.target.files[0])} />
                </label>
                <p className="text-xs text-gray-400 mt-1">Accepted: PDF, JPG, PNG, Word, Excel and other common formats (max 20MB)</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {uploaded.length > 0 && (
        <div className="p-3 rounded-2xl border border-green-200 bg-green-50 text-sm text-green-700">
          <p className="font-semibold mb-1">✅ Uploaded Documents</p>
          <div className="space-y-1">
            {uploaded.map(req => (
              <div key={req.id} className="flex items-center justify-between gap-2">
                <span className="text-xs">{req.document_name}</span>
                <button onClick={() => openDoc(req.uploaded_url)} className="text-xs text-[#294c79] underline font-semibold">View →</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ── UploadCard — category selector + file input ───────────────────────────────
// fileCategory: 'image' | 'pdf' | 'doc' | 'sheet'
const FILE_CATS = [
  { key: 'image', label: 'Image',    icon: '🖼️',  accept: '.jpg,.jpeg,.png,.gif,.webp,.heic,.heif,.bmp,.tiff,.tif,.svg', hint: 'JPG, PNG, WEBP, HEIC…' },
  { key: 'pdf',   label: 'PDF',      icon: '📄',  accept: '.pdf',                                                         hint: 'PDF document' },
  { key: 'doc',   label: 'Word',     icon: '📝',  accept: '.doc,.docx,.odt,.txt',                                         hint: 'DOC, DOCX, ODT' },
  { key: 'sheet', label: 'Sheet',    icon: '📊',  accept: '.xls,.xlsx,.ods,.csv,.ppt,.pptx',                              hint: 'XLS, XLSX, PPT, CSV' },
];

function UploadCard({ docKey, label, hint, required, url, uploading, onUpload }) {
  const [cat, setCat] = React.useState('image');
  const fileInputRef = React.useRef(null);

  // Detect category from stored URL hash on load
  React.useEffect(() => {
    if (url) {
      const m = url.match(/#cat=(image|pdf|doc|sheet)/);
      if (m) setCat(m[1]);
    }
  }, [url]);

  const chosen = FILE_CATS.find(c => c.key === cat) || FILE_CATS[0];
  const cleanUrl = url ? url.split('#')[0] : null; // strip hash for display/viewing

  // Read category from stored URL for viewing
  const storedCat = url ? (url.match(/#cat=(image|pdf|doc|sheet)/)?.[1] || 'image') : cat;
  const storedMeta = FILE_CATS.find(c => c.key === storedCat) || FILE_CATS[0];

  return (
    <div className={`border rounded-2xl p-3.5 transition ${url ? 'border-green-300 bg-green-50' : 'border-[#d9e5ef] bg-white'}`}>

      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <p className="font-semibold text-[#294c79] text-sm">{label}{required ? <span className="text-red-500"> *</span> : null}</p>
          <p className="text-xs text-[#1a1a1a]/45 mt-0.5">{hint}</p>
        </div>
        {url && <span className="text-[10px] font-bold text-green-700 bg-green-100 rounded-full px-2 py-0.5 flex-shrink-0">✓</span>}
      </div>

      {/* Uploaded file preview */}
      {url && (
        <div className="mb-3 flex items-center gap-2 bg-white rounded-xl px-3 py-2 border border-green-200">
          <span className="text-lg">{storedMeta.icon}</span>
          <span className="text-xs text-green-700 font-semibold flex-1">{storedMeta.label} uploaded</span>
          <button
            onClick={() => openDoc(url)}
            className="text-xs text-[#294c79] underline font-semibold hover:text-[#1f3a5c]"
          >
            View
          </button>
        </div>
      )}

      {/* Category selector */}
      <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
        {url ? 'Replace with:' : 'File type:'}
      </p>
      <div className="grid grid-cols-4 gap-1 mb-2">
        {FILE_CATS.map(c => (
          <button
            key={c.key}
            type="button"
            onClick={() => { setCat(c.key); if (fileInputRef.current) fileInputRef.current.value = ''; }}
            className={`flex flex-col items-center py-1.5 px-1 rounded-lg border text-[10px] font-semibold transition ${cat === c.key ? 'border-[#294c79] bg-[#294c79] text-white' : 'border-gray-200 bg-white text-gray-500 hover:border-[#294c79] hover:text-[#294c79]'}`}
          >
            <span className="text-base mb-0.5">{c.icon}</span>
            {c.label}
          </button>
        ))}
      </div>
      <p className="text-[10px] text-gray-400 text-center mb-2">{chosen.hint} · max 20MB</p>

      {/* Upload button */}
      <label className={`cursor-pointer flex items-center justify-center gap-1.5 rounded-xl px-3 py-2.5 text-xs font-semibold transition w-full ${uploading ? 'bg-gray-200 text-gray-500 cursor-wait' : 'bg-[#294c79] text-white hover:bg-[#1f3a5c]'}`}>
        {uploading ? '⏳ Uploading…' : url ? `↑ Replace ${chosen.label}` : `↑ Upload ${chosen.label}`}
        <input
          ref={fileInputRef}
          type="file"
          accept={chosen.accept}
          className="hidden"
          disabled={uploading}
          onChange={e => { if (e.target.files[0]) onUpload(e.target.files[0], cat); }}
        />
      </label>
    </div>
  );
}

/**
 * Enhanced Document Viewer
 * Provides inline viewing for PDFs, Word documents, and other file types
 * with a modal interface similar to the image lightbox
 */

// ── openDoc delegates to openDocEnhanced for inline modal viewing ────────────
function openDoc(rawUrl) {
  openDocEnhanced(rawUrl);
}



function ChangeDebitAccountForm({ appId, API_URL, getToken, bankOptions, onRequested }) {
  const [open, setOpen] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const [form, setForm] = React.useState({ reason: '', newBankName: '', newAccountNumber: '', newAccountName: '' });
  const [resolving, setResolving] = React.useState(false);
  const [acctError, setAcctError] = React.useState('');

  React.useEffect(() => {
    if (form.newBankName && form.newAccountNumber.length === 10) {
      setResolving(true); setAcctError('');
      const found = bankOptions.find(b => (b.name || b) === form.newBankName);
      const bankCode = found?.code || '';
      if (!bankCode) { setResolving(false); return; }
      const token = getToken();
      axios.post(`${API_URL}/api/banks/resolve-account`,
        { bankCode, accountNumber: form.newAccountNumber },
        { headers: { Authorization: `Bearer ${token}` } }
      )
        .then(r => setForm(prev => ({ ...prev, newAccountName: r.data.account_name || '' })))
        .catch(e => setAcctError(e.response?.data?.error || 'Could not verify account'))
        .finally(() => setResolving(false));
    }
  }, [form.newBankName, form.newAccountNumber]);

  const handleSubmit = async () => {
    if (!appId) { alert('Application ID not found. Please refresh the page and try again.'); return; }
    if (!form.reason.trim()) { alert('Please provide a reason for the account change.'); return; }
    if (!form.newBankName) { alert('Please select your new bank.'); return; }
    if (form.newAccountNumber.length !== 10) { alert('Please enter a valid 10-digit account number.'); return; }
    if (!form.newAccountName) { alert('Account name could not be verified. Please check your account number.'); return; }
    setSubmitting(true);
    try {
      const token = getToken();
      await axios.post(
        `${API_URL}/api/applications/${appId}/change-debit-account`,
        { reason: form.reason, newBankName: form.newBankName, newAccountNumber: form.newAccountNumber, newAccountName: form.newAccountName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDone(true);
      onRequested && onRequested();
    } catch (err) {
      alert(err.response?.data?.error || 'Could not submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) return (
    <div className="p-4 rounded-2xl border border-green-200 bg-green-50 text-sm text-green-700">
      ✅ <strong>Account change request submitted.</strong> Admin will review and send you a Paystack authorisation link. Check your email and notifications.
    </div>
  );

  if (!open) return (
    <div className="p-4 rounded-2xl border border-[#d9e5ef] bg-[#f8fbff] text-sm">
      <p className="font-semibold text-[#294c79] mb-1">🏦 Change Direct Debit Account</p>
      <p className="text-[#1a1a1a]/60 text-xs mb-3">Want to change the bank account your rent is debited from? Submit a request and admin will approve it.</p>
      <button onClick={() => setOpen(true)} className="px-5 py-2.5 border border-[#294c79] text-[#294c79] rounded-xl text-xs font-semibold hover:bg-[#f3f7fb] transition">
        🔄 Request Account Change
      </button>
    </div>
  );

  return (
    <div className="p-4 rounded-2xl border border-[#294c79]/30 bg-[#f8fbff] text-sm space-y-3">
      <p className="font-semibold text-[#294c79]">🔄 Request Direct Debit Account Change</p>
      <div>
        <label className="block text-xs font-semibold text-[#294c79] mb-1">Reason for change <span className="text-red-500">*</span></label>
        <textarea value={form.reason} onChange={e => setForm(p => ({ ...p, reason: e.target.value }))} rows={2} placeholder="e.g. Switching to a different bank account with more funds" className="w-full px-3 py-2.5 border-2 border-[#d9e5ef] rounded-xl text-sm focus:outline-none focus:border-[#294c79] resize-none" />
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#294c79] mb-1">New Bank <span className="text-red-500">*</span></label>
        <select value={form.newBankName} onChange={e => setForm(p => ({ ...p, newBankName: e.target.value, newAccountName: '' }))} className="w-full px-3 py-2.5 border-2 border-[#d9e5ef] rounded-xl text-sm focus:outline-none focus:border-[#294c79] bg-white">
          <option value="">Select bank…</option>
          {bankOptions.map(b => { const n = typeof b === 'string' ? b : (b.name || ''); return n ? <option key={n} value={n}>{n}</option> : null; })}
        </select>
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#294c79] mb-1">New Account Number <span className="text-red-500">*</span></label>
        <input type="text" inputMode="numeric" maxLength={10} value={form.newAccountNumber} onChange={e => setForm(p => ({ ...p, newAccountNumber: e.target.value.replace(/\D/g,'').slice(0,10), newAccountName: '' }))} placeholder="10-digit account number" className="w-full px-3 py-2.5 border-2 border-[#d9e5ef] rounded-xl text-sm focus:outline-none focus:border-[#294c79]" />
        {form.newAccountNumber.length === 10 && (
          <div className={`mt-2 px-3 py-2 rounded-xl text-xs font-semibold ${resolving ? 'bg-gray-50 text-gray-500' : form.newAccountName ? 'bg-green-50 border border-green-200 text-green-700' : acctError ? 'bg-red-50 border border-red-200 text-red-600' : ''}`}>
            {resolving ? '⏳ Verifying…' : form.newAccountName ? `✅ ${form.newAccountName}` : acctError || ''}
          </div>
        )}
      </div>
      <div className="flex gap-2 pt-1">
        <button onClick={handleSubmit} disabled={submitting || !form.newAccountName || !form.reason.trim()} className="px-5 py-2.5 bg-[#294c79] text-white rounded-xl text-xs font-semibold hover:bg-[#1f3a5c] disabled:opacity-50 transition">
          {submitting ? '⏳ Submitting…' : '📋 Submit Request'}
        </button>
        <button onClick={() => { setOpen(false); setForm({ reason: '', newBankName: '', newAccountNumber: '', newAccountName: '' }); }} className="px-4 py-2.5 border border-gray-300 rounded-xl text-xs text-gray-600 hover:bg-gray-50">Cancel</button>
      </div>
    </div>
  );
}

export default function TenantDashboard() {
  const [activeTab, setActiveTab] = useState('home');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [profileSubTab, setProfileSubTab] = useState('profile');
  const [profileWizardStep, setProfileWizardStep] = useState('personal');
  const [profileSaveError, setProfileSaveError] = useState('');
  const [profileSaveSuccess, setProfileSaveSuccess] = useState('');
  const [profileAutoSaveStatus, setProfileAutoSaveStatus] = useState('');
  const isSavingProfile = useRef(false);
  const profileLoadedRef = useRef(false);
  const profileAutoSaveTimer = useRef(null);
  const profileDirtyPatch = useRef({});

  const navigate = useNavigate();
  const { user, logout, loading: authLoading } = useContext(AuthContext);

  // ── Get token fresh every time (FIXED) ──
  const getToken = useCallback(() => localStorage.getItem('authToken'), []);
  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` }), []);

  // Loading states
  const [loading, setLoading] = useState(false);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  const [rentalsLoading, setRentalsLoading] = useState(false);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [debitConsentLoading, setDebitConsentLoading] = useState({});
  const [debitConsentSuccess, setDebitConsentSuccess] = useState({});
  const [guarantorFormLoading, setGuarantorFormLoading] = useState({});
  const [businessOwnerFormLoading, setBusinessOwnerFormLoading] = useState(false);
  const [signedFormUploading, setSignedFormUploading] = useState({});
  const [expandedTenantAppId, setExpandedTenantAppId] = useState(null);
  const [expandedSubmissionId, setExpandedSubmissionId] = useState(null); // FIX #2: which My-Submissions card is open
  const [expandedRentalId, setExpandedRentalId] = useState(null); // which active-lease card is expanded
  const [rentalDetails, setRentalDetails] = useState({}); // map rentalId -> { rental, payments }
  const [rentalDetailLoading, setRentalDetailLoading] = useState({}); // map rentalId -> bool
  const [sensitiveConfirm, setSensitiveConfirm] = useState(null); // FIX #8: password-confirm modal state
  const [employerLetterLoading, setEmployerLetterLoading] = useState({});
  const [monoLinking, setMonoLinking] = useState(false);

  // Data states
  const [profileData, setProfileData] = useState({});
  const [bankOptions, setBankOptions] = useState(BANK_OPTIONS);
  const [resolvingAccountName, setResolvingAccountName] = useState(false);
  const [applications, setApplications] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [payments, setPayments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLogsLoading, setActivityLogsLoading] = useState(false);
  // PDF documents tab
  const [tenantPdfs, setTenantPdfs] = useState([]);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [pdfLabel, setPdfLabel] = useState('');
  const [dashboardStats, setDashboardStats] = useState(null);

  // Notification filters
  const [notifFilter, setNotifFilter] = useState('all');
  const [unreadCount, setUnreadCount] = useState(0);

  // Tickets state
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketReplies, setTicketReplies] = useState([]);
  const [ticketReply, setTicketReply] = useState('');
  const [ticketLoading, setTicketLoading] = useState(false);
  const [showNewTicketForm, setShowNewTicketForm] = useState(false);
  const [newTicket, setNewTicket] = useState({ subject: '', message: '', category: 'general', priority: 'normal' });

  // Password change
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '', otp: '' });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpCountdown, setOtpCountdown] = useState(0);
  const [otpEmailHint, setOtpEmailHint] = useState('');

  // Document upload states
  const [docUploading, setDocUploading] = useState({});
  const [docSuccess, setDocSuccess] = useState({});

  // Upload house state
  const [houseImages, setHouseImages] = useState([]);
  const [houseImagePreviews, setHouseImagePreviews] = useState([]);
  const houseImagesRef = useRef(null);
  const [mySubmissions, setMySubmissions] = useState([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);

  // Payments state
  const [paymentLoading, setPaymentLoading] = useState({});
  const [receiptLoading, setReceiptLoading] = useState({});

  const [isProfileComplete, setIsProfileComplete] = useState(false);

  const profileStepFieldMap = {
    personal: ['fullName', 'phone', 'email', 'dateOfBirth', 'gender', 'maritalStatus', 'numberOfDependents'],
    address: ['currentAddress', 'durationAtCurrentAddressYears', 'previousAddress'],
    identity: ['nin', 'bvn'],
    work: ['employmentStatus', 'employerName', 'jobTitle', 'monthlyNetIncome'],
    banking: ['bankName', 'accountNumber', 'bankAccountName'],
    parent: ['parentFullName', 'parentPhone', 'parentEmail', 'parentRelationship', 'parentOccupation', 'parentEmployer', 'parentAddress', 'parentNin', 'parentBvn', 'parentBankName', 'parentAccountNumber', 'parentBankAccountName'],
    nysc_allawee_banking: ['nyscAllaweeBank', 'nyscAllaweeAccount', 'nyscAllaweeName'],
    contacts: [
      'nextOfKinFullName', 'nextOfKinPhone', 'nextOfKinRelationship', 'nextOfKinAddress',
      'emergencyContactName', 'emergencyContactPhone', 'emergencyContactRelationship',
    ],
    guarantors: [
      'guarantor1FullName', 'guarantor1Phone', 'guarantor1Relationship', 'guarantor1Occupation', 'guarantor1Employer', 'guarantor1Address',
      'guarantor2FullName', 'guarantor2Phone', 'guarantor2Relationship', 'guarantor2Occupation', 'guarantor2Employer', 'guarantor2Address',
    ],
    documents: getProfileDocumentsForType(profileData.applicantType).map(([key]) => key),
  };

  const profileFieldLabels = {
    fullName: 'Full Name', phone: 'Phone Number', email: 'Email Address',
    dateOfBirth: 'Date of Birth', gender: 'Gender',
    currentAddress: 'Current Address',
    nin: 'NIN (11 digits)', bvn: 'BVN (11 digits)',
    employmentStatus: 'Employment Status', employerName: 'Employer Name', monthlyNetIncome: 'Monthly Income',
    bankName: 'Bank Name', accountNumber: 'Account Number (10 digits)', bankAccountName: 'Account Name',
    nextOfKinFullName: 'Next of Kin Name', nextOfKinPhone: 'Next of Kin Phone',
    emergencyContactName: 'Emergency Contact Name', emergencyContactPhone: 'Emergency Contact Phone',
    guarantor1FullName: 'Guarantor 1 Full Name', guarantor1Phone: 'Guarantor 1 Phone', guarantor1Address: 'Guarantor 1 Address',
    guarantor2FullName: 'Guarantor 2 Full Name', guarantor2Phone: 'Guarantor 2 Phone', guarantor2Address: 'Guarantor 2 Address',
    idDocUrl: 'Government ID Document', passportPhotoUrl: 'Passport Photo', utilityBillUrl: 'Utility Bill',
    guarantor1IdUrl: 'Guarantor 1 ID', guarantor2IdUrl: 'Guarantor 2 ID',
    studentIdUrl: 'Student ID', schoolFeesReceiptUrl: 'School Fees Receipt', admissionLetterUrl: 'Admission Letter',
    parentIdUrl: 'Parent/Guardian ID', parentConsentUrl: 'Parent/Guardian Consent',
    parentProofOfEmploymentUrl: 'Proof of Parent Employment/Business', parentBankStatementUrl: 'Parent Bank Statement',
    nyscCallUpLetterUrl: 'NYSC Call-Up Letter', nyscDeploymentLetterUrl: 'NYSC Deployment Letter', nyscIdCardUrl: 'NYSC ID Card',
  };

  const requiredFields = getRequiredProfileFields(profileData);

  const profileFieldIsComplete = (field, value) => {
    const str = String(value ?? '');
    // Masked sensitive fields (returned as ••••••••123 from the backend) are complete by definition
    if (isMaskedSensitiveValue(str)) return true;
    if (field === 'nin' || field === 'bvn') return str.replace(/\D/g, '').length === 11;
    if (field === 'accountNumber') return str.replace(/\D/g, '').length === 10;
    return value !== null && value !== undefined && str.trim() !== '';
  };

  // ── Axios 401 interceptor — auto-logout if token expires mid-session ──
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      res => res,
      err => {
        if (err.response?.status === 401) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          navigate('/login', { replace: true });
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [navigate]);

  // ── FIXED: Wait for AuthContext to finish loading before checking auth ──
  // Load Mono Connect SDK
  useEffect(() => {
    if (document.getElementById('mono-connect-sdk')) return;
    const script = document.createElement('script');
    script.id = 'mono-connect-sdk';
    script.src = 'https://connect.withmono.com/connect.js';
    script.async = true;
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    if (authLoading) return;
    const token = getToken();
    if (!token) {
      navigate('/login', { replace: true });
    } else {
      loadProfile();
    }
  }, [authLoading, navigate, getToken]);

  useEffect(() => {
    const complete = requiredFields.every((field) => profileFieldIsComplete(field, profileData[field]));
    setIsProfileComplete(complete);
  }, [profileData]);

  useEffect(() => {
    loadBanks();
  }, []);

  // FIX 1: Auto-verify payment / handle mandate completion after Paystack redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const verifyRef = params.get('verify');
    const paymentId = params.get('payment_id');
    const mandateDone = params.get('mandate_done');
    const applicationId = params.get('application_id');
    const tabParam = params.get('tab');

    if (tabParam) setActiveTab(tabParam);

    if (verifyRef && paymentId) {
      const token = localStorage.getItem('authToken');
      axios.post(`${API_URL}/api/payments/verify`,
        { reference: verifyRef, payment_id: parseInt(paymentId) },
        { headers: { Authorization: `Bearer ${token}` } }
      ).then(async (res) => {
        if (res.data.status === 'success') {
          await loadPayments();
          await loadDashboardStats();
          await loadApplications();
        }
      }).catch(() => {}).finally(() => {
        window.history.replaceState({}, '', '/dashboard');
      });
    }

    if (mandateDone && applicationId) {
      loadApplications().finally(() => {
        window.history.replaceState({}, '', '/dashboard');
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const accountNumber = (profileData.accountNumber || '').replace(/\D/g, '');
    if (!profileData.bankName || accountNumber.length !== 10) return;
    const timer = setTimeout(() => {
      resolveBankAccountName(profileData.bankName, accountNumber);
    }, 450);
    return () => clearTimeout(timer);
  }, [profileData.bankName, profileData.accountNumber]);

  useEffect(() => {
    const accountNumber = (profileData.parentAccountNumber || '').replace(/\D/g, '');
    if (!profileData.parentBankName || accountNumber.length !== 10) return;
    const timer = setTimeout(async () => {
      try {
        setResolvingAccountName(true);
        const res = await axios.post(`${API_URL}/api/banks/resolve-account`, { bankName: profileData.parentBankName, accountNumber }, { headers: getHeaders() });
        const name = res.data?.accountName || res.data?.account_name || '';
        if (name) {
          profileDirtyPatch.current = { ...profileDirtyPatch.current, parentBankAccountName: name };
          setProfileData(prev => ({ ...prev, parentBankAccountName: name }));
        }
      } catch {
        // user can retry by editing the account number
      } finally {
        setResolvingAccountName(false);
      }
    }, 450);
    return () => clearTimeout(timer);
  }, [profileData.parentBankName, profileData.parentAccountNumber]);

  // Auto-resolve NYSC allawee account name
  useEffect(() => {
    const acct = (profileData.nyscAllaweeAccount || '').replace(/\D/g, '');
    if (!profileData.nyscAllaweeBank || acct.length !== 10) return;
    const timer = setTimeout(async () => {
      try {
        setResolvingAccountName(true);
        const res = await axios.post(`${API_URL}/api/banks/resolve-account`, { bankName: profileData.nyscAllaweeBank, accountNumber: acct }, { headers: getHeaders() });
        const name = res.data?.accountName || res.data?.account_name || '';
        if (name) {
          profileDirtyPatch.current = { ...profileDirtyPatch.current, nyscAllaweeName: name };
          setProfileData(prev => ({ ...prev, nyscAllaweeName: name }));
        }
      } catch {
        // silent fail — user can type name manually
      } finally {
        setResolvingAccountName(false);
      }
    }, 450);
    return () => clearTimeout(timer);
  }, [profileData.nyscAllaweeBank, profileData.nyscAllaweeAccount]);

  useEffect(() => {
    const token = getToken();
    if (!token) return;
    if (activeTab === 'home') loadDashboardStats();
    if (activeTab === 'my-applications') { loadApplications(); loadPayments(); }
    if (activeTab === 'my-rentals') loadRentals();
    if (activeTab === 'payments') loadPayments();
    if (activeTab === 'notifications') loadNotifications();
    if (activeTab === 'support') loadTickets();
    if (activeTab === 'upload-house') loadMySubmissions();
    if (activeTab === 'activity') loadActivityLogs();
    if (activeTab === 'documents') loadTenantPdfs();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'notifications') loadNotifications();
  }, [notifFilter]);


  useEffect(() => {
    if (profileSubTab === 'update' && !isSavingProfile.current) {
      // FIX #14: Always land on the profile-type selection step when Update Profile
      // is opened, so the tenant confirms/changes their applicant type first.
      // Previously we skipped this step whenever applicantType was already set,
      // which meant returning tenants never got a chance to review or change it.
      setProfileWizardStep('type');
      setProfileSaveError('');
      setProfileSaveSuccess('');
    }
  }, [profileSubTab]);

  useEffect(() => {
    if (!profileLoadedRef.current || profileSubTab !== 'update' || isSavingProfile.current) return;
    const dirty = profileDirtyPatch.current;
    if (!Object.keys(dirty).length) return;
    if (profileAutoSaveTimer.current) clearTimeout(profileAutoSaveTimer.current);
    setProfileAutoSaveStatus('Saving...');
    profileAutoSaveTimer.current = setTimeout(async () => {
      try {
        const patch = { ...profileDirtyPatch.current };
        profileDirtyPatch.current = {};
        await axios.put(`${API_URL}/api/profile`, buildProfilePayload(patch, { draft: true, partial: true }), { headers: getHeaders() });
        setProfileAutoSaveStatus('Saved');
      } catch (err) {
        setProfileAutoSaveStatus(err.response?.data?.error || 'Autosave failed');
      }
    }, 900);
    return () => {
      if (profileAutoSaveTimer.current) clearTimeout(profileAutoSaveTimer.current);
    };
  }, [profileData, profileSubTab]);

  // OTP countdown timer
  useEffect(() => {
    if (otpCountdown <= 0) return;
    const timer = setInterval(() => setOtpCountdown(prev => prev - 1), 1000);
    return () => clearInterval(timer);
  }, [otpCountdown]);

  // ═══════════════════════════════════════════════════════════════
  // API CALLS — all use getHeaders() so token is always fresh
  // ═══════════════════════════════════════════════════════════════

  const loadProfile = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/profile`, { headers: getHeaders() });
      if (res.data.status === 'success') {
        const p = res.data.profile;
        profileLoadedRef.current = false;
        profileDirtyPatch.current = {};
        setProfileData({
          fullName: p.full_name || '',
          phone: p.phone || '',
          email: p.email || '',
          dateOfBirth: p.date_of_birth || '',
          gender: p.gender || '',
          maritalStatus: p.marital_status || '',
          numberOfDependents: p.number_of_dependents ?? '',
          currentAddress: p.current_address || '',
          durationAtCurrentAddressYears: p.duration_at_current_address_years || '',
          previousAddress: p.previous_address || '',
          nin: p.nin || '',
          bvn: p.bvn || '',
          employmentStatus: p.employment_status || '',
          employerName: p.employer_name || '',
          jobTitle: p.job_title || '',
          monthlyNetIncome: p.monthly_net_income || '',
          bankName: p.bank_name || '',
          accountNumber: p.account_number || '',
          bankAccountName: p.bank_account_name || '',
          accountType: p.account_type || '',
          nextOfKinFullName: p.next_of_kin_full_name || '',
          nextOfKinPhone: p.next_of_kin_phone || '',
          nextOfKinRelationship: p.next_of_kin_relationship || '',
          nextOfKinAddress: p.next_of_kin_address || '',
          emergencyContactName: p.emergency_contact_name || '',
          emergencyContactPhone: p.emergency_contact_phone || '',
          emergencyContactRelationship: p.emergency_contact_relationship || '',
          guarantor1FullName: p.guarantor1_full_name || '',
          guarantor1Phone: p.guarantor1_phone || '',
          guarantor1Relationship: p.guarantor1_relationship || '',
          guarantor1Address: p.guarantor1_address || '',
          guarantor1Occupation: p.guarantor1_occupation || '',
          guarantor1Employer: p.guarantor1_employer || '',
          guarantor2FullName: p.guarantor2_full_name || '',
          guarantor2Phone: p.guarantor2_phone || '',
          guarantor2Relationship: p.guarantor2_relationship || '',
          guarantor2Address: p.guarantor2_address || '',
          guarantor2Occupation: p.guarantor2_occupation || '',
          guarantor2Employer: p.guarantor2_employer || '',
          idDocUrl: p.id_doc_url || '',
          proofOfIncomeUrl: p.proof_of_income_url || '',
          employmentLetterUrl: p.employment_letter_url || '',
          bankStatementUrl: p.bank_statement_url || '',
          utilityBillUrl: p.utility_bill_url || '',
          passportPhotoUrl: p.passport_photo_url || '',
          guarantor1IdUrl: p.guarantor1_id_url || '',
          guarantor2IdUrl: p.guarantor2_id_url || '',
          // Applicant type
          applicantType: p.applicant_type || '',
          // Student
          institutionName: p.institution_name || '',
          institutionType: p.institution_type || '',
          faculty: p.faculty || '',
          department: p.department || '',
          studentLevel: p.student_level || '',
          matricNumber: p.matric_number || '',
          studentIdUrl: p.student_id_url || '',
          schoolFeesReceiptUrl: p.school_fees_receipt_url || '',
          admissionLetterUrl: p.admission_letter_url || '',
          // Parent/guardian
          parentFullName: p.parent_full_name || '',
          parentPhone: p.parent_phone || '',
          parentEmail: p.parent_email || '',
          parentRelationship: p.parent_relationship || '',
          parentOccupation: p.parent_occupation || '',
          parentEmployer: p.parent_employer || '',
          parentAddress: p.parent_address || '',
          parentNin: p.parent_nin || '',
          parentBvn: p.parent_bvn || '',
          parentBankName: p.parent_bank_name || '',
          parentAccountNumber: p.parent_account_number || '',
          parentBankAccountName: p.parent_bank_account_name || '',
          parentProofOfEmploymentUrl: p.parent_proof_of_employment_url || '',
          parentBankStatementUrl: p.parent_bank_statement_url || '',
          parentIdUrl: p.parent_id_url || '',
          parentConsentUrl: p.parent_consent_url || '',
          // NYSC
          nyscStateCode: p.nysc_state_code || '',
          nyscCallUpNumber: p.nysc_call_up_number || '',
          nyscBatch: p.nysc_batch || '',
          nyscServiceState: p.nysc_service_state || '',
          nyscServiceLga: p.nysc_service_lga || '',
          nyscPpaName: p.nysc_ppa_name || '',
          nyscPpaAddress: p.nysc_ppa_address || '',
          nyscPpaType: p.nysc_ppa_type || '',
          nyscPlatoon: p.nysc_platoon || '',
          nyscCorpsNumber: p.nysc_corps_number || '',
          nyscDeploymentLetterUrl: p.nysc_deployment_letter_url || '',
          nyscCallUpLetterUrl: p.nysc_call_up_letter_url || '',
          nyscIdCardUrl: p.nysc_id_card_url || '',
          nyscAllaweeBank: p.nysc_allawee_bank_name || '',
          nyscAllaweeAccount: p.nysc_allawee_account_number || '',
          nyscAllaweeName: p.nysc_allawee_account_name || '',
          nyscSponsorFullName: p.nysc_sponsor_full_name || '',
          nyscSponsorPhone: p.nysc_sponsor_phone || '',
          nyscSponsorRelationship: p.nysc_sponsor_relationship || '',
          nyscSponsorBankName: p.nysc_sponsor_bank_name || '',
          nyscSponsorAccountNumber: p.nysc_sponsor_account_number || '',
          nyscSponsorAccountName: p.nysc_sponsor_account_name || '',
          nyscSponsorNin: p.nysc_sponsor_nin || '',
          nyscSponsorBvn: p.nysc_sponsor_bvn || '',
          // Business owner fields
          businessName: p.business_name || '',
          businessLocation: p.business_location || '',
          businessType: p.business_type || '',
          businessIsRegistered: p.business_is_registered || '',
          cacNumber: p.cac_number || '',
          businessYearsInOperation: p.business_years_in_operation || '',
          // Signed form upload URLs
          signedGuarantor1FormUrl: p.signed_guarantor1_form_url || '',
          signedGuarantor2FormUrl: p.signed_guarantor2_form_url || '',
          signedEmployerFormUrl: p.signed_employer_form_url || '',
          signedBusinessOwnerFormUrl: p.signed_business_owner_form_url || '',
        });
        setTimeout(() => {
          profileLoadedRef.current = true;
        }, 0);
      }
    } catch (err) {
      console.error('Load profile error:', err);
      if (err.response?.status === 401) {
        logout();
        navigate('/login', { replace: true });
      }
    }
  };

  const buildProfilePayload = (data, options = {}) => {
    const payload = { ...data };

    if (Object.prototype.hasOwnProperty.call(payload, 'numberOfDependents')) {
      if (payload.numberOfDependents === '' || payload.numberOfDependents === null || payload.numberOfDependents === undefined) delete payload.numberOfDependents;
      else payload.numberOfDependents = Number(payload.numberOfDependents);
    }
    if (Object.prototype.hasOwnProperty.call(payload, 'durationAtCurrentAddressYears') && payload.durationAtCurrentAddressYears === '') {
      delete payload.durationAtCurrentAddressYears;
    }
    if (Object.prototype.hasOwnProperty.call(payload, 'monthlyNetIncome')) {
      if (payload.monthlyNetIncome === '') delete payload.monthlyNetIncome;
      else payload.monthlyNetIncome = String(payload.monthlyNetIncome).replace(/,/g, '');
    }
    if (Object.prototype.hasOwnProperty.call(payload, 'accountNumber')) payload.accountNumber = (payload.accountNumber || '').replace(/\D/g, '').slice(0, 10);
    if (Object.prototype.hasOwnProperty.call(payload, 'parentAccountNumber')) payload.parentAccountNumber = (payload.parentAccountNumber || '').replace(/\D/g, '').slice(0, 10);
    if (Object.prototype.hasOwnProperty.call(payload, 'nyscAllaweeAccount')) payload.nyscAllaweeAccount = (payload.nyscAllaweeAccount || '').replace(/\D/g, '').slice(0, 10);

    if (data.nin && !isMaskedSensitiveValue(data.nin)) payload.nin = data.nin.replace(/\D/g, '').slice(0, 11);
    if (data.bvn && !isMaskedSensitiveValue(data.bvn)) payload.bvn = data.bvn.replace(/\D/g, '').slice(0, 11);

    if (options.draft) {
      ['accountNumber', 'parentAccountNumber', 'nyscAllaweeAccount'].forEach((key) => {
        if (payload[key] && payload[key].length !== 10) delete payload[key];
      });
      ['nin', 'bvn', 'parentNin', 'parentBvn', 'nyscSponsorNin', 'nyscSponsorBvn'].forEach((key) => {
        if (payload[key] && !isMaskedSensitiveValue(payload[key]) && String(payload[key]).replace(/\D/g, '').length !== 11) delete payload[key];
      });
    }

    return payload;
  };

  const loadBanks = async () => {
    // BANK_OPTIONS is already set as the initial state — the full list
    // shows immediately without waiting for the API. We only update if
    // the API returns a *larger* list (Paystack onboards new banks).
    try {
      const res = await axios.get(`${API_URL}/api/banks/direct-debit`);
      if (Array.isArray(res.data?.banks) && res.data.banks.length >= BANK_OPTIONS.length) {
        const normalised = res.data.banks
          .filter(b => b.name && b.code)
          .map(b => ({ name: b.name, code: b.code }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setBankOptions(normalised);
      }
      // If API returns fewer banks than static list (e.g. Kuda+PalmPay only),
      // keep the static list — it is more complete for direct debit purposes.
    } catch (err) {
      // Keep BANK_OPTIONS fallback — already showing
    }
  };

  const resolveBankAccountName = async (bankName, accountNumber) => {
    try {
      setResolvingAccountName(true);
      const res = await axios.post(`${API_URL}/api/banks/resolve-account`, { bankName, accountNumber }, { headers: getHeaders() });
      const resolvedAccountName = res.data?.accountName || res.data?.account_name || '';
      if (resolvedAccountName) {
        profileDirtyPatch.current = { ...profileDirtyPatch.current, bankAccountName: resolvedAccountName };
        setProfileData(prev => ({ ...prev, bankAccountName: resolvedAccountName }));
      }
    } catch (err) {
      setProfileData(prev => ({ ...prev, bankAccountName: prev.bankAccountName || '' }));
    } finally {
      setResolvingAccountName(false);
    }
  };

  const loadDashboardStats = async () => {
    setDashboardLoading(true);
    try {
      const [rentalsRes, paymentsRes, notifRes] = await Promise.all([
        axios.get(`${API_URL}/api/rentals/my`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/payments/my`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/notifications/my`, { headers: getHeaders() }),
      ]);
      const activeRental = rentalsRes.data.rentals?.[0];
      const allPayments = paymentsRes.data.payments || [];
      // Only count actionable pending payments (linked to active rentals/applications)
      const actionablePayments = allPayments.filter(p => p.is_actionable !== false);
      const nextPayment = actionablePayments.find(p => p.status === 'pending');
      const overduePayments = actionablePayments.filter(p => p.status === 'pending' && p.due_date && new Date(p.due_date) < new Date());
      const unread = (notifRes.data.notifications || []).filter(n => !n.is_read).length;
      setUnreadCount(unread);
      setDashboardStats({
        activeRental,
        nextPayment,
        overduePayments,
        totalPaid: allPayments.filter(p => p.status === 'completed').reduce((sum, p) => sum + parseFloat(p.amount || 0), 0),
        unreadNotifications: unread,
      });
    } catch (err) {
      console.error('Dashboard stats error:', err);
    } finally {
      setDashboardLoading(false);
    }
  };

  const loadApplications = async () => {
    setApplicationsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/applications/my`, { headers: getHeaders() });
      setApplications(res.data.applications || []);
    } catch (err) {
      console.error('Load applications error:', err);
    } finally {
      setApplicationsLoading(false);
    }
  };

  const loadRentals = async () => {
    setRentalsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/rentals/my`, { headers: getHeaders() });
      setRentals(res.data.rentals || []);
    } catch (err) {
      console.error('Load rentals error:', err);
    } finally {
      setRentalsLoading(false);
    }
  };

  // Load full detail (rental + payments) for a single lease the tenant clicked on.
  // Cached per-id in rentalDetails so reopening the card doesn't re-fetch.
  const loadRentalDetail = async (rentalId) => {
    setRentalDetailLoading(prev => ({ ...prev, [rentalId]: true }));
    try {
      const res = await axios.get(`${API_URL}/api/rentals/${rentalId}`, { headers: getHeaders() });
      setRentalDetails(prev => ({
        ...prev,
        [rentalId]: { rental: res.data.rental, payments: res.data.payments || [] },
      }));
    } catch (err) {
      console.error('Load rental detail error:', err);
    } finally {
      setRentalDetailLoading(prev => ({ ...prev, [rentalId]: false }));
    }
  };

  const loadPayments = async () => {
    setPaymentsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/payments/my`, { headers: getHeaders() });
      setPayments(res.data.payments || []);
    } catch (err) {
      console.error('Load payments error:', err);
    } finally {
      setPaymentsLoading(false);
    }
  };

  const loadNotifications = async () => {
    setNotificationsLoading(true);
    try {
      const url = notifFilter !== 'all'
        ? `${API_URL}/api/notifications/my?type=${notifFilter}`
        : `${API_URL}/api/notifications/my`;
      const res = await axios.get(url, { headers: getHeaders() });
      const notifs = res.data.notifications || [];
      setNotifications(notifs);
      setUnreadCount(notifs.filter(n => !n.is_read).length);
    } catch (err) {
      console.error('Load notifications error:', err);
    } finally {
      setNotificationsLoading(false);
    }
  };

  const markNotifRead = async (id) => {
    try {
      await axios.put(`${API_URL}/api/notifications/${id}/read`, {}, { headers: getHeaders() });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Mark notif read error:', err);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API_URL}/api/notifications/read-all`, {}, { headers: getHeaders() });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Mark all read error:', err);
    }
  };

  const deleteNotification = async (notifId) => {
    try {
      await axios.delete(`${API_URL}/api/notifications/${notifId}`, { headers: getHeaders() });
      setNotifications(prev => prev.filter(n => n.id !== notifId));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Delete notification error:', err);
    }
  };

  const deleteAllNotifications = () => {
    // FIX #8: Open the password-confirm modal instead of a browser confirm.
    // The modal calls /api/auth/confirm-password and hands us a short-lived
    // sensitiveActionToken we must send as X-Sensitive-Token with the DELETE.
    setSensitiveConfirm({
      title: 'Delete all notifications',
      message: 'This permanently removes every notification on your account. Confirm your password to continue.',
      confirmButtonLabel: 'Delete All',
      onConfirm: async (sensitiveActionToken) => {
        await axios.delete(`${API_URL}/api/notifications`, {
          headers: { ...getHeaders(), 'X-Sensitive-Token': sensitiveActionToken },
        });
        setNotifications([]);
        setUnreadCount(0);
        setSensitiveConfirm(null);
      },
    });
  };

  const loadTickets = async () => {
    setTicketLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/tickets/my`, { headers: getHeaders() });
      setTickets(res.data.tickets || []);
    } catch (err) {
      console.error('Load tickets error:', err);
    } finally {
      setTicketLoading(false);
    }
  };

  const loadActivityLogs = async () => {
    setActivityLogsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/tenant/activity`, { headers: getHeaders() });
      setActivityLogs(res.data.logs || []);
    } catch (err) {
      console.error('Load activity logs error:', err);
    } finally {
      setActivityLogsLoading(false);
    }
  };

  const loadTenantPdfs = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/tenant/activity`, { headers: getHeaders() });
      const allLogs = res.data.logs || [];
      setTenantPdfs(allLogs.filter(l => l.event_type === 'pdf_uploaded'));
    } catch (err) { console.error('Load PDFs error:', err); }
  };

  const uploadTenantPdf = async (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') { alert('Only PDF files are accepted.'); return; }
    if (file.size > 20 * 1024 * 1024) { alert('File must be under 20MB.'); return; }
    setPdfUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('label', pdfLabel || file.name);
      await axios.post(`${API_URL}/api/tenant/upload-pdf`, formData, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      setPdfLabel('');
      await loadTenantPdfs();
      alert('PDF uploaded successfully!');
    } catch (err) {
      alert(err.response?.data?.error || 'Upload failed. Please try again.');
    } finally { setPdfUploading(false); }
  };

  const loadTicketDetail = async (ticketId) => {
    try {
      const res = await axios.get(`${API_URL}/api/tickets/${ticketId}`, { headers: getHeaders() });
      setSelectedTicket(res.data.ticket);
      setTicketReplies(res.data.replies || []);
    } catch (err) {
      alert('Failed to load ticket details');
    }
  };

  const loadMySubmissions = async () => {
    setSubmissionsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/upload-house/my`, { headers: getHeaders() });
      setMySubmissions(res.data.houses || []);
    } catch (err) {
      console.error('Load submissions error:', err);
    } finally {
      setSubmissionsLoading(false);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!newTicket.subject || !newTicket.message) {
      alert('Subject and message are required');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/tickets`, newTicket, { headers: getHeaders() });
      alert('✅ Support ticket submitted! Our team will respond shortly.');
      setNewTicket({ subject: '', message: '', category: 'general', priority: 'normal' });
      setShowNewTicketForm(false);
      await loadTickets();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to submit ticket');
    } finally {
      setLoading(false);
    }
  };

  const handleSendTicketReply = async () => {
    if (!ticketReply.trim() || !selectedTicket) return;
    try {
      await axios.post(`${API_URL}/api/tickets/${selectedTicket.id}/reply`, { message: ticketReply }, { headers: getHeaders() });
      setTicketReply('');
      await loadTicketDetail(selectedTicket.id);
    } catch (err) {
      alert('Failed to send reply');
    }
  };

  const saveProfile = async () => {
    setProfileSaveError('');
    setProfileSaveSuccess('');

    const payload = buildProfilePayload(profileData);

    const missingFields = getRequiredProfileFields(profileData).filter((field) => !profileFieldIsComplete(field, profileData[field]));
    if (missingFields.length > 0) {
      const message = `Please complete all required fields: ${missingFields.map((field) => profileFieldLabels[field] || field).slice(0, 6).join(', ')}${missingFields.length > 6 ? ` and ${missingFields.length - 6} more` : ''}.`;
      setProfileSaveError(message);
      alert(message);
      return;
    }

    if (payload.nin && payload.nin.length !== 11) {
      const message = 'NIN must be 11 digits.';
      setProfileSaveError(message);
      alert(message);
      return;
    }
    if (payload.bvn && payload.bvn.length !== 11) {
      const message = 'BVN must be 11 digits.';
      setProfileSaveError(message);
      alert(message);
      return;
    }
    if ((profileData.applicantType || 'employed') === 'employed' && payload.accountNumber && payload.accountNumber.length !== 10) {
      const message = 'Bank account number must be 10 digits.';
      setProfileSaveError(message);
      alert(message);
      return;
    }
    if (profileData.applicantType === 'student' && payload.parentAccountNumber.length !== 10) {
      const message = "Parent's bank account number must be 10 digits.";
      setProfileSaveError(message);
      alert(message);
      return;
    }
    if (profileData.applicantType === 'nysc' && payload.nyscAllaweeAccount.length !== 10) {
      const message = 'NYSC allawee account number must be 10 digits.';
      setProfileSaveError(message);
      alert(message);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.put(`${API_URL}/api/profile`, payload, { headers: getHeaders() });
      if (res.data.status === 'success') {
        isSavingProfile.current = true;
        profileDirtyPatch.current = {};
        setProfileSaveSuccess('Profile saved successfully. Your LiteRent dashboard has been updated.');
        setProfileSubTab('profile');
        setTimeout(() => {
          setProfileSaveSuccess('');
          isSavingProfile.current = false;
        }, 5000);
      }
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to save profile';
      setProfileSaveError(message);
      alert(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSendPasswordOtp = async () => {
    setOtpLoading(true);
    setPasswordError('');
    setPasswordSuccess('');
    try {
      const res = await axios.post(`${API_URL}/api/auth/send-password-otp`, {}, { headers: getHeaders() });
      setOtpSent(true);
      setOtpCountdown(60);
      const msg = res.data.message || 'OTP sent to your email address';
      setOtpEmailHint(msg);
      setPasswordSuccess(msg);
      setTimeout(() => setPasswordSuccess(''), 6000);
    } catch (err) {
      setPasswordError(err.response?.data?.error || 'Failed to send OTP. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordError('All fields are required.');
      return;
    }
    if (passwordData.newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters.');
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }
    if (!otpSent) {
      setPasswordError('Please request the OTP first.');
      return;
    }
    if (!passwordData.otp || passwordData.otp.length !== 6) {
      setPasswordError('Please enter the 6-digit OTP sent to your email.');
      return;
    }

    setPasswordLoading(true);
    try {
      const res = await axios.put(`${API_URL}/api/auth/change-password`, {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
        otp: passwordData.otp,
      }, { headers: getHeaders() });

      setPasswordSuccess(res.data.message || 'Password changed successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '', otp: '' });
      setOtpSent(false);
      setOtpCountdown(0);
      setOtpEmailHint('');
    } catch (err) {
      setPasswordError(err.response?.data?.message || err.response?.data?.error || 'Failed to change password. Please try again.');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    let nextValue = value;
    if ([
      'accountNumber', 'nin', 'bvn', 'phone', 'nextOfKinPhone', 'emergencyContactPhone',
      'guarantor1Phone', 'guarantor2Phone', 'parentPhone', 'parentNin', 'parentBvn',
      'parentAccountNumber', 'nyscAllaweeAccount',
    ].includes(name)) {
      nextValue = value.replace(/\D/g, '');
    }
    if (['accountNumber', 'parentAccountNumber', 'nyscAllaweeAccount'].includes(name)) nextValue = nextValue.slice(0, 10);
    if (['nin', 'bvn', 'parentNin', 'parentBvn'].includes(name)) nextValue = nextValue.slice(0, 11);
    setProfileSaveError('');
    setProfileSaveSuccess('');
    const patch = { [name]: nextValue };
    if (name === 'bankName' || name === 'accountNumber') patch.bankAccountName = '';
    if (name === 'parentBankName' || name === 'parentAccountNumber') patch.parentBankAccountName = '';
    profileDirtyPatch.current = { ...profileDirtyPatch.current, ...patch };
    setProfileData((prev) => ({
      ...prev,
      [name]: nextValue,
      ...(name === 'bankName' || name === 'accountNumber' ? { bankAccountName: '' } : {}),
      ...(name === 'parentBankName' || name === 'parentAccountNumber' ? { parentBankAccountName: '' } : {}),
    }));
  };
  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
    setPasswordError('');
  };

  const handleDocUpload = async (docKey, file, fileCategory = 'image') => {
    if (!file) return;

    // Validate file type against selected category
    const ext = file.name.split('.').pop()?.toLowerCase();
    const catMap = {
      image: ['jpg','jpeg','png','gif','webp','heic','heif','bmp','tiff','tif','svg'],
      pdf:   ['pdf'],
      doc:   ['doc','docx','odt','txt'],
      sheet: ['xls','xlsx','ods','csv','ppt','pptx'],
    };
    const allowed = catMap[fileCategory] || [];
    if (!allowed.includes(ext)) {
      const names = { image: 'an image (JPG, PNG, WEBP, HEIC)', pdf: 'a PDF', doc: 'a Word doc (DOC, DOCX)', sheet: 'a spreadsheet or presentation (XLS, XLSX, PPT, CSV)' };
      alert(`You selected "${file.name}" but the category is set to ${names[fileCategory] || fileCategory}.\n\nPlease either change the file or change the category.`);
      return;
    }

    const maxSize = 20971520; // 20MB
    if (file.size > maxSize) { alert('Please upload a file smaller than 20MB.'); return; }

    setDocUploading(prev => ({ ...prev, [docKey]: true }));
    setDocSuccess(prev => ({ ...prev, [docKey]: false }));
    setProfileSaveError('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('docType', docKey);
      formData.append('fileCategory', fileCategory);

      const uploadRes = await axios.post(`${API_URL}/api/upload-document`, formData, {
        headers: getHeaders(),
      });

      const url = uploadRes.data?.url;
      if (!url) throw new Error('No document URL returned from upload.');

      // Append category as URL hash — stored in DB, read by both tenant and admin viewer
      // Cloudinary ignores hashes so the URL still works perfectly
      const urlWithCat = `${url}#cat=${fileCategory}`;

      const updatedData = { ...profileData, [docKey]: urlWithCat };
      setProfileData(updatedData);

      await axios.put(`${API_URL}/api/profile`, { [docKey]: urlWithCat }, { headers: getHeaders() });

      setDocSuccess(prev => ({ ...prev, [docKey]: true }));
      setProfileSaveSuccess('Document uploaded and saved successfully.');
      isSavingProfile.current = true;
      profileDirtyPatch.current = {};
      setTimeout(() => {
        setDocSuccess(prev => ({ ...prev, [docKey]: false }));
        setProfileSaveSuccess('');
        isSavingProfile.current = false;
      }, 3000);

    } catch (err) {
      console.error('Document upload error:', err);
      const message = err.response?.data?.error || err.message || 'Document upload failed. Please try again.';
      setProfileSaveError(message);
      alert(message);
    } finally {
      setDocUploading(prev => ({ ...prev, [docKey]: false }));
    }
  };



  const profileStepIndex = PROFILE_WIZARD_STEPS.findIndex((step) => step.key === profileWizardStep);
  const profileStepCompleted = (stepKey) => {
    const fields = profileStepFieldMap[stepKey] || [];
    return fields.every((field) => profileFieldIsComplete(field, profileData[field]));
  };

  // Dynamic slide order depends on applicant type
  const getProfileSlideKeys = () => {
    const atype = profileData.applicantType || '';
    if (atype === 'student') return ['type','personal','address','identity','student_info','parent','contacts','guarantors','documents','review'];
    if (atype === 'nysc') return ['type','personal','address','identity','nysc_info','nysc_ppa','nysc_allawee_banking','contacts','guarantors','documents','review'];
    if (atype === 'employed') return ['type','personal','address','identity','work','banking','contacts','guarantors','documents','review'];
    return ['type'];
  };

  // FIX #13: Scroll the newly-rendered step's TITLE into view (not the page top /
  // form-header). We defer to the next tick so the step has finished rendering.
  const scrollProfileStepIntoView = () => {
    requestAnimationFrame(() => {
      const el = document.getElementById('profile-step-title');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const goProfileNext = () => {
    const keys = getProfileSlideKeys();
    const idx = keys.indexOf(profileWizardStep);
    const nextKey = keys[idx + 1];
    if (nextKey) { setProfileSaveError(''); setProfileWizardStep(nextKey); scrollProfileStepIntoView(); }
  };

  const goProfilePrev = () => {
    const keys = getProfileSlideKeys();
    const idx = keys.indexOf(profileWizardStep);
    const prevKey = keys[idx - 1];
    if (prevKey) { setProfileSaveError(''); setProfileWizardStep(prevKey); scrollProfileStepIntoView(); }
  };
  const handleHouseImagesChange = (e) => {
    const files = Array.from(e.target.files).slice(0, 5);
    setHouseImages(files);
    const previews = files.map(f => URL.createObjectURL(f));
    setHouseImagePreviews(previews);
  };

  const removeHouseImage = (index) => {
    setHouseImages(houseImages.filter((_, i) => i !== index));
    setHouseImagePreviews(houseImagePreviews.filter((_, i) => i !== index));
  };

  const handlePayNow = async (paymentId) => {
    if (!profileData.email) {
      alert('Please update your profile with a valid email address before making payment.');
      setActiveTab('profile');
      setProfileSubTab('update');
      return;
    }
    setPaymentLoading(prev => ({ ...prev, [paymentId]: true }));
    try {
      const res = await axios.post(`${API_URL}/api/payments/${paymentId}/initiate`, {}, { headers: getHeaders() });
      if (res.data.authorization_url) {
        window.location.href = res.data.authorization_url;
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to initialize payment. Please try again.');
    } finally {
      setPaymentLoading(prev => ({ ...prev, [paymentId]: false }));
    }
  };

  const verifyPaymentAfterRedirect = async (paymentId, reference) => {
    try {
      const res = await axios.post(`${API_URL}/api/payments/verify`, { reference, payment_id: paymentId }, { headers: getHeaders() });
      if (res.data.status === 'success') {
        await loadPayments();
        await loadDashboardStats();
        alert(`✅ Payment confirmed! Receipt: ${res.data.receipt_number}`);
      }
    } catch (err) {
      console.error('Auto-verify error:', err);
    }
  };

  const handleVerifyPayment = async (paymentId) => {
    const payment = payments.find(p => p.id === paymentId);
    if (!payment?.paystack_reference) {
      alert('No payment reference found. Please initiate payment first.');
      return;
    }
    setPaymentLoading(prev => ({ ...prev, [paymentId]: true }));
    try {
      const res = await axios.post(`${API_URL}/api/payments/verify`, {
        reference: payment.paystack_reference,
        payment_id: paymentId,
      }, { headers: getHeaders() });
      if (res.data.status === 'success') {
        await loadPayments();
        await loadDashboardStats();
        alert(`✅ Payment verified! Receipt: ${res.data.receipt_number}`);
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Verification failed. Please try again.');
    } finally {
      setPaymentLoading(prev => ({ ...prev, [paymentId]: false }));
    }
  };

  const handleDownloadReceipt = async (paymentId) => {
    setReceiptLoading(prev => ({ ...prev, [paymentId]: true }));
    try {
      const res = await axios.get(`${API_URL}/api/payments/${paymentId}/receipt`, { headers: getHeaders() });
      const payment = res.data.payment;
      const html = generateReceiptHTML(payment, profileData);
      const printWindow = window.open('', '_blank');
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 500);
    } catch (err) {
      alert('Failed to load receipt. Payment may not be completed yet.');
    } finally {
      setReceiptLoading(prev => ({ ...prev, [paymentId]: false }));
    }
  };

  // ─── Nav ──────────────────────────────────────────────────────
  const navItems = [
    { id: 'home', label: 'Dashboard Home' },
    { id: 'browse-apply', label: 'Browse & Apply' },
    { id: 'my-applications', label: 'My Applications' },
    { id: 'my-rentals', label: 'My Rentals' },
    { id: 'payments', label: 'Payments' },
    { id: 'upload-house', label: 'Upload a House' },
    { id: 'notifications', label: 'Notifications' },
    { id: 'activity', label: 'Activity Log' },
    { id: 'support', label: 'Support & Tickets' },
    { id: 'profile', label: 'Profile & Settings' },
  ];

  // ─── Direct Debit Consent ──────────────────────────────────────────────────
  const handleDirectDebitConsent = async (applicationId) => {
    const msg = `By confirming, you authorise LiteRent to automatically debit your bank account monthly for rent.\n\nYou will be taken to Paystack's secure page to complete the authorisation. This takes about 1 minute.\n\nProceed?`;
    if (!window.confirm(msg)) return;
    setDebitConsentLoading((prev) => ({ ...prev, [applicationId]: true }));
    try {
      const token = localStorage.getItem('authToken');
      const res = await axios.post(
        `${API_URL}/api/applications/${applicationId}/direct-debit-consent`,
        { consent: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.authorization_url) {
        // Redirect tenant to Paystack to complete bank account authorisation
        window.location.href = res.data.authorization_url;
      } else {
        setDebitConsentSuccess((prev) => ({ ...prev, [applicationId]: true }));
        await loadApplications();
        alert(res.data?.message || 'Direct debit consent recorded.');
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Could not initiate direct debit setup. Please try again.');
    } finally {
      setDebitConsentLoading((prev) => ({ ...prev, [applicationId]: false }));
    }
  };



  // Tenant withdraw — requires password confirmation
  const [withdrawLoading, setWithdrawLoading] = useState({});
  const handleWithdrawApplication = async (applicationId) => {
    if (!window.confirm('Are you sure you want to withdraw this application? This cannot be undone.')) return;
    const pwd = window.prompt('Enter your account password to confirm:');
    if (pwd === null || pwd === '') return;
    setWithdrawLoading((prev) => ({ ...prev, [applicationId]: true }));
    try {
      await axios.post(`${API_URL}/api/auth/confirm-password`, { password: pwd }, { headers: getHeaders() });
      await axios.post(`${API_URL}/api/applications/${applicationId}/withdraw`, {}, { headers: getHeaders() });
      await loadApplications();
      alert('Application withdrawn successfully.');
    } catch (err) {
      alert(err.response?.data?.error || 'Could not withdraw. Please check your password and try again.');
    } finally {
      setWithdrawLoading((prev) => ({ ...prev, [applicationId]: false }));
    }
  };

  // ─── Guarantor Form Download ───────────────────────────────────────────────
  const handleDownloadGuarantorForm = async (guarantorNum) => {
    setGuarantorFormLoading(prev => ({ ...prev, [guarantorNum]: true }));
    try {
      const token = localStorage.getItem('authToken');
      const res = await axios.get(`${API_URL}/api/guarantor-form/${guarantorNum}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `LiteRent-Guarantor-${guarantorNum}-Form.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(`Could not download Guarantor ${guarantorNum} form. Please try again.`);
    } finally {
      setGuarantorFormLoading(prev => ({ ...prev, [guarantorNum]: false }));
    }
  };

  const handleDownloadBusinessOwnerForm = async () => {
    setBusinessOwnerFormLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const res = await axios.get(`${API_URL}/api/business-owner-form`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'LiteRent-Business-Owner-Form.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Could not download business owner form. Please try again.');
    } finally {
      setBusinessOwnerFormLoading(false);
    }
  };

  // ─── Upload signed/filled verification form ────────────────────────────────
  const handleSignedFormUpload = async (fieldKey, file) => {
    if (!file) return;
    setSignedFormUploading(prev => ({ ...prev, [fieldKey]: true }));
    try {
      const token = localStorage.getItem('authToken');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fieldName', fieldKey);
      const uploadRes = await axios.post(`${API_URL}/api/upload-document`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      const url = uploadRes.data.url;
      await axios.put(`${API_URL}/api/tenant/signed-forms`,
        { [fieldKey]: url },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProfileData(prev => ({ ...prev, [fieldKey]: url }));
      alert('✅ Signed form uploaded successfully.');
    } catch (err) {
      alert(err.response?.data?.error || 'Upload failed. Please try again.');
    } finally {
      setSignedFormUploading(prev => ({ ...prev, [fieldKey]: false }));
    }
  };

  // ─── Employer Notification Letter ─────────────────────────────────────────
  const handleDownloadEmployerLetter = async (appId) => {
    setEmployerLetterLoading(prev => ({ ...prev, [appId]: true }));
    try {
      const token = localStorage.getItem('authToken');
      const res = await axios.get(`${API_URL}/api/employer-notification-letter?appId=${appId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'LiteRent-Employer-Notification-Letter.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Could not download the employer letter. Please try again.');
    } finally {
      setEmployerLetterLoading(prev => ({ ...prev, [appId]: false }));
    }
  };

  // ─── Mono Connect Bank Linking ─────────────────────────────────────────────
  const handleMonoConnect = () => {
    const MONO_PUBLIC_KEY = process.env.REACT_APP_MONO_PUBLIC_KEY;
    if (!MONO_PUBLIC_KEY) {
      alert('Bank linking is not configured yet. Please contact support.');
      return;
    }
    setMonoLinking(true);
    const monoInstance = new window.Connect({
      key: MONO_PUBLIC_KEY,
      onSuccess: async ({ code }) => {
        try {
          const token = localStorage.getItem('authToken');
          await axios.post(`${API_URL}/api/mono/exchange`, { code }, { headers: { Authorization: `Bearer ${token}` } });
          alert('✅ Bank account linked successfully! LiteRent can now access your bank statement for verification.');
        } catch (err) {
          alert(err.response?.data?.error || 'Bank linking failed. Please try again.');
        } finally {
          setMonoLinking(false);
        }
      },
      onClose: () => setMonoLinking(false),
    });
    monoInstance.open();
  };

  const handleNavClick = (id) => {
    if (id === 'browse-apply') { navigate('/browse'); return; }
    setActiveTab(id);
    setIsMobileMenuOpen(false);
    if (id === 'profile') setProfileSubTab('profile');
  };

  const handleLogout = () => { logout(); navigate('/', { replace: true }); };

  // ─── Helpers ──────────────────────────────────────────────────
  const getPasswordStrength = (pwd) => {
    if (!pwd) return null;
    let score = 0;
    if (pwd.length >= 8) score++;
    if (pwd.length >= 12) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;
    if (score <= 2) return { label: 'Weak', color: 'bg-red-500', width: 'w-1/3' };
    if (score <= 3) return { label: 'Fair', color: 'bg-yellow-500', width: 'w-2/3' };
    return { label: 'Strong', color: 'bg-green-500', width: 'w-full' };
  };

  const strength = getPasswordStrength(passwordData.newPassword);

  const getNotifIcon = (type) => {
    const map = { payment: '💳', application: '📋', ticket: '🎫', ticket_reply: '💬', general: '🔔' };
    return map[type] || '🔔';
  };

  const getNotifColor = (type) => {
    const map = {
      payment: 'bg-purple-100 text-purple-700',
      application: 'bg-green-100 text-green-700',
      ticket: 'bg-blue-100 text-blue-700',
      ticket_reply: 'bg-indigo-100 text-indigo-700',
      general: 'bg-gray-100 text-gray-600',
    };
    return map[type] || 'bg-gray-100 text-gray-600';
  };

  const getStatusBadge = (status) => {
    const map = {
      open: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-yellow-100 text-yellow-700',
      resolved: 'bg-green-100 text-green-700',
      closed: 'bg-gray-100 text-gray-600',
    };
    return map[status] || 'bg-gray-100 text-gray-600';
  };

  const getSubmissionStatusColor = (status) => {
    const map = {
      submitted: 'bg-blue-100 text-blue-700',
      reviewing: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
    };
    return map[status] || 'bg-gray-100 text-gray-600';
  };

  const maskEmail = (email) => {
    if (!email) return '';
    return email.replace(/(.{2})(.*)(@.*)/, (_, a, b, c) => a + '*'.repeat(Math.max(2, b.length)) + c);
  };

  // ─── Display name — profile first, then AuthContext user ──────
  const displayFirstName =
    profileData.fullName?.split(' ')[0] ||
    user?.fullName?.split(' ')[0] ||
    user?.full_name?.split(' ')[0] ||
    user?.name?.split(' ')[0] ||
    'there';

  const displayFullName =
    profileData.fullName ||
    user?.fullName ||
    user?.full_name ||
    user?.name ||
    '';

  // ─── RENDER ───────────────────────────────────────────────────

  // Show nothing (or a spinner) while AuthContext is still hydrating
  if (authLoading) {
    return <BrandLoadingScreen message="Loading your LiteRent dashboard…" />;
  }
  return (
    <div className="min-h-screen bg-[#fdfdfd] flex flex-col lg:flex-row">

      {/* Desktop Sidebar */}
      <aside className="w-72 bg-white border-r border-[#afc09e]/30 hidden lg:block fixed h-full overflow-y-auto z-20">
        <div className="p-8">
          <Link to="/" className="flex items-center gap-3 mb-12">
            <img src="/Logo.jpg" alt="LiteRent Logo" className="h-20 w-auto object-contain" />
          </Link>
          <nav className="space-y-2">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.id)}
                className={`w-full text-left px-6 py-4 rounded-xl transition font-medium flex items-center justify-between ${activeTab === item.id ? 'bg-[#a5c250]/15 text-[#294c79] border-l-4 border-[#a5c250]' : 'text-[#1a1a1a]/70 hover:bg-[#e8f2d1]/50'}`}
              >
                <span>{item.label}</span>
                {item.id === 'notifications' && unreadCount > 0 && (
                  <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{unreadCount}</span>
                )}
              </button>
            ))}
          </nav>
          <div className="mt-20 pt-8 border-t border-[#afc09e]/30 text-sm text-[#1a1a1a]/60">
            <p>Logged in as:</p>
            <p className="font-semibold text-[#294c79] truncate">{displayFullName || 'Loading...'}</p>
            <button onClick={handleLogout} className="text-red-600 hover:underline mt-3 block font-medium">Log out</button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 bg-white border-b border-[#afc09e]/30 z-30 px-4 py-3 flex items-center justify-between">
        <Link to="/"><img src="/Logo.jpg" alt="LiteRent Logo" className="h-9 w-auto object-contain" /></Link>
        <div className="flex items-center gap-1">
          {/* FIX #7: Clickable bell icon — jumps straight to the notifications tab. */}
          <button
            type="button"
            onClick={() => handleNavClick('notifications')}
            aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
            className="relative p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#a5c250]"
          >
            <svg className="h-6 w-6 text-[#294c79]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            {unreadCount > 0 && (
              <span className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
          <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Open menu" className="p-2 -mr-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#a5c250]">
            <svg className="h-8 w-8 text-[#294c79]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              {isMobileMenuOpen
                ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />}
            </svg>
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/20" onClick={() => setIsMobileMenuOpen(false)}>
          <div className="absolute inset-y-0 right-0 w-4/5 max-w-sm bg-white shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex flex-col h-full">
              <div className="p-6 border-b flex justify-between items-center">
                <h2 className="text-xl font-bold text-[#294c79]">Menu</h2>
                <button onClick={() => setIsMobileMenuOpen(false)}>
                  <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-6 py-6">
                <nav className="space-y-2">
                  {navItems.map(item => (
                    <button
                      key={item.id}
                      onClick={() => handleNavClick(item.id)}
                      className={`w-full text-left px-5 py-4 rounded-xl transition font-medium flex items-center justify-between ${activeTab === item.id ? 'bg-[#a5c250]/15 text-[#294c79] border-l-4 border-[#a5c250]' : 'text-[#1a1a1a]/70 hover:bg-[#e8f2d1]/50'}`}
                    >
                      <span>{item.label}</span>
                      {item.id === 'notifications' && unreadCount > 0 && (
                        <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{unreadCount}</span>
                      )}
                    </button>
                  ))}
                </nav>
              </div>
              <div className="p-6 border-t text-sm">
                <p className="mb-1 text-[#1a1a1a]/60">Logged in as:</p>
                <p className="font-semibold text-[#294c79] mb-3">{displayFullName || 'Loading...'}</p>
                <button onClick={handleLogout} className="text-red-600 hover:underline font-medium">Log out</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 lg:ml-72 pt-16 lg:pt-0">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-10">

          {/* ════ DASHBOARD HOME ════ */}
          {activeTab === 'home' && (
            <section>
              <h1 className="text-3xl md:text-4xl font-bold text-[#294c79] mb-10">
                Welcome back, {displayFirstName} 👋
              </h1>
              {dashboardLoading ? (
                <BrandLoadingScreen fullScreen={false} compact message="Preparing your dashboard overview…" />
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <div className="bg-white p-6 rounded-2xl shadow-md border border-[#e8f2d1]">
                      <h3 className="text-lg font-semibold text-[#294c79] mb-3">Active Rent</h3>
                      {dashboardStats?.activeRental ? (
                        <>
                          <p className="text-xl font-bold text-[#a5c250]">1 Active Lease</p>
                          <p className="text-[#1a1a1a]/70 mt-1 text-sm">{dashboardStats.activeRental.title}</p>
                          <button
                            type="button"
                            onClick={() => {
                              setActiveTab('my-rentals');
                              setExpandedRentalId(dashboardStats.activeRental.id);
                              loadRentalDetail(dashboardStats.activeRental.id);
                            }}
                            className="mt-3 text-xs text-[#294c79] underline font-medium"
                          >
                            View Lease Details
                          </button>
                        </>
                      ) : <p className="text-[#1a1a1a]/70">No active rental</p>}
                    </div>
                    <div className="bg-white p-6 rounded-2xl shadow-md border border-[#e8f2d1]">
                      <h3 className="text-lg font-semibold text-[#294c79] mb-3">Next Payment</h3>
                      {dashboardStats?.nextPayment ? (
                        <>
                          <p className="text-2xl font-bold text-[#a5c250]">₦{parseFloat(dashboardStats.nextPayment.amount).toLocaleString()}</p>
                          <p className="text-[#1a1a1a]/70 text-sm mt-1">Due: {new Date(dashboardStats.nextPayment.due_date).toLocaleDateString('en-NG')}</p>
                          <button onClick={() => handleNavClick('payments')} className="mt-3 text-xs text-[#294c79] underline font-medium">Pay Now →</button>
                        </>
                      ) : <p className="text-[#1a1a1a]/70">No pending payments</p>}
                    </div>
                    <div className="bg-white p-6 rounded-2xl shadow-md border border-[#e8f2d1]">
                      <h3 className="text-lg font-semibold text-[#294c79] mb-3">Outstanding</h3>
                      {dashboardStats?.overduePayments?.length > 0 ? (
                        <>
                          <p className="text-2xl font-bold text-red-600">{dashboardStats.overduePayments.length}</p>
                          <p className="text-[#1a1a1a]/70 text-sm mt-1">Overdue payment{dashboardStats.overduePayments.length > 1 ? 's' : ''}</p>
                          <button onClick={() => handleNavClick('payments')} className="mt-3 text-xs text-red-600 underline font-medium">View →</button>
                        </>
                      ) : (
                        <>
                          <p className="text-2xl font-bold text-green-600">₦0</p>
                          <p className="text-[#1a1a1a]/70 text-sm mt-1">All up to date ✓</p>
                        </>
                      )}
                    </div>
                    <div className="bg-white p-6 rounded-2xl shadow-md border border-[#e8f2d1] cursor-pointer hover:shadow-lg transition" onClick={() => handleNavClick('notifications')}>
                      <h3 className="text-lg font-semibold text-[#294c79] mb-3">Notifications</h3>
                      {unreadCount > 0 ? (
                        <>
                          <p className="text-2xl font-bold text-orange-500">{unreadCount}</p>
                          <p className="text-[#1a1a1a]/70 text-sm mt-1">Unread message{unreadCount > 1 ? 's' : ''}</p>
                        </>
                      ) : (
                        <>
                          <p className="text-2xl font-bold text-green-600">0</p>
                          <p className="text-[#1a1a1a]/70 text-sm mt-1">All caught up!</p>
                        </>
                      )}
                    </div>
                  </div>

                  {!isProfileComplete && (
                    <div className="bg-amber-50 border border-amber-300 rounded-2xl p-5">
                      <div className="flex items-start gap-3">
                        <span className="text-2xl">⚠️</span>
                        <div className="flex-1">
                          <p className="font-semibold text-amber-800">Profile Incomplete</p>
                          <p className="text-sm text-amber-700 mt-1">A complete profile is required to apply for properties and process payments. Please fill in the following:</p>
                          <ul className="mt-2 space-y-0.5">
                            {requiredFields.filter(f => !profileFieldIsComplete(f, profileData[f])).map(f => (
                              <li key={f} className="text-xs text-amber-800 font-medium">• {profileFieldLabels[f] || f}</li>
                            ))}
                          </ul>
                          <button onClick={() => { setActiveTab('profile'); setProfileSubTab('update'); }} className="mt-3 text-sm text-amber-800 underline font-medium">Update Profile →</button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </section>
          )}

          {/* ════ MY APPLICATIONS ════ */}
          {activeTab === 'my-applications' && (
            <section>
              <h1 className="text-3xl md:text-4xl font-bold text-[#294c79] mb-10">My Applications</h1>
              {applicationsLoading ? (
                <BrandLoadingScreen fullScreen={false} compact message="Loading your house applications…" />
              ) : applications.length === 0 ? (
                <div className="bg-white p-10 rounded-2xl shadow-md text-center">
                  <p className="text-xl text-[#1a1a1a]/70 mb-6">No applications yet</p>
                  <Link to="/browse">
                    <button className="px-10 py-4 bg-[#294c79] text-white rounded-full font-medium hover:bg-[#1f3a5c]">Apply for a House</button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {applications.map((app) => {
                    const stage = app.workflow_stage || 'application';
                    const stageMeta = getStageMeta(stage);
                    const currentStageIndex = getStageIndex(stage);
                    const upfrontPayment = payments.find((payment) => payment.application_id === app.id && payment.category === 'upfront');
                    const showPaymentCTA = stage === 'upfront_payment' && upfrontPayment && upfrontPayment.status !== 'completed';
                    const isOpen = expandedTenantAppId === app.id;

                    return (
                      <div key={app.id} className={`rounded-2xl border transition-all duration-200 overflow-hidden ${isOpen ? 'border-[#294c79] shadow-lg' : 'border-[#e8f2d1] bg-white shadow-sm hover:border-[#294c79]/40 hover:shadow-md'}`}>

                        {/* ── Clickable Summary Row ── */}
                        <button
                          onClick={() => setExpandedTenantAppId(prev => prev === app.id ? null : app.id)}
                          className="w-full text-left px-5 py-4 bg-white flex items-center justify-between gap-4"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <span className="font-bold text-[#294c79] text-base truncate">{app.house_title}</span>
                              <span className="text-gray-400 text-sm hidden sm:inline">·</span>
                              <span className="text-[#1a1a1a]/50 text-sm hidden sm:inline">{app.location}</span>
                            </div>
                            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-400">
                              <span>{formatCurrency(app.annual_rent)}/yr</span>
                              <span>·</span>
                              <span>Applied {new Date(app.created_at).toLocaleDateString('en-NG')}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${
                              app.status === 'pending' ? 'bg-yellow-100 text-yellow-700'
                              : app.status === 'approved' ? 'bg-green-100 text-green-700'
                              : app.status === 'withdrawn' ? 'bg-gray-200 text-gray-700'
                              : 'bg-red-100 text-red-700'
                            }`}>{app.status}</span>
                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-[#e8f2d1] text-[#4a7a1e]">{stageMeta.label}</span>
                            <svg className={`w-5 h-5 text-[#294c79] transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </button>

                        {/* ── Expanded Detail ── */}
                        {isOpen && (
                          <div className="border-t border-[#edf2f7] bg-[#fbfcfe] px-5 py-6 space-y-5">

                            {/* Stage Progress */}
                            <div className="grid grid-cols-5 gap-2">
                              {APPLICATION_STAGES.slice(0, 5).map((item, index) => {
                                const isCurrent = item.key === stage;
                                const isComplete = currentStageIndex > index || stage === 'active';
                                return (
                                  <div key={item.key} className={`rounded-xl border p-2.5 text-center ${isCurrent ? 'border-[#294c79] bg-[#294c79] text-white' : isComplete ? 'border-[#a5c250] bg-[#f5f9ee] text-[#294c79]' : 'border-gray-200 bg-white text-gray-400'}`}>
                                    <div className="text-[10px] uppercase tracking-wide font-bold opacity-70">Step {index + 1}</div>
                                    <div className="font-semibold text-xs mt-0.5 leading-tight">{item.label}</div>
                                    {isComplete && <div className="text-[10px] opacity-70">✓</div>}
                                  </div>
                                );
                              })}
                            </div>

                            {/* Payment Summary */}
                            <div className="grid md:grid-cols-3 gap-3 text-sm">
                              <div className="bg-[#f8fbf2] rounded-2xl p-4 border border-[#e8f2d1]"><div className="text-gray-500 mb-1 text-xs">30% Upfront</div><div className="text-lg font-bold text-[#294c79]">{formatCurrency(app.upfront_amount)}</div></div>
                              <div className="bg-[#f8fbf2] rounded-2xl p-4 border border-[#e8f2d1]"><div className="text-gray-500 mb-1 text-xs">Monthly Plan</div><div className="text-lg font-bold text-[#294c79]">{formatCurrency(app.monthly_instalment_amount)} × {app.monthly_instalments_total || 11}</div></div>
                              <div className="bg-[#f8fbf2] rounded-2xl p-4 border border-[#e8f2d1]"><div className="text-gray-500 mb-1 text-xs">Direct Debit</div><div className="text-lg font-bold text-[#294c79] capitalize">{(app.direct_debit_status || 'pending').replace('_', ' ')}</div></div>
                            </div>

                            {app.notes && (
                              <div className="p-3 bg-white rounded-xl text-sm text-gray-600 border border-gray-200">
                                <strong>Note from LiteRent:</strong> {app.notes}
                              </div>
                            )}

                            {/* ── Stage-specific CTAs ── */}

                            {/* APPLICATION stage: submitted, awaiting review */}
                            {stage === 'application' && (
                              <div className="p-4 rounded-2xl border border-[#d9e5ef] bg-[#f8fbff] text-sm text-[#294c79]">
                                <p className="font-semibold mb-1">⏳ Application Under Review</p>
                                <p className="text-[#1a1a1a]/60 text-xs">LiteRent is reviewing your application. You'll be notified when it moves to the next stage.</p>
                                {/* FIX #3: Tenant can withdraw while pending */}
                                {app.status === 'pending' && (
                                  <div className="mt-3 pt-3 border-t border-[#d9e5ef]">
                                    <button
                                      type="button"
                                      onClick={() => handleWithdrawApplication(app.id)}
                                      disabled={withdrawLoading[app.id]}
                                      className="px-4 py-2 rounded-lg border border-red-300 text-red-700 text-xs font-semibold hover:bg-red-50 transition disabled:opacity-50"
                                    >
                                      {withdrawLoading[app.id] ? 'Withdrawing…' : 'Withdraw Application'}
                                    </button>
                                    <p className="text-[11px] text-[#1a1a1a]/50 mt-2">
                                      Once your application moves past review, it can no longer be withdrawn here. Contact support for later stages.
                                    </p>
                                  </div>
                                )}
                                {app.status === 'withdrawn' && (
                                  <button
                                    onClick={async () => {
                                      if (!window.confirm('Permanently delete this withdrawn application record?')) return;
                                      const pwd = window.prompt('Enter your password to confirm:');
                                      if (!pwd) return;
                                      try {
                                        await axios.post(`${API_URL}/api/auth/confirm-password`, { password: pwd }, { headers: getHeaders() });
                                        await axios.delete(`${API_URL}/api/applications/${app.id}`, { headers: getHeaders() });
                                        alert('Application deleted.');
                                        await loadApplications();
                                      } catch (err) { alert(err.response?.data?.error || 'Could not delete. Check password.'); }
                                    }}
                                    className="px-4 py-2 text-xs text-red-600 border border-red-300 rounded-xl hover:bg-red-50 transition font-medium"
                                  >🗑 Delete Record</button>
                                )}
                                {app.status === 'withdrawn' && (
                                  <p className="mt-3 text-xs text-gray-500 italic">You withdrew this application.</p>
                                )}
                              </div>
                            )}

                            {/* VERIFICATION stage: fee, guarantor forms, employer or business owner form */}
                            {stage === 'verification' && (
                              <div className="space-y-3">
                                {(() => {
                                  const verifPayment = payments.find(p => p.status === 'pending' && p.category === 'verification_fee' && p.application_id === app.id);
                                  const verifPaid = payments.find(p => p.status === 'completed' && p.category === 'verification_fee' && p.application_id === app.id);
                                  return verifPaid ? (
                                    <div className="p-4 rounded-2xl border border-green-200 bg-green-50 text-sm text-green-700">
                                      ✅ <strong>Verification fee paid.</strong> LiteRent is reviewing your documents. You will be notified once verification is complete.
                                    </div>
                                  ) : (
                                    <div className="p-4 rounded-2xl border border-amber-200 bg-amber-50 text-sm text-amber-800">
                                      <p className="font-semibold mb-1">⏳ Verification Fee Required</p>
                                      <p className="mb-3">Pay the verification fee of <strong>{formatCurrency(app.verification_fee_amount || (app.annual_rent >= 1000000 ? 20000 : 10000))}</strong> to proceed with your application. LiteRent will review your documents after payment.</p>
                                      {verifPayment ? (
                                        <button
                                          onClick={() => handlePayNow(verifPayment.id)}
                                          disabled={paymentLoading[verifPayment.id]}
                                          className={`px-6 py-3 rounded-xl font-bold text-sm transition ${paymentLoading[verifPayment.id] ? 'bg-gray-300 text-gray-600' : 'bg-[#294c79] text-white hover:bg-[#1f3a5c] shadow-md'}`}
                                        >
                                          {paymentLoading[verifPayment.id] ? '⏳ Opening Paystack…' : `💳 Pay Verification Fee — ${formatCurrency(app.verification_fee_amount || (app.annual_rent >= 1000000 ? 20000 : 10000))}`}
                                        </button>
                                      ) : (
                                        <button onClick={() => handleNavClick('payments')} className="px-5 py-2.5 bg-[#294c79] text-white rounded-xl font-semibold text-sm hover:bg-[#1f3a5c]">Go to Payments →</button>
                                      )}
                                    </div>
                                  );
                                })()}

                                {/* Guarantor Forms */}
                                <div className="p-4 rounded-2xl border border-[#e8f2d1] bg-[#f8fbf2] text-sm space-y-3">
                                  <p className="font-semibold text-[#294c79]">📋 Guarantor Forms</p>
                                  <p className="text-[#1a1a1a]/60 text-xs">Download the pre-filled form for each guarantor. Have them sign it, then upload the signed copy below.</p>

                                  {/* Guarantor 1 */}
                                  <div className="rounded-xl border border-[#d9e5ef] bg-white p-3 space-y-2">
                                    <p className="text-xs font-semibold text-[#294c79] uppercase tracking-wide">Guarantor 1{profileData.guarantor1FullName ? ` — ${profileData.guarantor1FullName}` : ''}</p>
                                    <button onClick={() => handleDownloadGuarantorForm(1)} disabled={guarantorFormLoading[1]} className="w-full px-4 py-2 rounded-xl border border-[#294c79] text-[#294c79] font-semibold text-xs hover:bg-[#f3f7fb] transition disabled:opacity-50 text-left">
                                      {guarantorFormLoading[1] ? '⏳ Preparing…' : '⬇ Download Guarantor 1 Form (PDF)'}
                                    </button>
                                    <div>
                                      <p className="text-xs text-[#1a1a1a]/50 mb-1">Upload signed copy:</p>
                                      {profileData.signedGuarantor1FormUrl ? (
                                        <div className="flex items-center gap-2">
                                          <a href={profileData.signedGuarantor1FormUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-[#294c79] underline">✅ Signed form uploaded — view</a>
                                          <label className="text-xs text-[#294c79] underline cursor-pointer">(replace)<input type="file" accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.heic,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.bmp,.tiff" className="hidden" disabled={signedFormUploading.signedGuarantor1FormUrl} onChange={e => handleSignedFormUpload('signedGuarantor1FormUrl', e.target.files[0])} /></label>
                                        </div>
                                      ) : (
                                        <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-dashed border-[#294c79]/40 text-xs text-[#294c79] cursor-pointer hover:bg-[#f3f7fb] transition ${signedFormUploading.signedGuarantor1FormUrl ? 'opacity-50 pointer-events-none' : ''}`}>
                                          {signedFormUploading.signedGuarantor1FormUrl ? '⏳ Uploading…' : '📎 Upload Signed Guarantor 1 Form'}
                                          <input type="file" accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.heic,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.bmp,.tiff" className="hidden" disabled={signedFormUploading.signedGuarantor1FormUrl} onChange={e => handleSignedFormUpload('signedGuarantor1FormUrl', e.target.files[0])} />
                                        </label>
                                      )}
                                    </div>
                                  </div>

                                  {/* Guarantor 2 — only show if G2 name is filled */}
                                  {profileData.guarantor2FullName && (
                                    <div className="rounded-xl border border-[#d9e5ef] bg-white p-3 space-y-2">
                                      <p className="text-xs font-semibold text-[#294c79] uppercase tracking-wide">Guarantor 2 — {profileData.guarantor2FullName}</p>
                                      <button onClick={() => handleDownloadGuarantorForm(2)} disabled={guarantorFormLoading[2]} className="w-full px-4 py-2 rounded-xl border border-[#294c79] text-[#294c79] font-semibold text-xs hover:bg-[#f3f7fb] transition disabled:opacity-50 text-left">
                                        {guarantorFormLoading[2] ? '⏳ Preparing…' : '⬇ Download Guarantor 2 Form (PDF)'}
                                      </button>
                                      <div>
                                        <p className="text-xs text-[#1a1a1a]/50 mb-1">Upload signed copy:</p>
                                        {profileData.signedGuarantor2FormUrl ? (
                                          <div className="flex items-center gap-2">
                                            <a href={profileData.signedGuarantor2FormUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-[#294c79] underline">✅ Signed form uploaded — view</a>
                                            <label className="text-xs text-[#294c79] underline cursor-pointer">(replace)<input type="file" accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.heic,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.bmp,.tiff" className="hidden" disabled={signedFormUploading.signedGuarantor2FormUrl} onChange={e => handleSignedFormUpload('signedGuarantor2FormUrl', e.target.files[0])} /></label>
                                          </div>
                                        ) : (
                                          <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-dashed border-[#294c79]/40 text-xs text-[#294c79] cursor-pointer hover:bg-[#f3f7fb] transition ${signedFormUploading.signedGuarantor2FormUrl ? 'opacity-50 pointer-events-none' : ''}`}>
                                            {signedFormUploading.signedGuarantor2FormUrl ? '⏳ Uploading…' : '📎 Upload Signed Guarantor 2 Form'}
                                            <input type="file" accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.heic,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.bmp,.tiff" className="hidden" disabled={signedFormUploading.signedGuarantor2FormUrl} onChange={e => handleSignedFormUpload('signedGuarantor2FormUrl', e.target.files[0])} />
                                          </label>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {/* Employer OR Business Owner form */}
                                <div className="p-4 rounded-2xl border border-[#e8f2d1] bg-[#f8fbf2] text-sm space-y-3">
                                  <p className="font-semibold text-[#294c79]">🏢 Employment Verification Document</p>
                                  <p className="text-[#1a1a1a]/60 text-xs">Select whichever applies to you — download, complete, and upload the signed copy.</p>

                                  {/* Employer Letter */}
                                  <div className="rounded-xl border border-[#d9e5ef] bg-white p-3 space-y-2">
                                    <p className="text-xs font-semibold text-[#294c79] uppercase tracking-wide">Option A — I am Employed</p>
                                    <button onClick={() => handleDownloadEmployerLetter(app.id)} disabled={employerLetterLoading[app.id]} className="w-full px-4 py-2 rounded-xl border border-[#294c79] text-[#294c79] font-semibold text-xs hover:bg-[#f3f7fb] transition disabled:opacity-50 text-left">
                                      {employerLetterLoading[app.id] ? '⏳ Preparing…' : '⬇ Download Employer Notification Letter (PDF)'}
                                    </button>
                                    <div>
                                      <p className="text-xs text-[#1a1a1a]/50 mb-1">Upload signed / HR-stamped copy:</p>
                                      {profileData.signedEmployerFormUrl ? (
                                        <div className="flex items-center gap-2">
                                          <button onClick={() => openDoc(profileData.signedEmployerFormUrl)} className="text-xs text-[#294c79] underline">✅ Uploaded — view</button>
                                          <label className="text-xs text-[#294c79] underline cursor-pointer">(replace)<input type="file" accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.heic,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.bmp,.tiff" className="hidden" disabled={signedFormUploading.signedEmployerFormUrl} onChange={e => handleSignedFormUpload('signedEmployerFormUrl', e.target.files[0])} /></label>
                                        </div>
                                      ) : (
                                        <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-dashed border-[#294c79]/40 text-xs text-[#294c79] cursor-pointer hover:bg-[#f3f7fb] transition ${signedFormUploading.signedEmployerFormUrl ? 'opacity-50 pointer-events-none' : ''}`}>
                                          {signedFormUploading.signedEmployerFormUrl ? '⏳ Uploading…' : '📎 Upload Signed Employer Letter'}
                                          <input type="file" accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.heic,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.bmp,.tiff" className="hidden" disabled={signedFormUploading.signedEmployerFormUrl} onChange={e => handleSignedFormUpload('signedEmployerFormUrl', e.target.files[0])} />
                                        </label>
                                      )}
                                    </div>
                                  </div>

                                  {/* Business Owner Form */}
                                  <div className="rounded-xl border border-[#d9e5ef] bg-white p-3 space-y-2">
                                    <p className="text-xs font-semibold text-[#294c79] uppercase tracking-wide">Option B — I am a Business Owner</p>
                                    <button onClick={handleDownloadBusinessOwnerForm} disabled={businessOwnerFormLoading} className="w-full px-4 py-2 rounded-xl border border-[#294c79] text-[#294c79] font-semibold text-xs hover:bg-[#f3f7fb] transition disabled:opacity-50 text-left">
                                      {businessOwnerFormLoading ? '⏳ Preparing…' : '⬇ Download Business Owner Form (PDF)'}
                                    </button>
                                    <div>
                                      <p className="text-xs text-[#1a1a1a]/50 mb-1">Upload completed signed copy:</p>
                                      {profileData.signedBusinessOwnerFormUrl ? (
                                        <div className="flex items-center gap-2">
                                          <button onClick={() => openDoc(profileData.signedBusinessOwnerFormUrl)} className="text-xs text-[#294c79] underline">✅ Uploaded — view</button>
                                          <label className="text-xs text-[#294c79] underline cursor-pointer">(replace)<input type="file" accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.heic,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.bmp,.tiff" className="hidden" disabled={signedFormUploading.signedBusinessOwnerFormUrl} onChange={e => handleSignedFormUpload('signedBusinessOwnerFormUrl', e.target.files[0])} /></label>
                                        </div>
                                      ) : (
                                        <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-dashed border-[#294c79]/40 text-xs text-[#294c79] cursor-pointer hover:bg-[#f3f7fb] transition ${signedFormUploading.signedBusinessOwnerFormUrl ? 'opacity-50 pointer-events-none' : ''}`}>
                                          {signedFormUploading.signedBusinessOwnerFormUrl ? '⏳ Uploading…' : '📎 Upload Signed Business Owner Form'}
                                          <input type="file" accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.heic,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.bmp,.tiff" className="hidden" disabled={signedFormUploading.signedBusinessOwnerFormUrl} onChange={e => handleSignedFormUpload('signedBusinessOwnerFormUrl', e.target.files[0])} />
                                        </label>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* UPFRONT PAYMENT stage */}
                            {stage === 'upfront_payment' && (
                              <div className="p-4 rounded-2xl border border-[#cfe1f7] bg-[#f3f8fe] text-sm text-[#294c79]">
                                {upfrontPayment?.status === 'completed' ? (
                                  <div className="text-green-700">
                                    ✅ <strong>30% upfront payment confirmed.</strong> LiteRent is preparing your monthly payment plan. Admin will set up your direct debit shortly.
                                  </div>
                                ) : (
                                  <>
                                    <p className="font-semibold mb-1">✅ Verification Approved — Pay 30% Upfront</p>
                                    <div className="bg-white rounded-xl p-3 border border-[#d9e5ef] mb-3 space-y-1">
                                      <div className="flex justify-between text-sm"><span className="text-gray-500">30% Upfront</span><span className="font-bold text-[#294c79]">{formatCurrency(app.upfront_amount || Math.ceil(app.annual_rent * 0.3))}</span></div>
                                      <div className="flex justify-between text-sm"><span className="text-gray-500">Then monthly</span><span className="font-semibold text-[#294c79]">{formatCurrency(app.monthly_instalment_amount)} × {app.monthly_instalments_total || 11}</span></div>
                                    </div>
                                    {upfrontPayment ? (
                                      <div className="space-y-2">
                                        <button
                                          onClick={() => handlePayNow(upfrontPayment.id)}
                                          disabled={paymentLoading[upfrontPayment.id]}
                                          className={`w-full px-6 py-3.5 rounded-xl font-bold text-base transition ${paymentLoading[upfrontPayment.id] ? 'bg-gray-300 text-gray-600' : 'bg-[#a5c250] text-white hover:bg-[#8bb83a] shadow-md'}`}
                                        >
                                          {paymentLoading[upfrontPayment.id] ? '⏳ Opening Paystack…' : `💳 Pay ${formatCurrency(app.upfront_amount || Math.ceil(app.annual_rent * 0.3))} Now`}
                                        </button>
                                        {upfrontPayment.paystack_reference && (
                                          <button
                                            onClick={() => handleVerifyPayment(upfrontPayment.id)}
                                            disabled={paymentLoading[upfrontPayment.id]}
                                            className="w-full px-5 py-2.5 rounded-xl font-medium text-sm border-2 border-[#294c79] text-[#294c79] hover:bg-[#294c79]/5 transition"
                                          >
                                            🔍 I already paid — Verify Payment
                                          </button>
                                        )}
                                      </div>
                                    ) : (
                                      <button onClick={() => handleNavClick('payments')} className="px-5 py-3 bg-[#294c79] text-white rounded-xl font-semibold text-sm hover:bg-[#1f3a5c]">Go to Payments →</button>
                                    )}
                                  </>
                                )}
                              </div>
                            )}

                            {/* DIRECT DEBIT stage */}
                            {stage === 'direct_debit' && (
                              <div className="space-y-3">
                                {(app.direct_debit_status === 'pending_mandate') ? (
                                  <div className="p-4 rounded-2xl border border-amber-200 bg-amber-50 text-sm text-amber-800">
                                    <p className="font-semibold mb-1">⏳ Completing Direct Debit Setup</p>
                                    <p className="mb-2">You were redirected to Paystack to complete the bank transfer authorization. If you did not complete it, click the button below to try again.</p>
                                    <button
                                      onClick={() => handleDirectDebitConsent(app.id)}
                                      disabled={debitConsentLoading[app.id]}
                                      className="mt-2 px-5 py-2.5 bg-[#294c79] text-white rounded-xl text-sm font-semibold hover:bg-[#1f3a5c] transition disabled:opacity-50"
                                    >
                                      {debitConsentLoading[app.id] ? '⏳ Opening Paystack…' : '🏦 Try Again on Paystack →'}
                                    </button>
                                  </div>
                                ) : app.direct_debit_status === 'pending_manual_mandate' ? (
                                  <div className="p-4 rounded-2xl border border-blue-200 bg-blue-50 text-sm text-blue-700">
                                    <p className="font-semibold mb-1">⏳ Mandate Being Prepared</p>
                                    <p>Your consent has been recorded. LiteRent is preparing your direct debit mandate. You will receive an email shortly with an approval link.</p>
                                  </div>
                                ) : debitConsentSuccess[app.id] ? (
                                  <div className="p-4 rounded-2xl border border-amber-200 bg-amber-50 text-sm text-amber-800">
                                    <p className="font-semibold mb-1">📧 Check Your Email</p>
                                    <p>Paystack has sent you a mandate approval email. Click the link in that email to complete the setup.</p>
                                  </div>
                                ) : (
                                  <div className="p-4 rounded-2xl border border-[#cfe1f7] bg-[#f3f8fe] text-sm text-[#294c79] space-y-3">
                                    <p className="font-semibold text-base">🏦 Authorise Monthly Direct Debit</p>
                                    <div className="bg-white rounded-xl p-3 border border-[#d9e5ef] text-sm space-y-1">
                                      <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Your debit account</p>
                                      <p className="font-semibold text-[#294c79]">{profileData.bankAccountName || app.bank_account_name || '—'}</p>
                                      <p className="text-xs text-gray-500">{profileData.bankName || app.tenant_bank_name || '—'} · ****{String(profileData.accountNumber || app.tenant_account_number || '').slice(-4)}</p>
                                    </div>
                                    <p className="text-[#1a1a1a]/70">
                                      LiteRent will automatically debit <strong>{formatCurrency(app.monthly_instalment_amount)}/month</strong> for {app.monthly_instalments_total || 11} months from the account above.
                                      Clicking the button takes you to <strong>Paystack's secure page</strong> where you complete the authorisation in about 1 minute.
                                    </p>
                                    <div className="bg-[#f0f5ff] rounded-xl p-3 text-xs text-[#294c79] border border-[#c0d4f5]">
                                      💡 <strong>How it works:</strong> Click the button below → Paystack page opens → enter your bank account details + OTP → authorisation complete → your rental activates automatically → LiteRent debits your account monthly.
                                    </div>
                                    <button
                                      onClick={() => handleDirectDebitConsent(app.id)}
                                      disabled={debitConsentLoading[app.id]}
                                      className="w-full px-6 py-3.5 bg-[#294c79] text-white rounded-xl font-bold hover:bg-[#1f3a5c] transition disabled:opacity-50 text-base"
                                    >
                                      {debitConsentLoading[app.id] ? '⏳ Opening Paystack…' : '🏦 Authorise Direct Debit on Paystack →'}
                                    </button>
                                    <p className="text-xs text-[#1a1a1a]/40 text-center">You will receive a receipt email for every successful debit. Contact support anytime.</p>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* ACTIVE stage */}
                            {stage === 'active' && (
                              <div className="space-y-3">
                                <div className="p-4 rounded-2xl border border-green-200 bg-green-50 text-sm text-green-700">
                                  ✅ Your LiteRent plan is active. Track receipts, monthly debits, and your lease under <button onClick={() => handleNavClick('my-rentals')} className="underline font-semibold">My Rentals</button> and <button onClick={() => handleNavClick('payments')} className="underline font-semibold">Payments</button>.
                                </div>
                                {/* Change Direct Debit Account — request flow */}
                                <ChangeDebitAccountForm
                                  appId={app.id}
                                  API_URL={API_URL}
                                  getToken={getToken}
                                  bankOptions={bankOptions}
                                  onRequested={() => loadApplications()}
                                />
                              </div>
                            )}

                            {/* DOCUMENT REQUESTS from admin */}
                            <DocumentRequests
                              appId={app.id}
                              API_URL={API_URL}
                              getHeaders={getHeaders}
                            />

                            {/* Download application summary */}
                            <div className="pt-2">
                              <button
                                onClick={() => {
                                  const token = getToken();
                                  const w = window.open('', '_blank');
                                  fetch(`${API_URL}/api/tenant/applications/${app.id}/summary`, {
                                    headers: { Authorization: `Bearer ${token}` }
                                  }).then(r => r.ok ? r.text() : Promise.reject('Not available')).then(html => {
                                    w.document.write(html); w.document.close();
                                  }).catch(() => { w.close(); alert('Could not load application summary. Please try again.'); });
                                }}
                                className="px-5 py-2.5 border border-[#294c79] text-[#294c79] rounded-xl text-sm font-semibold hover:bg-[#f3f7fb] transition"
                              >
                                📄 Download My Application
                              </button>
                            </div>

                            {/* REJECTED */}
                            {stage === 'rejected' && (
                              <div className="p-4 rounded-2xl border border-red-200 bg-red-50 text-sm text-red-700">
                                This application could not proceed. Please review the note above or contact support if you need help.
                              </div>
                            )}

                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          )}

          {/* ════ MY RENTALS ════ */}
          {activeTab === 'my-rentals' && (
            <section>
              <h1 className="text-3xl md:text-4xl font-bold text-[#294c79] mb-10">My Rentals</h1>
              {rentalsLoading ? (
                <BrandLoadingScreen fullScreen={false} compact message="Loading your rental records…" />
              ) : rentals.length === 0 ? (
                <div className="bg-white p-10 rounded-2xl shadow-md text-center">
                  <p className="text-xl text-[#1a1a1a]/70">No active rentals yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {rentals.map(rental => {
                    const isOpen = expandedRentalId === rental.id;
                    const isDetailLoading = rentalDetailLoading[rental.id];
                    const detail = rentalDetails[rental.id]; // { rental, payments }
                    return (
                      <div key={rental.id} className={`rounded-2xl border transition overflow-hidden ${isOpen ? 'border-[#294c79] shadow-lg' : 'border-[#e8f2d1] bg-white shadow-md hover:border-[#294c79]/40 hover:shadow-lg'}`}>
                        {/* Clickable summary */}
                        <button
                          type="button"
                          onClick={() => {
                            setExpandedRentalId(prev => prev === rental.id ? null : rental.id);
                            if (!detail) loadRentalDetail(rental.id);
                          }}
                          aria-expanded={isOpen}
                          className="w-full text-left p-6 bg-white flex items-start justify-between gap-4 focus:outline-none focus:ring-2 focus:ring-[#a5c250]/40"
                        >
                          <div className="flex-1 min-w-0">
                            <h3 className="text-xl sm:text-2xl font-semibold text-[#294c79] mb-1 truncate">{rental.title}</h3>
                            <p className="text-[#1a1a1a]/70 text-sm mb-3">📍 {rental.location}</p>
                            <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
                              <span className="text-gray-500">Monthly: <strong className="text-[#1a1a1a]">₦{parseFloat(rental.monthly_payment).toLocaleString()}</strong></span>
                              <span className="text-gray-500">Progress: <strong className="text-[#1a1a1a]">{rental.instalments_paid}/{rental.total_instalments}</strong></span>
                              <span className={`capitalize px-2 py-0.5 rounded-full text-xs font-semibold ${rental.status === 'active' ? 'bg-green-100 text-green-700' : rental.status === 'completed' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>{rental.status}</span>
                            </div>
                          </div>
                          <svg className={`w-5 h-5 text-[#294c79] transition-transform flex-shrink-0 mt-2 ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>

                        {/* Expanded detail */}
                        {isOpen && (
                          <div className="border-t border-[#edf2f7] bg-[#fbfcfe] px-6 py-6 space-y-5">
                            {isDetailLoading ? (
                              <BrandLoadingScreen fullScreen={false} compact message="Loading lease details…" />
                            ) : !detail ? (
                              <p className="text-sm text-gray-500">Could not load lease details. <button onClick={() => loadRentalDetail(rental.id)} className="text-[#294c79] underline">Retry</button></p>
                            ) : (
                              <>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                  {[
                                    ['Annual Rent', `₦${parseFloat(detail.rental.annual_rent).toLocaleString()}`],
                                    ['Monthly', `₦${parseFloat(detail.rental.monthly_payment).toLocaleString()}`],
                                    ['Remaining Balance', `₦${parseFloat(detail.rental.remaining_balance || 0).toLocaleString()}`],
                                    ['Instalments', `${detail.rental.instalments_paid}/${detail.rental.total_instalments}`],
                                    ['Lease Start', detail.rental.lease_start_date ? new Date(detail.rental.lease_start_date).toLocaleDateString('en-NG') : '—'],
                                    ['Lease End', detail.rental.lease_end_date ? new Date(detail.rental.lease_end_date).toLocaleDateString('en-NG') : '—'],
                                    ['Direct Debit', (detail.rental.direct_debit_status || 'pending').replace(/_/g, ' ')],
                                    ['Status', detail.rental.status],
                                  ].map(([label, val]) => (
                                    <div key={label} className="rounded-xl bg-white border border-[#edf2f7] p-3">
                                      <p className="text-xs text-gray-400 mb-0.5 uppercase tracking-wide">{label}</p>
                                      <p className="font-semibold text-[#1a1a1a] text-sm capitalize">{val}</p>
                                    </div>
                                  ))}
                                </div>

                                {/* Payment progress */}
                                <div>
                                  <div className="flex justify-between text-sm text-gray-500 mb-2">
                                    <span>Payment progress</span>
                                    <span>{detail.rental.instalments_paid}/{detail.rental.total_instalments} paid</span>
                                  </div>
                                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-[#a5c250] rounded-full transition-all"
                                      style={{ width: `${(detail.rental.instalments_paid / detail.rental.total_instalments) * 100}%` }}
                                    />
                                  </div>
                                </div>

                                {/* Payment history */}
                                {detail.payments && detail.payments.length > 0 && (
                                  <div>
                                    <p className="text-xs font-bold text-[#294c79] uppercase tracking-wide mb-2">Payment History ({detail.payments.length})</p>
                                    <div className="bg-white rounded-xl border border-[#edf2f7] overflow-hidden">
                                      <div className="overflow-x-auto">
                                        <table className="w-full text-xs">
                                          <thead className="bg-[#e8f2d1] text-[#294c79]">
                                            <tr>
                                              {['Due', 'Amount', 'Type', 'Status', 'Paid On', 'Receipt'].map(h => <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>)}
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {detail.payments.map(p => (
                                              <tr key={p.id} className="border-t border-gray-100">
                                                <td className="px-3 py-2 whitespace-nowrap text-gray-500">{p.due_date ? new Date(p.due_date).toLocaleDateString('en-NG') : '—'}</td>
                                                <td className="px-3 py-2 whitespace-nowrap font-bold">₦{parseFloat(p.amount).toLocaleString()}</td>
                                                <td className="px-3 py-2 whitespace-nowrap capitalize">{p.category}</td>
                                                <td className="px-3 py-2 whitespace-nowrap">
                                                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize ${
                                                    p.status === 'completed' ? 'bg-green-100 text-green-700'
                                                    : p.status === 'pending' ? 'bg-yellow-100 text-yellow-700'
                                                    : p.status === 'failed' ? 'bg-orange-100 text-orange-700'
                                                    : 'bg-gray-100 text-gray-600'
                                                  }`}>{p.status}</span>
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap text-gray-500">{p.paid_at ? new Date(p.paid_at).toLocaleDateString('en-NG') : '—'}</td>
                                                <td className="px-3 py-2 whitespace-nowrap text-gray-500 text-[11px]">{p.receipt_number || '—'}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                <div className="pt-2 flex flex-wrap gap-2">
                                  <button onClick={() => handleNavClick('payments')} className="px-5 py-2.5 rounded-xl bg-[#294c79] text-white text-sm font-semibold hover:bg-[#1f3a5c] transition">
                                    View & Pay Instalments →
                                  </button>
                                </div>

                                {/* Change Direct Debit Account — visible in My Rentals */}
                                {rental.status === 'active' && (
                                  <ChangeDebitAccountForm
                                    appId={detail.rental.application_id}
                                    API_URL={API_URL}
                                    getToken={getToken}
                                    bankOptions={bankOptions}
                                    onRequested={() => loadRentalDetail(rental.id)}
                                  />
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          )}

          {/* ════ PAYMENTS ════ */}
          {activeTab === 'payments' && (
            <section>
              <h1 className="text-3xl md:text-4xl font-bold text-[#294c79] mb-10">Payments</h1>

              {!profileData.email && (
                <div className="bg-amber-50 border border-amber-300 rounded-2xl p-4 mb-6 flex items-start gap-3">
                  <span className="text-2xl">⚠️</span>
                  <div>
                    <p className="font-semibold text-amber-800">Email required for payments</p>
                    <p className="text-sm text-amber-700 mt-1">Please add your email address to your profile to make payments via Paystack.</p>
                    <button onClick={() => { setActiveTab('profile'); setProfileSubTab('update'); }} className="mt-2 text-sm text-amber-800 underline font-medium">Update Profile →</button>
                  </div>
                </div>
              )}

              {paymentsLoading ? (
                <BrandLoadingScreen fullScreen={false} compact message="Loading your payment timeline…" />
              ) : payments.length === 0 ? (
                <div className="bg-white p-10 rounded-2xl shadow-md text-center">
                  <p className="text-xl text-[#1a1a1a]/70">No payment history yet</p>
                </div>
              ) : (
                <>
                  {(() => {
                    // Only show pending payments that are truly actionable:
                    // - instalment: only if rental is still active (is_actionable flag from backend)
                    // - upfront/verification_fee: only if application is not rejected/withdrawn
                    const pendingVerifFee = payments.filter(p => p.status === 'pending' && p.category === 'verification_fee' && p.is_actionable !== false);
                    const pendingInstalments = payments
                      .filter(p => p.status === 'pending' && p.category === 'instalment' && p.is_actionable !== false)
                      .sort((a, b) => (a.instalment_number || 0) - (b.instalment_number || 0));
                    const pendingUpfront = payments.filter(p => p.status === 'pending' && p.category === 'upfront' && p.is_actionable !== false);
                    const allPending = [...pendingVerifFee, ...pendingUpfront, ...(pendingInstalments.length > 0 ? [pendingInstalments[0]] : [])];
                    if (allPending.length === 0) return null;
                    return (
                      <div className="mb-8">
                        <h2 className="text-xl font-semibold text-[#294c79] mb-1">⏳ Payments Due</h2>
                        <p className="text-sm text-gray-500 mb-4">
                          {pendingVerifFee.length > 0 && <span className="mr-3">🔍 Verification fee pending</span>}
                          {pendingUpfront.length > 0 && <span className="mr-3">🏠 Upfront payment pending</span>}
                          {pendingInstalments.length > 1 && <span>{pendingInstalments.length} monthly instalments remaining · showing next due</span>}
                        </p>
                        <div className="space-y-4">
                          {allPending.map(payment => {
                            const isOverdue = payment.due_date && new Date(payment.due_date) < new Date();
                            const dueDate = payment.due_date
                              ? new Date(payment.due_date).toLocaleDateString('en-NG', { year: 'numeric', month: 'long', day: 'numeric' })
                              : null;
                            return (
                              <div key={payment.id} className={`bg-white p-5 rounded-2xl shadow-md border-2 ${isOverdue ? 'border-red-300 bg-red-50' : 'border-[#e8f2d1]'}`}>
                                <div className="flex flex-wrap items-start justify-between gap-4">
                                  <div className="flex-1">
                                    <p className="font-bold text-[#294c79] text-2xl">₦{parseFloat(payment.amount).toLocaleString()}</p>
                                    <p className="text-gray-600 text-sm mt-1">{payment.description || payment.type}</p>
                                    {dueDate && (
                                      <p className="text-gray-500 text-sm mt-2 font-medium">
                                        📅 Due: {dueDate}
                                        {isOverdue && <span className="ml-2 text-red-600 font-semibold">⚠️ OVERDUE</span>}
                                      </p>
                                    )}
                                    {payment.instalment_number && (() => {
                                      let meta = {};
                                      try { meta = typeof payment.metadata === 'string' ? JSON.parse(payment.metadata) : (payment.metadata || {}); } catch {}
                                      return (
                                        <p className="text-xs text-gray-400 mt-1">
                                          Instalment {payment.instalment_number} of {meta.totalInstalments || 11}
                                          {pendingInstalments.length > 1 && ` · ${pendingInstalments.length - 1} more after this`}
                                        </p>
                                      );
                                    })()}
                                    {payment.category === 'instalment' && (
                                      <p className="text-xs text-[#294c79]/60 mt-1">🏦 This will be automatically debited via direct debit</p>
                                    )}
                                    {payment.category === 'verification_fee' && (
                                      <p className="text-xs text-amber-600 mt-1 font-medium">🔍 One-time verification fee — required to proceed with your application</p>
                                    )}
                                    {payment.category === 'upfront' && (
                                      <p className="text-xs text-[#294c79]/60 mt-1">🏠 30% upfront move-in payment — secures your property</p>
                                    )}
                                  </div>
                                  <div className="flex flex-col gap-2 min-w-[160px]">
                                    <button
                                      onClick={() => handlePayNow(payment.id)}
                                      disabled={paymentLoading[payment.id] || !profileData.email}
                                      className={`px-5 py-3 rounded-xl font-semibold text-sm transition ${paymentLoading[payment.id] ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : !profileData.email ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-[#a5c250] text-white hover:bg-[#8bb83a] shadow-md'}`}
                                    >
                                      {paymentLoading[payment.id] ? '⏳ Processing...' : '💳 Pay Now'}
                                    </button>
                                    {payment.paystack_reference && (
                                      <button
                                        onClick={() => handleVerifyPayment(payment.id)}
                                        disabled={paymentLoading[payment.id]}
                                        className="px-5 py-2 rounded-xl font-medium text-sm border-2 border-[#294c79] text-[#294c79] hover:bg-[#294c79]/5 transition"
                                      >
                                        🔍 Verify Payment
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}

                  {payments.filter(p => p.status === 'completed').length > 0 && (
                    <div>
                      <h2 className="text-xl font-semibold text-[#294c79] mb-4">✅ Payment History</h2>
                      <div className="bg-white rounded-2xl shadow-md border border-[#e8f2d1] overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-[#e8f2d1]">
                              <tr>
                                {['Date Paid', 'Description', 'Amount', 'Receipt No.', 'Status', 'Receipt'].map(h => (
                                  <th key={h} className="px-6 py-4 text-left text-sm font-semibold text-[#294c79]">{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {payments.filter(p => p.status === 'completed').map(payment => (
                                <tr key={payment.id} className="border-t border-[#e8f2d1] hover:bg-gray-50">
                                  <td className="px-6 py-4 text-sm">{payment.paid_at ? new Date(payment.paid_at).toLocaleDateString('en-NG') : new Date(payment.created_at).toLocaleDateString('en-NG')}</td>
                                  <td className="px-6 py-4 text-sm">{payment.description || payment.type}</td>
                                  <td className="px-6 py-4 font-bold text-[#294c79]">₦{parseFloat(payment.amount).toLocaleString()}</td>
                                  <td className="px-6 py-4 text-xs text-gray-500 font-mono">{payment.receipt_number || '—'}</td>
                                  <td className="px-6 py-4">
                                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">✓ Paid</span>
                                  </td>
                                  <td className="px-6 py-4">
                                    <button
                                      onClick={() => handleDownloadReceipt(payment.id)}
                                      disabled={receiptLoading[payment.id]}
                                      className="px-4 py-2 bg-[#294c79] text-white rounded-lg text-xs font-medium hover:bg-[#1f3a5c] transition disabled:bg-gray-300"
                                    >
                                      {receiptLoading[payment.id] ? '⏳...' : '📄 Download'}
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </section>
          )}

          {/* ════ UPLOAD HOUSE ════ */}
          {activeTab === 'upload-house' && (
            <section>
              <h1 className="text-3xl md:text-4xl font-bold text-[#294c79] mb-10">Upload a House</h1>

              <form
                className="bg-white p-10 rounded-2xl shadow-md space-y-6 mb-10"
                onSubmit={async (e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  houseImages.forEach(img => formData.append('images', img));
                  setLoading(true);
                  try {
                    await axios.post(`${API_URL}/api/upload-house`, formData, {
                      headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
                    });
                    alert('✅ House submitted for review! Our team will get back to you shortly.');
                    e.target.reset();
                    setHouseImages([]);
                    setHouseImagePreviews([]);
                    await loadMySubmissions();
                  } catch (err) {
                    alert(err.response?.data?.error || 'Failed to submit house. Please try again.');
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                <div>
                  <label className="block text-sm font-semibold text-[#294c79] mb-2">House Address <span className="text-red-600">*</span></label>
                  <input name="address" placeholder="Full house address" required className="w-full px-5 py-4 border-2 border-[#afc09e] rounded-xl focus:outline-none focus:border-[#a5c250]" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#294c79] mb-2">Area / Location <span className="text-red-600">*</span></label>
                  <input name="location" placeholder="e.g. Lekki Phase 1, Lagos" required className="w-full px-5 py-4 border-2 border-[#afc09e] rounded-xl focus:outline-none focus:border-[#a5c250]" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-[#294c79] mb-2">Rent Requested (₦/year)</label>
                    <input name="landlordRent" type="number" min="0" placeholder="Annual rent amount" className="w-full px-5 py-4 border-2 border-[#afc09e] rounded-xl focus:outline-none focus:border-[#a5c250]" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#294c79] mb-2">Landlord Contact</label>
                    <input name="landlordContact" placeholder="Phone number or email" className="w-full px-5 py-4 border-2 border-[#afc09e] rounded-xl focus:outline-none focus:border-[#a5c250]" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#294c79] mb-2">
                    House Images <span className="text-gray-400 font-normal">(Up to 5 — JPG, PNG, WebP)</span>
                  </label>
                  <div
                    className="border-2 border-dashed border-[#afc09e] rounded-xl p-6 text-center cursor-pointer hover:border-[#a5c250] transition"
                    onClick={() => houseImagesRef.current?.click()}
                  >
                    <input
                      ref={houseImagesRef}
                      type="file"
                      multiple
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      className="hidden"
                      onChange={handleHouseImagesChange}
                    />
                    {houseImagePreviews.length === 0 ? (
                      <div>
                        <p className="text-4xl mb-2">📷</p>
                        <p className="text-[#294c79] font-medium">Click to upload images</p>
                        <p className="text-sm text-gray-500 mt-1">Max 5 images, 5MB each</p>
                      </div>
                    ) : (
                      <p className="text-[#294c79] font-medium">{houseImages.length} image(s) selected — click to change</p>
                    )}
                  </div>
                  {houseImagePreviews.length > 0 && (
                    <div className="flex gap-3 mt-4 flex-wrap">
                      {houseImagePreviews.map((preview, idx) => (
                        <div key={idx} className="relative w-24 h-24 rounded-xl overflow-hidden border-2 border-[#afc09e] group">
                          <img src={preview} alt={`Preview ${idx + 1}`} className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); removeHouseImage(idx); }}
                            className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition"
                          >×</button>
                          <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs text-center py-0.5">{idx + 1}</div>
                        </div>
                      ))}
                      {houseImages.length < 5 && (
                        <button
                          type="button"
                          onClick={() => houseImagesRef.current?.click()}
                          className="w-24 h-24 rounded-xl border-2 border-dashed border-[#afc09e] flex items-center justify-center text-[#294c79] hover:border-[#a5c250] transition text-2xl"
                        >+</button>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#294c79] mb-2">Additional Notes</label>
                  <textarea name="notes" placeholder="Any extra details about the property..." className="w-full px-5 py-4 border-2 border-[#afc09e] rounded-xl h-32 focus:outline-none focus:border-[#a5c250]" />
                </div>
                <button type="submit" disabled={loading} className={`w-full px-10 py-5 rounded-full font-semibold text-lg transition ${loading ? 'bg-gray-400 text-white cursor-not-allowed' : 'bg-[#294c79] text-white hover:bg-[#1f3a5c]'}`}>
                  {loading ? '⏳ Submitting...' : '📤 Submit for Review'}
                </button>
              </form>

              <div>
                <h2 className="text-2xl font-bold text-[#294c79] mb-6">My Submissions</h2>
                {submissionsLoading ? (
                  <BrandLoadingScreen fullScreen={false} compact message="Loading your uploaded house submissions…" />
                ) : mySubmissions.length === 0 ? (
                  <div className="bg-white p-8 rounded-2xl shadow-md text-center text-gray-500">No submissions yet</div>
                ) : (
                  <div className="space-y-4">
                    {mySubmissions.map(sub => {
                      const isOpen = expandedSubmissionId === sub.id;
                      const isApproved = sub.status === 'approved';
                      return (
                        // FIX #2: Wrap each submission in a clickable container. Clicking the
                        // summary row toggles the details panel; approved rows additionally
                        // expose an "Apply for this house" button once expanded.
                        <div key={sub.id} className={`rounded-2xl shadow-md border transition overflow-hidden ${isOpen ? 'border-[#294c79] bg-white' : 'border-gray-200 bg-white hover:border-[#294c79]/40'}`}>
                          <button
                            type="button"
                            onClick={() => setExpandedSubmissionId(prev => prev === sub.id ? null : sub.id)}
                            className="w-full text-left p-5 flex flex-wrap items-start gap-4 focus:outline-none focus:ring-2 focus:ring-[#a5c250]/40 rounded-2xl"
                            aria-expanded={isOpen}
                          >
                            {/* Thumbnail — cover image only on the summary row */}
                            {sub.images && sub.images.length > 0 && (
                              <img
                                src={sub.images[0]}
                                alt={`${sub.address} thumbnail`}
                                className="w-20 h-20 object-cover rounded-xl border border-gray-200 flex-shrink-0"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <h3 className="font-bold text-[#294c79]">{sub.address}</h3>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getSubmissionStatusColor(sub.status)}`}>{sub.status}</span>
                                {isApproved && <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-[#a5c250] text-white">Ready to Apply</span>}
                              </div>
                              <p className="text-sm text-gray-600">📍 {sub.location}</p>
                              {sub.landlord_rent && <p className="text-sm text-gray-600">💰 ₦{Number(sub.landlord_rent).toLocaleString()}/year</p>}
                              <p className="text-xs text-gray-400 mt-2">Submitted: {new Date(sub.created_at).toLocaleDateString('en-NG')}</p>
                            </div>
                            <svg className={`w-5 h-5 text-[#294c79] transition-transform flex-shrink-0 mt-2 ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>

                          {isOpen && (
                            <div className="border-t border-gray-100 bg-[#fbfcfe] px-5 py-5 space-y-4">
                              {/* FIX #16: Show ALL photos the user uploaded, not just the first three. */}
                              {sub.images && sub.images.length > 0 && (
                                <div>
                                  <p className="text-xs font-semibold text-[#294c79] mb-2 uppercase tracking-wide">Photos ({sub.images.length})</p>
                                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                                    {sub.images.map((img, idx) => (
                                      <a key={idx} href={img} target="_blank" rel="noopener noreferrer" className="block aspect-square rounded-xl overflow-hidden border border-gray-200 hover:opacity-90 transition">
                                        <img
                                          src={img}
                                          alt={`${sub.address} photo ${idx + 1}`}
                                          className="w-full h-full object-cover"
                                          loading="lazy"
                                          onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                      </a>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {sub.landlord_contact && (
                                <p className="text-sm text-gray-600"><strong>Landlord contact:</strong> {sub.landlord_contact}</p>
                              )}
                              {sub.notes && (
                                <div className="text-sm text-gray-600"><strong>Your notes:</strong> {sub.notes}</div>
                              )}
                              {sub.admin_notes && (
                                <div className="p-3 bg-blue-50 rounded-xl text-sm text-[#294c79]">
                                  <strong>Admin note:</strong> {sub.admin_notes}
                                </div>
                              )}

                              {/* FIX #2: Approved tenant submissions stay private to this tenant's dashboard. */}
                              {isApproved && (
                                <div className="pt-2 flex flex-wrap gap-2">
                                  {sub.house_id ? (
                                    <button
                                      onClick={async () => {
                                        try {
                                          const res = await axios.get(
                                            `${API_URL}/api/houses/${sub.house_id}/my`,
                                            { headers: getHeaders() }
                                          );
                                          const house = res.data?.house;
                                          if (house) {
                                            navigate('/apply', { state: { house } });
                                          } else {
                                            alert('Could not load house details. Please try again.');
                                          }
                                        } catch (err) {
                                          alert(err.response?.data?.message || 'Could not load house. Please try again.');
                                        }
                                      }}
                                      className="px-5 py-2.5 rounded-xl bg-[#294c79] text-white text-sm font-semibold hover:bg-[#1f3a5c] transition inline-flex items-center gap-2"
                                    >
                                      Apply for this house →
                                    </button>
                                  ) : (
                                    <p className="text-sm text-[#294c79] bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
                                      Approved. LiteRent is preparing the private application link for this submission.
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* ════ NOTIFICATIONS ════ */}
          {activeTab === 'notifications' && (
            <section>
              <div className="flex flex-wrap gap-3 justify-between items-center mb-8">
                <h1 className="text-3xl md:text-4xl font-bold text-[#294c79]">Notifications</h1>
                <div className="flex gap-2 flex-wrap">
                  {unreadCount > 0 && (
                    <button onClick={markAllRead} className="px-4 py-2 text-sm text-[#294c79] border border-[#294c79] rounded-xl hover:bg-[#294c79]/5">
                      ✓ Mark all read
                    </button>
                  )}
                  {notifications.length > 0 && (
                    <button onClick={deleteAllNotifications} className="px-4 py-2 text-sm text-red-600 border border-red-200 rounded-xl hover:bg-red-50">
                      🗑 Delete all
                    </button>
                  )}
                </div>
              </div>

              <div className="flex gap-2 mb-6 flex-wrap">
                {[
                  { key: 'all', label: 'All', icon: '🔔' },
                  { key: 'payment', label: 'Payments', icon: '💳' },
                  { key: 'application', label: 'Applications', icon: '📋' },
                  { key: 'ticket_reply', label: 'Ticket Replies', icon: '💬' },
                  { key: 'general', label: 'General', icon: '📢' },
                ].map(f => (
                  <button
                    key={f.key}
                    onClick={() => setNotifFilter(f.key)}
                    className={`px-4 py-2 rounded-xl font-medium text-sm transition flex items-center gap-1 ${notifFilter === f.key ? 'bg-[#294c79] text-white' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'}`}
                  >
                    {f.icon} {f.label}
                  </button>
                ))}
              </div>

              {notificationsLoading ? (
                <BrandLoadingScreen fullScreen={false} compact message="Loading your LiteRent notifications…" />
              ) : notifications.length === 0 ? (
                <div className="bg-white p-10 rounded-2xl shadow-md text-center">
                  <p className="text-4xl mb-4">🔔</p>
                  <p className="text-xl text-[#1a1a1a]/70">No notifications yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {notifications.map(notif => (
                    <div
                      key={notif.id}
                      onClick={() => !notif.is_read && markNotifRead(notif.id)}
                      className={`bg-white p-5 rounded-2xl shadow-md border cursor-pointer transition hover:shadow-lg ${notif.is_read ? 'border-gray-200 opacity-80' : 'border-[#a5c250]'}`}
                    >
                      <div className="flex gap-4 items-start">
                        <span className="text-2xl flex-shrink-0">{getNotifIcon(notif.type)}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap justify-between items-start gap-2 mb-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getNotifColor(notif.type)}`}>
                                {notif.type?.replace('_', ' ')}
                              </span>
                              {!notif.is_read && <span className="w-2 h-2 bg-[#a5c250] rounded-full inline-block"></span>}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500 whitespace-nowrap">{new Date(notif.created_at).toLocaleString('en-NG')}</span>
                              <button
                                onClick={e => { e.stopPropagation(); deleteNotification(notif.id); }}
                                className="text-gray-300 hover:text-red-500 transition text-lg leading-none flex-shrink-0"
                                title="Delete notification"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                          <p className="font-semibold text-gray-800 mb-1">{notif.title}</p>
                          <p className="text-gray-600 text-sm">{notif.message}</p>
                          {notif.from_user && <p className="text-xs text-gray-500 mt-1">From: {notif.from_user}</p>}
                          {notif.related_type === 'ticket' && notif.related_id && (
                            <button onClick={(e) => { e.stopPropagation(); handleNavClick('support'); setTimeout(() => loadTicketDetail(notif.related_id), 200); }}
                              className="mt-2 text-sm text-blue-600 hover:underline font-medium">
                              View & Reply to Ticket →
                            </button>
                          )}
                          {notif.related_type === 'application' && (
                            <button onClick={(e) => { e.stopPropagation(); handleNavClick('my-applications'); }}
                              className="mt-2 text-sm text-green-600 hover:underline font-medium">
                              View Application →
                            </button>
                          )}
                          {notif.related_type === 'uploaded_house' && (
                            <button onClick={(e) => { e.stopPropagation(); handleNavClick('upload-house'); }}
                              className="mt-2 text-sm text-[#294c79] hover:underline font-medium">
                              View Submitted House →
                            </button>
                          )}
                          {notif.related_type === 'payment' && (
                            <button onClick={(e) => { e.stopPropagation(); handleNavClick('payments'); }}
                              className="mt-2 text-sm text-purple-600 hover:underline font-medium">
                              View Payments →
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* ════ SUPPORT & TICKETS ════ */}
          {activeTab === 'activity' && (
            <section>
              <div className="flex flex-wrap gap-3 justify-between items-center mb-8">
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold text-[#294c79]">Activity Log</h1>
                  <p className="text-[#1a1a1a]/50 text-sm mt-1">A record of actions and events on your account.</p>
                </div>
                <button onClick={loadActivityLogs} className="px-4 py-2.5 rounded-xl border border-[#294c79] text-[#294c79] text-sm font-medium hover:bg-[#f3f7fb] transition">
                  🔄 Refresh
                </button>
              </div>
              {activityLogsLoading ? (
                <div className="flex items-center justify-center py-20 text-[#294c79]/50">Loading activity…</div>
              ) : activityLogs.length === 0 ? (
                <div className="text-center py-20 text-[#1a1a1a]/40">
                  <div className="text-5xl mb-4">📋</div>
                  <p className="font-semibold text-lg">No activity recorded yet</p>
                  <p className="text-sm">Actions like submissions, payments, and document uploads will appear here.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {activityLogs.map(log => {
                    const iconMap = {
                      application_submitted: '📝', upfront_payment_completed: '💰',
                      direct_debit_authorised: '✅', monthly_debit_success: '✔',
                      monthly_debit_failed: '⚠️', debit_change_requested: '🔄',
                      debit_change_approved: '✅', debit_change_rejected: '❌',
                      password_changed: '🔐', profile_updated: '👤',
                      document_uploaded: '📎', guarantor_form_downloaded: '📋',
                      employer_letter_downloaded: '📄', business_owner_form_downloaded: '🏢',
                      verification_fee_paid: '✅', login: '🔑',
                    };
                    const icon = iconMap[log.event_type] || '📋';
                    return (
                      <div key={log.id} className="flex gap-4 p-4 rounded-2xl border border-[#e8f2d1] bg-white hover:shadow-sm transition">
                        <div className="w-10 h-10 rounded-xl bg-[#f3f7fb] flex items-center justify-center text-xl flex-shrink-0">
                          {icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-[#294c79] text-sm">{log.event_title}</p>
                          {log.event_detail && <p className="text-[#1a1a1a]/60 text-xs mt-0.5 truncate">{log.event_detail}</p>}
                          <p className="text-[#1a1a1a]/40 text-xs mt-1">{new Date(log.created_at).toLocaleString('en-NG', { dateStyle: 'medium', timeStyle: 'short' })}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          )}

          {activeTab === 'support' && (
            <section>
              <div className="flex flex-wrap gap-3 justify-between items-center mb-8">
                <h1 className="text-3xl md:text-4xl font-bold text-[#294c79]">Support & Tickets</h1>
                <button onClick={() => setShowNewTicketForm(true)} className="px-5 py-3 bg-[#a5c250] text-white rounded-xl hover:bg-[#8bb83a] font-medium">
                  + New Ticket
                </button>
              </div>

              {showNewTicketForm && (
                <div className="bg-white p-6 rounded-2xl shadow-md border border-[#e8f2d1] mb-6">
                  <h2 className="text-xl font-semibold text-[#294c79] mb-5">Submit a Support Ticket</h2>
                  <form onSubmit={handleCreateTicket} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#294c79] mb-2">Subject *</label>
                      <input type="text" value={newTicket.subject} onChange={e => setNewTicket({ ...newTicket, subject: e.target.value })} placeholder="Brief description of your issue" className="w-full px-4 py-3 border-2 border-[#afc09e] rounded-xl focus:outline-none focus:border-[#a5c250]" required />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-[#294c79] mb-2">Category</label>
                        <select value={newTicket.category} onChange={e => setNewTicket({ ...newTicket, category: e.target.value })} className="w-full px-4 py-3 border-2 border-[#afc09e] rounded-xl focus:outline-none focus:border-[#a5c250]">
                          <option value="general">General Enquiry</option>
                          <option value="payment">Payment Issue</option>
                          <option value="maintenance">Maintenance Request</option>
                          <option value="application">Application Query</option>
                          <option value="account">Account Issue</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#294c79] mb-2">Priority</label>
                        <select value={newTicket.priority} onChange={e => setNewTicket({ ...newTicket, priority: e.target.value })} className="w-full px-4 py-3 border-2 border-[#afc09e] rounded-xl focus:outline-none focus:border-[#a5c250]">
                          <option value="normal">Normal</option>
                          <option value="high">High</option>
                          <option value="urgent">Urgent</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#294c79] mb-2">Message *</label>
                      <textarea value={newTicket.message} onChange={e => setNewTicket({ ...newTicket, message: e.target.value })} placeholder="Describe your issue in detail..." className="w-full px-4 py-3 border-2 border-[#afc09e] rounded-xl h-28 resize-none focus:outline-none focus:border-[#a5c250]" required />
                    </div>
                    <div className="flex gap-3">
                      <button type="submit" disabled={loading} className="flex-1 py-3 bg-[#294c79] text-white rounded-xl font-medium hover:bg-[#1f3a5c] disabled:bg-gray-400">
                        {loading ? 'Submitting...' : 'Submit Ticket'}
                      </button>
                      <button type="button" onClick={() => setShowNewTicketForm(false)} className="flex-1 py-3 border-2 border-gray-300 rounded-xl font-medium text-gray-600">Cancel</button>
                    </div>
                  </form>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <h2 className="text-lg font-semibold text-[#294c79] mb-3">My Tickets</h2>
                  {ticketLoading ? (
                    <BrandLoadingScreen fullScreen={false} compact message="Loading your support conversations…" />
                  ) : tickets.length === 0 ? (
                    <div className="bg-white p-8 rounded-2xl shadow-md text-center">
                      <p className="text-3xl mb-2">🎫</p>
                      <p className="text-gray-500">No support tickets yet</p>
                      <button onClick={() => setShowNewTicketForm(true)} className="mt-4 text-[#294c79] hover:underline font-medium text-sm">Create your first ticket</button>
                    </div>
                  ) : (
                    tickets.map(ticket => (
                      <div
                        key={ticket.id}
                        onClick={() => loadTicketDetail(ticket.id)}
                        className={`bg-white p-4 rounded-2xl shadow-sm border cursor-pointer transition hover:shadow-md ${selectedTicket?.id === ticket.id ? 'border-[#294c79]' : 'border-gray-200'}`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="font-semibold text-[#294c79] text-sm">{ticket.subject}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getStatusBadge(ticket.status)}`}>{ticket.status?.replace('_', ' ')}</span>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <span className="text-xs bg-gray-100 px-2 py-0.5 rounded text-gray-600">{ticket.category}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${ticket.priority === 'urgent' ? 'bg-red-100 text-red-600' : ticket.priority === 'high' ? 'bg-orange-100 text-orange-600' : 'bg-gray-100 text-gray-600'}`}>{ticket.priority}</span>
                        </div>
                        <p className="text-xs text-gray-400 mt-2">{new Date(ticket.updated_at).toLocaleString('en-NG')}</p>
                      </div>
                    ))
                  )}
                </div>

                <div className="bg-white rounded-2xl shadow-md border border-gray-200 flex flex-col" style={{ minHeight: '400px', maxHeight: '600px' }}>
                  {!selectedTicket ? (
                    <div className="flex-1 flex items-center justify-center text-center p-10 text-gray-400">
                      <div><p className="text-4xl mb-2">💬</p><p>Select a ticket to view the conversation</p></div>
                    </div>
                  ) : (
                    <>
                      <div className="p-4 border-b border-gray-100">
                        <h3 className="font-bold text-[#294c79]">{selectedTicket.subject}</h3>
                        <div className="flex gap-2 mt-1">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${getStatusBadge(selectedTicket.status)}`}>{selectedTicket.status?.replace('_', ' ')}</span>
                          <span className="text-xs bg-gray-100 px-2 py-0.5 rounded text-gray-600">{selectedTicket.category}</span>
                        </div>
                      </div>
                      <div className="flex-1 overflow-y-auto p-4 space-y-3">
                        <div className="bg-blue-50 rounded-xl p-3 mr-6">
                          <p className="text-xs font-semibold text-blue-600 mb-1">👤 You (original message)</p>
                          <p className="text-sm text-gray-800">{selectedTicket.message}</p>
                          <p className="text-xs text-gray-400 mt-1">{new Date(selectedTicket.created_at).toLocaleString('en-NG')}</p>
                        </div>
                        {ticketReplies.map(reply => (
                          <div key={reply.id} className={`rounded-xl p-3 ${reply.is_admin_reply ? 'bg-[#e8f2d1] ml-6' : 'bg-blue-50 mr-6'}`}>
                            <p className="text-xs font-semibold mb-1" style={{ color: reply.is_admin_reply ? '#294c79' : '#1d4ed8' }}>
                              {reply.is_admin_reply ? `👑 ${reply.replier_name} (LiteRent Team)` : '👤 You'}
                            </p>
                            <p className="text-sm text-gray-800">{reply.message}</p>
                            <p className="text-xs text-gray-400 mt-1">{new Date(reply.created_at).toLocaleString('en-NG')}</p>
                          </div>
                        ))}
                      </div>
                      {selectedTicket.status !== 'closed' ? (
                        <div className="p-4 border-t border-gray-100">
                          <textarea value={ticketReply} onChange={e => setTicketReply(e.target.value)} placeholder="Type your reply..." className="w-full px-3 py-2 border-2 border-[#afc09e] rounded-xl text-sm focus:outline-none focus:border-[#294c79] h-20 resize-none" />
                          <button onClick={handleSendTicketReply} disabled={!ticketReply.trim()} className="mt-2 w-full py-2 bg-[#294c79] text-white rounded-xl font-medium text-sm hover:bg-[#1f3a5c] disabled:bg-gray-300">
                            Send Reply
                          </button>
                        </div>
                      ) : (
                        <div className="p-4 border-t text-center text-sm text-gray-500">This ticket is closed. Open a new ticket if you need further assistance.</div>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="mt-8 bg-white p-6 rounded-2xl shadow-md border border-[#e8f2d1]">
                <h3 className="text-xl font-semibold text-[#294c79] mb-4">Other Ways to Reach Us</h3>
                <div className="flex flex-wrap gap-4">
                  <a
                    href="https://wa.me/2349057582412?text=Hello%20LiteRent%20Support%2C%20I%20need%20help%20with..."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-6 py-3 bg-[#25D366] text-white rounded-xl hover:bg-[#20b858] font-medium transition"
                  >
                    <span className="text-xl">💬</span> WhatsApp Support
                  </a>
                  <a
                    href="mailto:support@literent.com.ng"
                    className="flex items-center gap-2 px-6 py-3 bg-[#294c79] text-white rounded-xl hover:bg-[#1f3a5c] font-medium transition"
                  >
                    <span className="text-xl">📧</span> support@literent.com.ng
                  </a>
                </div>
              </div>
            </section>
          )}

          {/* ════ PROFILE & SETTINGS ════ */}
          {activeTab === 'profile' && (
            <section>
              <h1 className="text-3xl md:text-4xl font-bold text-[#294c79] mb-8">Profile & Settings</h1>

              <div className="flex gap-3 mb-10 border-b-2 border-[#e8f2d1] pb-0">
                {['profile', 'settings'].map(tab => (
                  <button
                    key={tab}
                    onClick={() => setProfileSubTab(tab)}
                    className={`px-8 py-3 rounded-t-xl font-semibold text-base transition border-2 border-b-0 -mb-0.5 capitalize ${(profileSubTab === tab || (tab === 'profile' && profileSubTab === 'update')) ? 'bg-[#294c79] text-white border-[#294c79]' : 'bg-white text-[#294c79] border-[#afc09e] hover:border-[#294c79]'}`}
                  >
                    {tab === 'profile' ? '👤 Profile' : '⚙️ Settings'}
                  </button>
                ))}
              </div>

              {(profileSubTab === 'profile' || profileSubTab === 'update') && (
                <>
                  <div className="flex flex-wrap gap-4 mb-8">
                    {['profile', 'update'].map(sub => (
                      <button
                        key={sub}
                        onClick={() => setProfileSubTab(sub)}
                        className={`px-7 py-3 rounded-xl font-medium transition ${profileSubTab === sub ? 'bg-[#a5c250] text-white shadow-md' : 'bg-gray-100 text-[#294c79] hover:bg-gray-200'}`}
                      >
                        {sub === 'profile' ? 'View Profile' : 'Update Profile'}
                      </button>
                    ))}
                  </div>

                  {profileSubTab === 'profile' && (
                    <div className="bg-white p-8 md:p-10 rounded-2xl shadow-md space-y-8">
                      <div className="flex justify-between items-center">
                        <h2 className="text-2xl font-semibold text-[#294c79]">Your Profile</h2>
                        <button onClick={() => setProfileSubTab('update')} className="px-6 py-3 bg-[#294c79] text-white rounded-xl hover:bg-[#1f3a5c]">Edit Profile</button>
                      </div>
                      {profileSaveSuccess ? (
                        <div className="rounded-2xl border border-green-200 bg-green-50 text-green-700 px-4 py-3 text-sm font-medium">
                          ✓ {profileSaveSuccess}
                        </div>
                      ) : null}
                      {profileSaveError ? (
                        <div className="rounded-2xl border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm">
                          {profileSaveError}
                        </div>
                      ) : null}
                      <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${isProfileComplete ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {isProfileComplete ? '✓ Profile Complete' : '⚠ Profile Incomplete — Please update to enable payments'}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                        {[
                          ['Profile Type', profileData.applicantType === 'student' ? 'Student' : profileData.applicantType === 'nysc' ? 'NYSC Corps Member' : 'Employed / Business Owner'],
                          ['Full Name', profileData.fullName],
                          ['Phone', profileData.phone],
                          ['Email', profileData.email],
                          ['Date of Birth', profileData.dateOfBirth],
                          ['Gender', profileData.gender],
                          ['Marital Status', profileData.maritalStatus],
                          ['Dependents', profileData.numberOfDependents],
                          ['Current Address', profileData.currentAddress],
                          ['Previous Address', profileData.previousAddress],
                          ['NIN', profileData.nin ? '••••••••' + profileData.nin.slice(-3) : '—'],
                          ['BVN', profileData.bvn ? '••••••••' + profileData.bvn.slice(-3) : '—'],
                          ['Employment', profileData.employmentStatus],
                          ['Employer', profileData.employerName],
                          ['Job Title', profileData.jobTitle],
                          ['Monthly Income', profileData.monthlyNetIncome ? `₦${Number(profileData.monthlyNetIncome).toLocaleString()}` : '—'],
                          ['Bank', profileData.bankName],
                          ['Account Name', profileData.bankAccountName || '—'],
                          ['Account Number', profileData.accountNumber ? '••••••' + profileData.accountNumber.slice(-4) : '—'],
                          ['Account Type', profileData.accountType],
                          ['Next of Kin', profileData.nextOfKinFullName],
                          ['Next of Kin Phone', profileData.nextOfKinPhone],
                          ['Emergency Contact', profileData.emergencyContactName],
                          ['Emergency Phone', profileData.emergencyContactPhone],
                          ['Guarantor 1', profileData.guarantor1FullName],
                          ['Guarantor 1 Phone', profileData.guarantor1Phone],
                          ['Guarantor 1 Relationship', profileData.guarantor1Relationship],
                          ['Guarantor 1 Address', profileData.guarantor1Address],
                          ['Guarantor 2', profileData.guarantor2FullName],
                          ['Guarantor 2 Phone', profileData.guarantor2Phone],
                          ['Guarantor 2 Relationship', profileData.guarantor2Relationship],
                          ['Guarantor 2 Address', profileData.guarantor2Address],
                          profileData.applicantType === 'student' ? ['Institution', profileData.institutionName] : null,
                          profileData.applicantType === 'student' ? ['Institution Type', profileData.institutionType] : null,
                          profileData.applicantType === 'student' ? ['Faculty / School', profileData.faculty] : null,
                          profileData.applicantType === 'student' ? ['Department / Course', profileData.department] : null,
                          profileData.applicantType === 'student' ? ['Current Level', profileData.studentLevel] : null,
                          profileData.applicantType === 'student' ? ['Matric Number', profileData.matricNumber] : null,
                          profileData.applicantType === 'student' ? ['Parent / Guardian', profileData.parentFullName] : null,
                          profileData.applicantType === 'student' ? ['Parent Phone', profileData.parentPhone] : null,
                          profileData.applicantType === 'student' ? ['Parent Email', profileData.parentEmail] : null,
                          profileData.applicantType === 'student' ? ['Parent Bank', profileData.parentBankName] : null,
                          profileData.applicantType === 'student' ? ['Parent Account Name', profileData.parentBankAccountName] : null,
                          profileData.applicantType === 'nysc' ? ['NYSC State Code', profileData.nyscStateCode] : null,
                          profileData.applicantType === 'nysc' ? ['Call-Up Number', profileData.nyscCallUpNumber] : null,
                          profileData.applicantType === 'nysc' ? ['Batch', profileData.nyscBatch] : null,
                          profileData.applicantType === 'nysc' ? ['Service State', profileData.nyscServiceState] : null,
                          profileData.applicantType === 'nysc' ? ['Service LGA', profileData.nyscServiceLga] : null,
                          profileData.applicantType === 'nysc' ? ['PPA Name', profileData.nyscPpaName] : null,
                          profileData.applicantType === 'nysc' ? ['PPA Address', profileData.nyscPpaAddress] : null,
                          profileData.applicantType === 'nysc' ? ['Allawee Bank', profileData.nyscAllaweeBank] : null,
                          profileData.applicantType === 'nysc' ? ['Allawee Account Name', profileData.nyscAllaweeName] : null,
                        ].filter(Boolean).map(([label, val]) => (
                          <div key={label}>
                            <p className="text-sm text-gray-500">{label}</p>
                            <p className="font-medium text-base mt-0.5">{val || '—'}</p>
                          </div>
                        ))}
                      </div>
                      <div className="mt-6 pt-6 border-t border-gray-100">
                        <h4 className="text-base font-semibold text-[#294c79] mb-4">📎 Uploaded Documents</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {getProfileDocumentsForType(profileData.applicantType).map(([key, label]) => (
                            <div key={key} className={`p-3 rounded-xl border text-center text-xs ${profileData[key] ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
                              <p className="font-medium mb-1">{label}</p>
                              {profileData[key]
                                ? <button onClick={() => openDoc(profileData[key])} className="text-green-600 underline font-semibold">✅ View</button>
                                : <span className="text-gray-400">Not uploaded</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {profileSubTab === 'update' && (
                    <BrandFormShell
                      badge="Tenant profile"
                      title="Complete your LiteRent profile the easy way"
                      subtitle="Move step by step just like Typeform. Save the details we need for verification, payment setup, and your application journey."
                      footer="Your details are used for identity checks, landlord confidence, payment setup, and smoother approvals."
                    >
                      <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between mb-8">
                        <div>
                          <p className="text-sm text-[#1a1a1a]/60">Profile completion status</p>
                          <div className={`mt-2 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold ${isProfileComplete ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                            <span>{isProfileComplete ? '✅' : '⚠️'}</span>
                            <span>{isProfileComplete ? 'Profile complete' : 'More details still needed'}</span>
                          </div>
                        </div>
                        <div className="text-sm text-[#1a1a1a]/60">
                          Step {profileStepIndex + 1} of {PROFILE_WIZARD_STEPS.length}
                        </div>
                      </div>

                      <FormStepper steps={PROFILE_WIZARD_STEPS} currentStep={profileWizardStep} />

                      {profileSaveError ? (
                        <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm">
                          {profileSaveError}
                        </div>
                      ) : null}
                      {profileSaveSuccess ? (
                        <div className="mb-6 rounded-2xl border border-green-200 bg-green-50 text-green-700 px-4 py-3 text-sm">
                          {profileSaveSuccess}
                        </div>
                      ) : null}

                      {/* ── Applicant Type + TypeForm Profile Wizard ── */}
                      {(() => {
                        const atype = profileData.applicantType || '';

                        // ── Step 0: Applicant Type Selector ──────────────────
                        const typeSlide = {
                          key: 'type', title: 'What best describes you?', hint: 'This shapes your profile questions. You can change it later.',
                          content: (
                            <div className="grid gap-4">
                              {[
                                { value: 'employed', icon: '💼', label: 'Employed / Business Owner', desc: 'Full-time employee, self-employed, freelancer, or business owner with regular income.' },
                                { value: 'nysc', icon: '🟢', label: 'NYSC Corps Member', desc: 'Currently serving in the National Youth Service Corps (NYSC).' },
                                { value: 'student', icon: '🎓', label: 'Student', desc: 'Currently enrolled in a tertiary institution (university, polytechnic, college of education).' },
                              ].map(opt => (
                                <button
                                  key={opt.value}
                                  type="button"
                                  onClick={() => { handleProfileChange({ target: { name: 'applicantType', value: opt.value } }); }}
                                  className={`w-full text-left p-5 rounded-2xl border-2 transition-all ${atype === opt.value ? 'border-[#294c79] bg-[#294c79] text-white shadow-lg' : 'border-[#d9e5ef] bg-white hover:border-[#294c79]/60'}`}
                                >
                                  <div className="flex items-center gap-4">
                                    <span className="text-3xl">{opt.icon}</span>
                                    <div>
                                      <p className={`font-bold text-base ${atype === opt.value ? 'text-white' : 'text-[#294c79]'}`}>{opt.label}</p>
                                      <p className={`text-sm mt-0.5 ${atype === opt.value ? 'text-white/80' : 'text-gray-500'}`}>{opt.desc}</p>
                                    </div>
                                    {atype === opt.value && <span className="ml-auto text-2xl">✓</span>}
                                  </div>
                                </button>
                              ))}
                            </div>
                          )
                        };

                        // ── Shared slides (all types) ─────────────────────────
                        const personalSlide = {
                          key: 'personal', title: 'Tell us about you', hint: 'The basics — used across your dashboard and applications.',
                          content: (
                            <div className="space-y-4">
                              <WizardInput label="Full Name" name="fullName" value={profileData.fullName || ''} onChange={handleProfileChange} required />
                              <WizardInput label="Phone Number" name="phone" type="tel" value={profileData.phone || ''} onChange={handleProfileChange} required hint="Use the number you actively answer." />
                              <WizardInput label="Email Address" name="email" type="email" value={profileData.email || ''} onChange={handleProfileChange} required />
                              <WizardInput label="Date of Birth" name="dateOfBirth" type="date" value={profileData.dateOfBirth || ''} onChange={handleProfileChange} required />
                              <div className="grid grid-cols-2 gap-4">
                                <div><label className="block text-sm font-semibold text-[#294c79] mb-2">Gender <span className="text-red-500">*</span></label>
                                  <select name="gender" value={profileData.gender || ''} onChange={handleProfileChange} className="w-full px-4 py-3 rounded-2xl border-2 border-[#d9e5ef] focus:outline-none focus:border-[#294c79] bg-white">
                                    <option value="">Select…</option>
                                    {['Male','Female','Other','Prefer not to say'].map(o => <option key={o} value={o}>{o}</option>)}
                                  </select>
                                </div>
                                <div><label className="block text-sm font-semibold text-[#294c79] mb-2">Marital Status <span className="text-red-500">*</span></label>
                                  <select name="maritalStatus" value={profileData.maritalStatus || ''} onChange={handleProfileChange} className="w-full px-4 py-3 rounded-2xl border-2 border-[#d9e5ef] focus:outline-none focus:border-[#294c79] bg-white">
                                    <option value="">Select…</option>
                                    {['Single','Married','Divorced','Widowed'].map(o => <option key={o} value={o}>{o}</option>)}
                                  </select>
                                </div>
                              </div>
                              <WizardInput label="Number of Dependents" name="numberOfDependents" type="number" min="0" value={profileData.numberOfDependents ?? ''} onChange={handleProfileChange} required hint="Enter 0 if none." />
                            </div>
                          )
                        };

                        const addressSlide = {
                          key: 'address', title: 'Where do you live?', hint: 'Your current residential address.',
                          content: (
                            <div className="space-y-4">
                              <WizardInput label="Current Address" name="currentAddress" value={profileData.currentAddress || ''} onChange={handleProfileChange} required hint="Full address including street, area, and state." />
                              <WizardInput label="Years at Current Address" name="durationAtCurrentAddressYears" type="number" min="0" value={profileData.durationAtCurrentAddressYears || ''} onChange={handleProfileChange} required hint="Use 0 if less than a year." />
                              <WizardInput label="Previous Address" name="previousAddress" value={profileData.previousAddress || ''} onChange={handleProfileChange} required hint="Use N/A if this is your first address." />
                            </div>
                          )
                        };

                        const identitySlide = {
                          key: 'identity', title: 'Identity verification', hint: 'Your NIN and BVN are required for every profile type.',
                          content: (
                            <div className="space-y-4">
                              <WizardInput label="NIN (National ID Number)" name="nin" value={profileData.nin || ''} onChange={handleProfileChange} required maxLength="11" hint="11 digits. Dial *346# to retrieve yours." />
                              <WizardInput label="BVN (Bank Verification Number)" name="bvn" value={profileData.bvn || ''} onChange={handleProfileChange} required maxLength="11" hint="11 digits. Dial *565*0# on any Nigerian network." />
                            </div>
                          )
                        };

                        const bankingSlide = {
                          key: 'banking', title: 'Your bank details', hint: 'Used for direct debit setup and monthly payment processing.',
                          content: (
                            <div className="space-y-4">
                              <div><label className="block text-sm font-semibold text-[#294c79] mb-2">Bank Name <span className="text-red-500">*</span></label>
                                <select name="bankName" value={profileData.bankName || ''} onChange={handleProfileChange} className="w-full px-4 py-3 rounded-2xl border-2 border-[#d9e5ef] focus:outline-none focus:border-[#294c79] bg-white">
                                  <option value="">Select your bank…</option>
                                  {bankOptions.map(b => { const n = typeof b === 'string' ? b : (b.name || b.bank_name || ''); return n ? <option key={n} value={n}>{n}</option> : null; })}
                                </select>
                              </div>
                              <WizardInput label="Account Number" name="accountNumber" value={profileData.accountNumber || ''} onChange={handleProfileChange} required maxLength="10" hint="10-digit account number." />
                              <div className={`p-4 rounded-2xl text-sm font-medium ${resolvingAccountName ? 'bg-[#f3f7fb] text-[#294c79]' : profileData.bankAccountName ? 'bg-[#f8fbf1] border border-[#c8dfa0] text-[#2d6a39]' : 'bg-gray-50 border border-gray-200 text-gray-400'}`}>
                                {resolvingAccountName ? '⏳ Verifying account name…' : profileData.bankAccountName ? `✅ ${profileData.bankAccountName}` : 'Account name will appear here after verification'}
                              </div>
                            </div>
                          )
                        };

                        // NYSC allawee sub-section (shown inside banking slide for nysc type)
                        const nyscAllaweeBankSlide = atype === 'nysc' ? {
                          key: 'nysc_allawee_banking', title: 'NYSC Allawee Account', hint: 'The account where NYSC pays your monthly allawee.',
                          content: (
                            <div className="space-y-4">
                              <div className="p-3 rounded-2xl bg-[#f0f8e8] border border-[#c8dfa0] text-xs text-[#294c79]">
                                💡 Enter the bank account where NYSC deposits your monthly allawee (stipend). This is separate from your direct-debit account above.
                              </div>
                              <div>
                                <label className="block text-sm font-semibold text-[#294c79] mb-2">Allawee Bank <span className="text-red-500">*</span></label>
                                <select name="nyscAllaweeBank" value={profileData.nyscAllaweeBank || ''} onChange={e => { handleProfileChange(e); setProfileData(prev => ({ ...prev, nyscAllaweeName: '' })); }} className="w-full px-4 py-3 rounded-2xl border-2 border-[#d9e5ef] focus:outline-none focus:border-[#294c79] bg-white">
                                  <option value="">Select bank…</option>
                                  {bankOptions.map(b => { const n = typeof b === 'string' ? b : (b.name || b.bank_name || ''); return n ? <option key={n} value={n}>{n}</option> : null; })}
                                </select>
                              </div>
                              <WizardInput label="Allawee Account Number" name="nyscAllaweeAccount" value={profileData.nyscAllaweeAccount || ''} onChange={e => { handleProfileChange(e); setProfileData(prev => ({ ...prev, nyscAllaweeName: '' })); }} required maxLength="10" hint="10-digit NYSC allawee account number." />
                              <div className={`p-4 rounded-2xl text-sm font-medium ${resolvingAccountName ? 'bg-[#f3f7fb] text-[#294c79]' : profileData.nyscAllaweeName ? 'bg-[#f8fbf1] border border-[#c8dfa0] text-[#2d6a39]' : 'bg-gray-50 border border-gray-200 text-gray-400'}`}>
                                {resolvingAccountName ? '⏳ Verifying allawee account name…' : profileData.nyscAllaweeName ? `✅ ${profileData.nyscAllaweeName}` : 'Account name will appear after entering bank and account number'}
                              </div>
                            </div>
                          )
                        } : null;

                        const contactsSlide = {
                          key: 'contacts', title: 'Emergency & next of kin', hint: 'Someone we can reach in an emergency.',
                          content: (
                            <div className="space-y-4">
                              <p className="text-xs font-bold text-[#294c79] uppercase tracking-wide">Next of Kin</p>
                              <WizardInput label="Full Name" name="nextOfKinFullName" value={profileData.nextOfKinFullName || ''} onChange={handleProfileChange} required />
                              <WizardInput label="Phone" name="nextOfKinPhone" value={profileData.nextOfKinPhone || ''} onChange={handleProfileChange} required />
                              <WizardInput label="Relationship" name="nextOfKinRelationship" value={profileData.nextOfKinRelationship || ''} onChange={handleProfileChange} required />
                              <WizardInput label="Address" name="nextOfKinAddress" value={profileData.nextOfKinAddress || ''} onChange={handleProfileChange} required />
                              <div className="h-px bg-[#edf2f7] my-2" />
                              <p className="text-xs font-bold text-[#294c79] uppercase tracking-wide">Emergency Contact</p>
                              <WizardInput label="Full Name" name="emergencyContactName" value={profileData.emergencyContactName || ''} onChange={handleProfileChange} required />
                              <WizardInput label="Phone" name="emergencyContactPhone" value={profileData.emergencyContactPhone || ''} onChange={handleProfileChange} required />
                              <WizardInput label="Relationship" name="emergencyContactRelationship" value={profileData.emergencyContactRelationship || ''} onChange={handleProfileChange} required />
                            </div>
                          )
                        };

                        // ── EMPLOYED-specific slides ────────────────────────
                        const workSlide = {
                          key: 'work', title: 'Your employment', hint: 'Tell us about your work and income.',
                          content: (
                            <div className="space-y-4">
                              <div><label className="block text-sm font-semibold text-[#294c79] mb-2">Employment Status <span className="text-red-500">*</span></label>
                                <select name="employmentStatus" value={profileData.employmentStatus || ''} onChange={handleProfileChange} className="w-full px-4 py-3 rounded-2xl border-2 border-[#d9e5ef] focus:outline-none focus:border-[#294c79] bg-white">
                                  <option value="">Select…</option>
                                  {['Employed','Self-employed','Business Owner','Freelancer','Retired','Unemployed'].map(o => <option key={o} value={o}>{o}</option>)}
                                </select>
                              </div>
                              <WizardInput label="Employer / Company Name" name="employerName" value={profileData.employerName || ''} onChange={handleProfileChange} required hint="Use your business name if self-employed." />
                              <WizardInput label="Job Title" name="jobTitle" value={profileData.jobTitle || ''} onChange={handleProfileChange} required />
                              <WizardInput label="Monthly Net Income (₦)" name="monthlyNetIncome" type="number" min="0" value={profileData.monthlyNetIncome || ''} onChange={handleProfileChange} required hint="After tax and deductions." />
                            </div>
                          )
                        };

                        const businessInfoSlide = {
                          key: 'business_info', title: 'Your business', hint: 'Tell us about your business so we can verify it.',
                          content: (
                            <div className="space-y-4">
                              <WizardInput label="Business Name" name="businessName" value={profileData.businessName || ''} onChange={handleProfileChange} required hint="The registered or trading name of your business." />
                              <WizardInput label="Business Location / Address" name="businessLocation" value={profileData.businessLocation || ''} onChange={handleProfileChange} required hint="Full address where the business operates." />
                              <div><label className="block text-sm font-semibold text-[#294c79] mb-2">Nature of Business <span className="text-red-500">*</span></label>
                                <select name="businessType" value={profileData.businessType || ''} onChange={handleProfileChange} className="w-full px-4 py-3 rounded-2xl border-2 border-[#d9e5ef] focus:outline-none focus:border-[#294c79] bg-white">
                                  <option value="">Select…</option>
                                  {['Retail / Trading','Food & Beverage','Fashion & Clothing','Technology / IT','Transportation / Logistics','Real Estate','Agriculture','Manufacturing','Professional Services','Education & Training','Health & Beauty','Media & Entertainment','Construction','Finance & Consulting','Other'].map(o => <option key={o} value={o}>{o}</option>)}
                                </select>
                              </div>
                              <div><label className="block text-sm font-semibold text-[#294c79] mb-2">Is the business registered with CAC? <span className="text-red-500">*</span></label>
                                <div className="flex gap-3">
                                  {['Yes','No'].map(opt => (
                                    <button key={opt} type="button"
                                      onClick={() => handleProfileChange({ target: { name: 'businessIsRegistered', value: opt } })}
                                      className={`flex-1 py-3 rounded-2xl border-2 font-semibold text-sm transition ${profileData.businessIsRegistered === opt ? 'border-[#294c79] bg-[#294c79] text-white' : 'border-[#d9e5ef] bg-white text-[#294c79] hover:border-[#294c79]/60'}`}
                                    >{opt}</button>
                                  ))}
                                </div>
                              </div>
                              {profileData.businessIsRegistered === 'Yes' && (
                                <WizardInput label="CAC Registration Number" name="cacNumber" value={profileData.cacNumber || ''} onChange={handleProfileChange} required hint="e.g. RC-1234567 or BN-1234567" />
                              )}
                              <WizardInput label="Years in Operation" name="businessYearsInOperation" type="number" min="0" value={profileData.businessYearsInOperation || ''} onChange={handleProfileChange} required hint="How many years has the business been running?" />
                            </div>
                          )
                        };

                        const guarantorSlide = {
                          key: 'guarantors', title: 'Your guarantors', hint: 'Two guarantors are required for every tenant profile type.',
                          content: (
                            <div className="space-y-4">
                              <p className="text-xs font-bold text-[#294c79] uppercase tracking-wide">Guarantor 1 <span className="text-red-500">*</span></p>
                              <WizardInput label="Full Name" name="guarantor1FullName" value={profileData.guarantor1FullName || ''} onChange={handleProfileChange} required />
                              <WizardInput label="Phone" name="guarantor1Phone" value={profileData.guarantor1Phone || ''} onChange={handleProfileChange} required />
                              <WizardInput label="Relationship" name="guarantor1Relationship" value={profileData.guarantor1Relationship || ''} onChange={handleProfileChange} required />
                              <WizardInput label="Occupation" name="guarantor1Occupation" value={profileData.guarantor1Occupation || ''} onChange={handleProfileChange} required />
                              <WizardInput label="Employer" name="guarantor1Employer" value={profileData.guarantor1Employer || ''} onChange={handleProfileChange} required />
                              <WizardInput label="Address" name="guarantor1Address" value={profileData.guarantor1Address || ''} onChange={handleProfileChange} required />
                              <div className="h-px bg-[#edf2f7] my-2" />
                              <p className="text-xs font-bold text-[#294c79] uppercase tracking-wide">Guarantor 2 <span className="text-red-500">*</span></p>
                              <WizardInput label="Full Name" name="guarantor2FullName" value={profileData.guarantor2FullName || ''} onChange={handleProfileChange} required />
                              <WizardInput label="Phone" name="guarantor2Phone" value={profileData.guarantor2Phone || ''} onChange={handleProfileChange} required />
                              <WizardInput label="Relationship" name="guarantor2Relationship" value={profileData.guarantor2Relationship || ''} onChange={handleProfileChange} required />
                              <WizardInput label="Occupation" name="guarantor2Occupation" value={profileData.guarantor2Occupation || ''} onChange={handleProfileChange} required />
                              <WizardInput label="Employer" name="guarantor2Employer" value={profileData.guarantor2Employer || ''} onChange={handleProfileChange} required />
                              <WizardInput label="Address" name="guarantor2Address" value={profileData.guarantor2Address || ''} onChange={handleProfileChange} required />
                            </div>
                          )
                        };

                        // ── STUDENT-specific slides ──────────────────────────
                        const studentInfoSlide = {
                          key: 'student_info', title: 'Your student details', hint: 'Tell us about your institution and programme.',
                          content: (
                            <div className="space-y-4">
                              <WizardInput label="Institution Name" name="institutionName" value={profileData.institutionName || ''} onChange={handleProfileChange} required hint="e.g. University of Ibadan, LASU, Covenant University" />
                              <div><label className="block text-sm font-semibold text-[#294c79] mb-2">Institution Type <span className="text-red-500">*</span></label>
                                <select name="institutionType" value={profileData.institutionType || ''} onChange={handleProfileChange} className="w-full px-4 py-3 rounded-2xl border-2 border-[#d9e5ef] focus:outline-none focus:border-[#294c79] bg-white">
                                  <option value="">Select…</option>
                                  {['Federal University','State University','Private University','Federal Polytechnic','State Polytechnic','Private Polytechnic','College of Education','Monotechnic'].map(o => <option key={o} value={o}>{o}</option>)}
                                </select>
                              </div>
                              <WizardInput label="Faculty / School" name="faculty" value={profileData.faculty || ''} onChange={handleProfileChange} required hint="e.g. Faculty of Engineering" />
                              <WizardInput label="Department / Course" name="department" value={profileData.department || ''} onChange={handleProfileChange} required hint="e.g. Electrical Engineering" />
                              <div><label className="block text-sm font-semibold text-[#294c79] mb-2">Current Level <span className="text-red-500">*</span></label>
                                <select name="studentLevel" value={profileData.studentLevel || ''} onChange={handleProfileChange} className="w-full px-4 py-3 rounded-2xl border-2 border-[#d9e5ef] focus:outline-none focus:border-[#294c79] bg-white">
                                  <option value="">Select…</option>
                                  {['100 Level','200 Level','300 Level','400 Level','500 Level','600 Level','Postgraduate','HND 1','HND 2','ND 1','ND 2'].map(o => <option key={o} value={o}>{o}</option>)}
                                </select>
                              </div>
                              <WizardInput label="Matriculation / Registration Number" name="matricNumber" value={profileData.matricNumber || ''} onChange={handleProfileChange} required hint="As shown on your school ID card." />
                            </div>
                          )
                        };

                        const parentSlide = {
                          key: 'parent', title: 'Parent / Guardian details', hint: 'Monthly rent debits will be taken from this account. Their consent is required.',
                          content: (
                            <div className="space-y-4">
                              <div className="p-3 rounded-2xl bg-amber-50 border border-amber-200 text-xs text-amber-800">
                                ⚠️ <strong>Important:</strong> Since you are a student, LiteRent will debit rent payments from your parent or guardian's bank account. They must provide written consent (upload below).
                              </div>
                              <WizardInput label="Full Name" name="parentFullName" value={profileData.parentFullName || ''} onChange={handleProfileChange} required />
                              <WizardInput label="Phone Number" name="parentPhone" value={profileData.parentPhone || ''} onChange={handleProfileChange} required />
                              <WizardInput label="Email Address" name="parentEmail" value={profileData.parentEmail || ''} onChange={handleProfileChange} required hint="For payment receipts and communication." />
                              <div><label className="block text-sm font-semibold text-[#294c79] mb-2">Relationship to You <span className="text-red-500">*</span></label>
                                <select name="parentRelationship" value={profileData.parentRelationship || ''} onChange={handleProfileChange} className="w-full px-4 py-3 rounded-2xl border-2 border-[#d9e5ef] focus:outline-none focus:border-[#294c79] bg-white">
                                  <option value="">Select…</option>
                                  {['Father','Mother','Guardian','Uncle','Aunt','Sibling','Sponsor'].map(o => <option key={o} value={o}>{o}</option>)}
                                </select>
                              </div>
                              <WizardInput label="Occupation" name="parentOccupation" value={profileData.parentOccupation || ''} onChange={handleProfileChange} required />
                              <WizardInput label="Employer / Business" name="parentEmployer" value={profileData.parentEmployer || ''} onChange={handleProfileChange} required />
                              <WizardInput label="Residential Address" name="parentAddress" value={profileData.parentAddress || ''} onChange={handleProfileChange} required />
                              <WizardInput label="NIN" name="parentNin" value={profileData.parentNin || ''} onChange={handleProfileChange} required maxLength="11" hint="11 digits. Encrypted and stored securely." />
                              <WizardInput label="BVN" name="parentBvn" value={profileData.parentBvn || ''} onChange={handleProfileChange} required maxLength="11" hint="11 digits." />
                              <div><label className="block text-sm font-semibold text-[#294c79] mb-2">Parent's Bank Name <span className="text-red-500">*</span></label>
                                <select name="parentBankName" value={profileData.parentBankName || ''} onChange={handleProfileChange} className="w-full px-4 py-3 rounded-2xl border-2 border-[#d9e5ef] focus:outline-none focus:border-[#294c79] bg-white">
                                  <option value="">Select bank…</option>
                                  {bankOptions.map(b => { const n = typeof b === 'string' ? b : (b.name || b.bank_name || ''); return n ? <option key={n} value={n}>{n}</option> : null; })}
                                </select>
                              </div>
                              <WizardInput label="Parent's Account Number" name="parentAccountNumber" value={profileData.parentAccountNumber || ''} onChange={handleProfileChange} required maxLength="10" hint="10-digit account number for monthly debits." />
                              <div className={`p-4 rounded-2xl text-sm font-medium ${resolvingAccountName ? 'bg-[#f3f7fb] text-[#294c79]' : profileData.parentBankAccountName ? 'bg-[#f8fbf1] border border-[#c8dfa0] text-[#2d6a39]' : 'bg-gray-50 border border-gray-200 text-gray-400'}`}>
                                {resolvingAccountName ? 'Verifying parent account name...' : profileData.parentBankAccountName ? profileData.parentBankAccountName : 'Parent account name will appear after entering bank and account number'}
                              </div>
                            </div>
                          )
                        };

                        // ── NYSC-specific slides ─────────────────────────────
                        const nyscInfoSlide = {
                          key: 'nysc_info', title: 'Your NYSC service details', hint: 'Information from your call-up letter and deployment.',
                          content: (
                            <div className="space-y-4">
                              <div className="p-3 rounded-2xl bg-[#f0f5ff] border border-[#c0d4f5] text-xs text-[#294c79]">
                                💡 You'll find most of these on your NYSC call-up letter, deployment letter, and ID card.
                              </div>
                              <WizardInput label="State Code (e.g. LA/23A/1234)" name="nyscStateCode" value={profileData.nyscStateCode || ''} onChange={handleProfileChange} required hint="Your unique NYSC state code from your call-up letter." />
                              <WizardInput label="Call-Up Number" name="nyscCallUpNumber" value={profileData.nyscCallUpNumber || ''} onChange={handleProfileChange} required />
                              
                              <div><label className="block text-sm font-semibold text-[#294c79] mb-2">Batch <span className="text-red-500">*</span></label>
                                <select name="nyscBatch" value={profileData.nyscBatch || ''} onChange={handleProfileChange} className="w-full px-4 py-3 rounded-2xl border-2 border-[#d9e5ef] focus:outline-none focus:border-[#294c79] bg-white">
                                  <option value="">Select…</option>
                                  {['Batch A Stream 1','Batch A Stream 2','Batch B Stream 1','Batch B Stream 2','Batch C Stream 1','Batch C Stream 2'].map(o => <option key={o} value={o}>{o}</option>)}
                                </select>
                              </div>
                              <WizardInput label="Service State" name="nyscServiceState" value={profileData.nyscServiceState || ''} onChange={handleProfileChange} required hint="The state where you are currently serving." />
                              <WizardInput label="Service LGA" name="nyscServiceLga" value={profileData.nyscServiceLga || ''} onChange={handleProfileChange} required hint="The local government area of your posting." />
                              
                            </div>
                          )
                        };

                        const nyscPpaSlide = {
                          key: 'nysc_ppa', title: 'Place of Primary Assignment (PPA)', hint: 'Where you report for work during your service year.',
                          content: (
                            <div className="space-y-4">
                              <div className="p-3 rounded-2xl bg-[#f0f5ff] border border-[#c0d4f5] text-xs text-[#294c79]">
                                💡 Enter the details of the organisation you were assigned to by NYSC.
                              </div>
                              <WizardInput label="PPA Name" name="nyscPpaName" value={profileData.nyscPpaName || ''} onChange={handleProfileChange} required hint="Full official name of your assigned organisation." />
                              <WizardInput label="PPA Address" name="nyscPpaAddress" value={profileData.nyscPpaAddress || ''} onChange={handleProfileChange} required hint="Physical address of your PPA." />
                              <div><label className="block text-sm font-semibold text-[#294c79] mb-2">PPA Type <span className="text-red-500">*</span></label>
                                <select name="nyscPpaType" value={profileData.nyscPpaType || ''} onChange={handleProfileChange} className="w-full px-4 py-3 rounded-2xl border-2 border-[#d9e5ef] focus:outline-none focus:border-[#294c79] bg-white">
                                  <option value="">Select…</option>
                                  {['Federal Government','State Government','Private Company','NGO / Non-Profit','School / University','Hospital','Bank','Tech Company','Other'].map(o => <option key={o} value={o}>{o}</option>)}
                                </select>
                              </div>

                            </div>
                          )
                        };

                        
                        // ── Documents slide (type-aware) ─────────────────────
                        const docsForType = () => {
                          const base = [
                            ['idDocUrl', 'Government-Issued ID', "NIN slip, passport, driver's license or voter's card", true],
                            ['passportPhotoUrl', 'Passport Photo', 'Recent, clear passport-sized photograph', true],
                            ['utilityBillUrl', 'Utility Bill', 'Recent electricity, water, or internet bill', true],
                            ['guarantor1IdUrl', 'Guarantor 1 ID', 'Govt-issued ID for guarantor 1', true],
                            ['guarantor2IdUrl', 'Guarantor 2 ID', 'Govt-issued ID for guarantor 2', true],
                          ];
                          const employedDocs = [
                            ['proofOfIncomeUrl', 'Proof of Income', 'Payslip, salary letter, or income statement', true],
                            ['bankStatementUrl', 'Bank Statement', 'Last 6 months from your main bank', true],
                            ['employmentLetterUrl', 'Employment / Business Letter', 'HR confirmation or CAC registration', true],
                          ];
                          const studentDocs = [
                            ['studentIdUrl', 'Student ID Card', 'Current year school identity card', true],
                            ['admissionLetterUrl', 'Admission Letter', 'Original or scanned admission letter', true],
                            ['schoolFeesReceiptUrl', 'School Fees Receipt / Course Registration Form', 'Current semester receipt or course reg form', true],
                            ['parentIdUrl', 'Parent/Guardian ID', 'Govt-issued ID of the account holder', true],
                            ['parentConsentUrl', 'Parent/Guardian Consent Letter', 'Signed consent to debit their account', true],
                            ['parentProofOfEmploymentUrl', 'Proof of Parent Employment/Business', 'Employment letter, CAC/business proof, or similar income proof', true],
                            ['parentBankStatementUrl', 'Parent Bank Statement', 'Recent statement for the debit account', true],
                          ];
                          const nyscDocs = [
                            ['nyscCallUpLetterUrl', 'NYSC Call-Up Letter', 'Official NYSC call-up letter', true],
                            ['nyscDeploymentLetterUrl', 'Deployment Letter', 'Letter showing your PPA posting', true],
                            ['nyscIdCardUrl', 'NYSC ID Card', 'Green card or temporary ID', true],
                           
                          ];
                          if (atype === 'student') return [...base, ...studentDocs];
                          if (atype === 'nysc') return [...base, ...nyscDocs];
                          return [...base, ...employedDocs];
                        };

                        const documentsSlide = {
                          key: 'documents', title: 'Upload your documents', hint: 'Clear scans or photos. PDF, JPG, and PNG accepted.',
                          content: (
                            <div className="space-y-3">
                              <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-700 mb-2">
                                Required documents must be uploaded before your application can be processed.
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {docsForType().map(([docKey, label, hint, required]) => (
                                  <UploadCard
                                    key={docKey}
                                    docKey={docKey}
                                    label={label}
                                    hint={hint}
                                    required={required}
                                    url={profileData[docKey]}
                                    uploading={!!docUploading[docKey]}
                                    onUpload={(file, cat) => handleDocUpload(docKey, file, cat)}
                                  />
                                ))}
                              </div>
                            </div>
                          )
                        };

                        // ── Review slide ─────────────────────────────────────
                        const reviewSlide = {
                          key: 'review', title: 'Review and save', hint: 'One final check before saving your profile.',
                          content: (
                            <div className="space-y-2 text-sm">
                              {[
                                ['Type', atype === 'student' ? '🎓 Student' : atype === 'nysc' ? '🟢 NYSC Corps Member' : '💼 Employed/Business'],
                                ['Full Name', profileData.fullName],
                                ['Phone', profileData.phone],
                                ['Email', profileData.email],
                                ['Address', profileData.currentAddress],
                                ['NIN', profileData.nin ? `••••••••${profileData.nin.slice(-3)}` : '—'],
                                ['BVN', profileData.bvn ? `••••••••${profileData.bvn.slice(-3)}` : '—'],
                                atype === 'student' ? ['Institution', profileData.institutionName] : null,
                                atype === 'student' ? ['Level', profileData.studentLevel] : null,
                                atype === 'student' ? ['Parent/Guardian', profileData.parentFullName] : null,
                                atype === 'student' ? ['Parent Bank', profileData.parentBankName] : null,

                                atype === 'nysc' ? ['Service State', profileData.nyscServiceState] : null,
                                atype === 'nysc' ? ['PPA', profileData.nyscPpaName] : null,
                              
                                atype === 'employed' ? ['Employer', profileData.employerName] : null,
                                atype === 'employed' ? ['Monthly Income', profileData.monthlyNetIncome ? `₦${Number(profileData.monthlyNetIncome).toLocaleString()}` : '—'] : null,
                                ['Bank', profileData.bankName],
                                ['Account Name', profileData.bankAccountName],
                              ].filter(Boolean).map(([label, val]) => (
                                <div key={label} className="flex justify-between items-center p-3 rounded-2xl bg-[#fbfcfe] border border-[#edf2f7]">
                                  <span className="text-[#1a1a1a]/45 text-xs uppercase tracking-wide">{label}</span>
                                  <span className="font-semibold text-[#294c79] text-sm">{val || '—'}</span>
                                </div>
                              ))}
                              <div className="mt-3 p-3 rounded-2xl border border-[#e8f2d1] bg-[#f8fdf0] text-xs text-[#294c79]">
                                Saving your profile allows LiteRent to process your applications and manage payments accordingly.
                              </div>
                            </div>
                          )
                        };

                        // ── Build slide list by type ─────────────────────────
                        const slidesByType = {
                          '': [typeSlide],
                          employed: [typeSlide, personalSlide, addressSlide, identitySlide, workSlide, bankingSlide, contactsSlide, guarantorSlide, documentsSlide, reviewSlide],
                          student: [typeSlide, personalSlide, addressSlide, identitySlide, studentInfoSlide, parentSlide, contactsSlide, guarantorSlide, documentsSlide, reviewSlide],
                          nysc: [typeSlide, personalSlide, addressSlide, identitySlide, nyscInfoSlide, nyscPpaSlide, nyscAllaweeBankSlide, contactsSlide, guarantorSlide, documentsSlide, reviewSlide].filter(Boolean),
                        };
                        const profileSlides = slidesByType[atype] || slidesByType[''];
                        const slideKeys = profileSlides.map(s => s.key);
                        const slideIdx = Math.max(0, slideKeys.indexOf(profileWizardStep));
                        const totalSlides = profileSlides.length;
                        const currentSlide = profileSlides[slideIdx] || profileSlides[0];

                        const goToSlide = (key) => {
                          const targetIdx = slideKeys.indexOf(key);
                          // Only allow navigating to steps already visited (at or before current)
                          if (targetIdx !== -1 && targetIdx <= slideIdx) {
                            setProfileWizardStep(key);
                            scrollProfileStepIntoView();
                          }
                        };

                        const handleNext = () => {
                          if (currentSlide.key === 'type' && !atype) { alert('Please select your applicant type to continue.'); return; }
                          const nextKey = slideKeys[slideIdx + 1];
                          if (nextKey) { setProfileWizardStep(nextKey); scrollProfileStepIntoView(); }
                        };

                        const handleSaveAndContinue = async () => {
                          // Save current progress silently then advance
                          setProfileSaveError('');
                          setLoading(true);
                          try {
                            const payload = buildProfilePayload({ ...profileData, applicantType: atype }, { draft: true });
                            await axios.put(`${API_URL}/api/profile`, payload, { headers: getHeaders() });
                            const nextKey = slideKeys[slideIdx + 1];
                            if (nextKey) { setProfileWizardStep(nextKey); scrollProfileStepIntoView(); }
                            else { await saveProfile(); }
                          } catch (err) {
                            setProfileSaveError(err.response?.data?.error || 'Could not save. Please try again.');
                          } finally {
                            setLoading(false);
                          }
                        };

                        return (
                          <div>
                            {/* Progress bar */}
                            <div className="w-full bg-[#e8f2d1] rounded-full h-1.5 mb-4">
                              <div className="h-1.5 rounded-full bg-[#294c79] transition-all duration-500" style={{ width: `${Math.round(((slideIdx + 1) / totalSlides) * 100)}%` }} />
                            </div>

                            {/* Step breadcrumb — completed steps are clickable, future steps greyed out */}
                            <div className="flex flex-wrap gap-1.5 mb-6">
                              {profileSlides.map((slide, idx) => {
                                const isDone = idx < slideIdx;
                                const isCurrent = idx === slideIdx;
                                const isFuture = idx > slideIdx;
                                return (
                                  <button
                                    key={slide.key}
                                    onClick={() => goToSlide(slide.key)}
                                    disabled={isFuture}
                                    className={`px-3 py-1 rounded-full text-xs font-semibold transition border ${
                                      isCurrent ? 'bg-[#294c79] text-white border-[#294c79]'
                                      : isDone ? 'bg-[#e8f2d1] text-[#294c79] border-[#c8dfa0] hover:bg-[#d4ebbb] cursor-pointer'
                                      : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                    }`}
                                    title={isFuture ? 'Complete previous steps first' : isDone ? 'Click to go back' : ''}
                                  >
                                    {isDone ? '✓ ' : ''}{slide.title.split(' ').slice(0, 2).join(' ')}
                                  </button>
                                );
                              })}
                            </div>

                            <div className="mb-1 text-xs text-[#294c79]/50 uppercase tracking-widest font-semibold">Step {slideIdx + 1} of {totalSlides}</div>
                            <h2 id="profile-step-title" className="text-2xl font-extrabold text-[#1a1a2e] mb-1 scroll-mt-24">{currentSlide.title}</h2>
                            <p className="text-sm text-[#1a1a1a]/50 mb-6">{currentSlide.hint}</p>
                            <div className="mb-6">{currentSlide.content}</div>

                            {profileSaveError && (
                              <div className="mb-4 p-3 rounded-2xl bg-red-50 border border-red-200 text-red-700 text-sm">{profileSaveError}</div>
                            )}
                            {profileAutoSaveStatus && (
                              <div className="mb-4 text-xs font-semibold text-[#294c79]/60">{profileAutoSaveStatus}</div>
                            )}

                            <div className="flex flex-wrap gap-3 items-center">
                              {slideIdx > 0 && (
                                <button onClick={goProfilePrev} className="px-6 py-3 rounded-2xl border border-[#d9e5ef] text-[#294c79] font-semibold text-sm hover:bg-gray-50 transition">Back</button>
                              )}
                              {currentSlide.key !== 'review' ? (
                                <>
                                  <button
                                    onClick={handleNext}
                                    className="px-8 py-3.5 rounded-2xl bg-[#294c79] text-white font-semibold hover:bg-[#1f3a5c] shadow-md transition"
                                  >
                                    {currentSlide.key === 'type' ? (atype ? `Continue as ${atype === 'student' ? 'Student' : atype === 'nysc' ? 'Corps Member' : 'Employed'}` : 'Select a type above') : 'Next'}
                                  </button>
                                  {currentSlide.key !== 'type' && (
                                    <button
                                      onClick={handleSaveAndContinue}
                                      disabled={loading}
                                      className="px-6 py-3.5 rounded-2xl bg-[#a5c250] text-white font-semibold hover:bg-[#8bb83a] shadow-sm transition disabled:opacity-50 text-sm"
                                    >
                                      {loading ? 'Saving...' : 'Save & Continue'}
                                    </button>
                                  )}
                                </>
                              ) : (
                                <button onClick={saveProfile} disabled={loading} className="px-8 py-3.5 rounded-2xl bg-[#a5c250] text-white font-bold hover:bg-[#8bb83a] shadow-md transition disabled:opacity-40">
                                  {loading ? 'Saving...' : 'Save Profile'}
                                </button>
                              )}
                              <button onClick={() => setProfileSubTab('profile')} className="px-5 py-3 rounded-2xl border border-[#d9e5ef] text-[#294c79]/60 font-medium text-sm hover:bg-gray-50 transition">Cancel</button>
                            </div>
                          </div>
                        );
                      })()}
                    </BrandFormShell>
                  )}
                </>
              )}

              {profileSubTab === 'settings' && (
                <div className="space-y-8">
                  <div className="bg-white p-8 md:p-10 rounded-2xl shadow-md border border-[#e8f2d1]">
                    <div className="flex items-center gap-3 mb-8">
                      <div className="w-12 h-12 rounded-full bg-[#294c79]/10 flex items-center justify-center text-2xl">🔐</div>
                      <div>
                        <h2 className="text-2xl font-semibold text-[#294c79]">Change Password</h2>
                        <p className="text-sm text-gray-500 mt-1">Secure your account with email OTP verification</p>
                      </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 text-sm text-blue-700">
                      <p className="font-semibold mb-1">📧 Email Verification Required</p>
                      <p>A one-time code will be sent to your email address{profileData.email ? ` (${maskEmail(profileData.email)})` : ''}. Enter it below to confirm the password change.</p>
                    </div>

                    {passwordSuccess && (
                      <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6 rounded-r-xl flex items-center gap-3">
                        <span className="text-green-600 text-xl">✓</span>
                        <p className="text-green-700 font-medium">{passwordSuccess}</p>
                      </div>
                    )}
                    {passwordError && (
                      <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-r-xl flex items-center gap-3">
                        <span className="text-red-600 text-xl">✕</span>
                        <p className="text-red-700 font-medium">{passwordError}</p>
                      </div>
                    )}

                    <form onSubmit={handleChangePassword} className="space-y-6 max-w-lg">
                      <div>
                        <label className="block text-sm font-medium text-[#294c79] mb-2">Current Password <span className="text-red-600">*</span></label>
                        <div className="relative">
                          <input type={showCurrentPassword ? 'text' : 'password'} name="currentPassword" value={passwordData.currentPassword} onChange={handlePasswordChange} placeholder="Enter current password" className="w-full px-5 py-4 pr-14 border-2 border-[#afc09e] rounded-xl focus:outline-none focus:border-[#a5c250]" />
                          <button type="button" onClick={() => setShowCurrentPassword(!showCurrentPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#294c79] text-xl">{showCurrentPassword ? '🙈' : '👁️'}</button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-[#294c79] mb-2">New Password <span className="text-red-600">*</span></label>
                        <div className="relative">
                          <input type={showNewPassword ? 'text' : 'password'} name="newPassword" value={passwordData.newPassword} onChange={handlePasswordChange} placeholder="At least 8 characters" className="w-full px-5 py-4 pr-14 border-2 border-[#afc09e] rounded-xl focus:outline-none focus:border-[#a5c250]" />
                          <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#294c79] text-xl">{showNewPassword ? '🙈' : '👁️'}</button>
                        </div>
                        {passwordData.newPassword && strength && (
                          <div className="mt-3">
                            <div className="flex justify-between text-xs mb-1">
                              <span className="text-gray-500">Password strength</span>
                              <span className={`font-semibold ${strength.label === 'Weak' ? 'text-red-600' : strength.label === 'Fair' ? 'text-yellow-600' : 'text-green-600'}`}>{strength.label}</span>
                            </div>
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${strength.color} ${strength.width}`} />
                            </div>
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-[#294c79] mb-2">Confirm New Password <span className="text-red-600">*</span></label>
                        <div className="relative">
                          <input type={showConfirmPassword ? 'text' : 'password'} name="confirmPassword" value={passwordData.confirmPassword} onChange={handlePasswordChange} placeholder="Re-enter new password" className={`w-full px-5 py-4 pr-14 border-2 rounded-xl focus:outline-none focus:border-[#a5c250] ${passwordData.confirmPassword ? (passwordData.newPassword === passwordData.confirmPassword ? 'border-green-400' : 'border-red-400') : 'border-[#afc09e]'}`} />
                          <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#294c79] text-xl">{showConfirmPassword ? '🙈' : '👁️'}</button>
                        </div>
                        {passwordData.confirmPassword && (
                          <p className={`text-xs mt-2 ${passwordData.newPassword === passwordData.confirmPassword ? 'text-green-600' : 'text-red-600'}`}>
                            {passwordData.newPassword === passwordData.confirmPassword ? '✓ Passwords match' : '✕ Passwords do not match'}
                          </p>
                        )}
                      </div>

                      <div className="bg-gray-50 border-2 border-[#afc09e] rounded-xl p-5">
                        <label className="block text-sm font-semibold text-[#294c79] mb-3">📧 Email OTP Verification <span className="text-red-600">*</span></label>
                        {profileData.email ? (
                          <p className="text-xs text-gray-500 mb-4">OTP will be sent to: <span className="font-semibold">{maskEmail(profileData.email)}</span></p>
                        ) : (
                          <p className="text-xs text-amber-600 mb-4 font-medium">⚠️ No email on your profile. Please update your profile with an email address first.</p>
                        )}
                        <button type="button" onClick={handleSendPasswordOtp} disabled={otpLoading || otpCountdown > 0 || !profileData.email} className={`w-full py-3 rounded-xl font-medium text-sm transition mb-4 ${otpLoading || otpCountdown > 0 || !profileData.email ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-[#a5c250] text-white hover:bg-[#8bb83a]'}`}>
                          {otpLoading ? '⏳ Sending OTP...' : otpCountdown > 0 ? `⏳ Resend OTP in ${otpCountdown}s` : otpSent ? '🔄 Resend OTP to Email' : '📧 Send OTP to Email'}
                        </button>
                        {otpSent && otpEmailHint && (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 text-xs text-green-700">✅ {otpEmailHint}</div>
                        )}
                        {otpSent && (
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-2">Enter 6-digit OTP from your email</label>
                            <input type="text" name="otp" value={passwordData.otp} onChange={handlePasswordChange} placeholder="• • • • • •" maxLength="6" inputMode="numeric" className="w-full px-5 py-4 border-2 border-[#afc09e] rounded-xl focus:outline-none focus:border-[#a5c250] text-center text-2xl font-bold tracking-[0.5em]" />
                            <p className="text-xs text-gray-400 mt-2 text-center">Check your inbox and spam folder. OTP is valid for 10 minutes.</p>
                          </div>
                        )}
                      </div>

                      <button type="submit" disabled={passwordLoading || !otpSent || !passwordData.otp} className={`w-full py-5 rounded-full font-semibold text-lg transition ${passwordLoading || !otpSent || !passwordData.otp ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-[#294c79] text-white hover:bg-[#1f3a5c] shadow-md'}`}>
                        {passwordLoading ? '⏳ Updating Password...' : !otpSent ? '📧 Send OTP First to Continue' : !passwordData.otp ? 'Enter OTP to Continue' : '🔐 Update Password'}
                      </button>
                    </form>
                  </div>
                </div>
              )}
            </section>
          )}

          {/* ════ DOCUMENTS (PDF Upload & View) ════ */}
          {activeTab === 'documents' && (
            <section>
              <div className="flex flex-wrap gap-3 justify-between items-center mb-8">
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold text-[#294c79]">My Documents</h1>
                  <p className="text-[#1a1a1a]/50 text-sm mt-1">Upload PDFs for your LiteRent file. Stored securely on Cloudinary.</p>
                </div>
                <button onClick={loadTenantPdfs} className="px-4 py-2.5 rounded-xl border border-[#294c79] text-[#294c79] text-sm font-medium hover:bg-[#f3f7fb] transition">🔄 Refresh</button>
              </div>

              {/* Upload card */}
              <div className="bg-white rounded-2xl shadow-sm border border-[#e8f2d1] p-6 mb-8">
                <h2 className="font-bold text-[#294c79] text-lg mb-1">Upload a PDF Document</h2>
                <p className="text-sm text-gray-500 mb-5">Upload any PDF — payslips, bank statements, signed forms, etc. Max 20MB per file.</p>
                <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Document Label (optional)</label>
                    <input
                      type="text"
                      placeholder="e.g. Bank Statement June 2025"
                      value={pdfLabel}
                      onChange={e => setPdfLabel(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#294c79]"
                    />
                  </div>
                  <label className={`px-6 py-2.5 rounded-xl text-sm font-semibold cursor-pointer transition whitespace-nowrap ${pdfUploading ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-[#294c79] text-white hover:bg-[#1f3a5c]'}`}>
                    {pdfUploading ? '⏳ Uploading…' : '📤 Select & Upload PDF'}
                    <input
                      type="file"
                      accept="application/pdf,.pdf"
                      className="hidden"
                      disabled={pdfUploading}
                      onChange={e => uploadTenantPdf(e.target.files[0])}
                    />
                  </label>
                </div>
              </div>

              {/* Uploaded PDFs list */}
              {tenantPdfs.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-2xl shadow-sm">
                  <div className="text-5xl mb-4">📄</div>
                  <p className="font-semibold text-lg text-gray-500">No documents uploaded yet</p>
                  <p className="text-sm text-gray-400 mt-1">Your uploaded PDFs will appear here. LiteRent admins can also view them.</p>
                </div>
              ) : (
                <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                    <h3 className="font-semibold text-[#294c79]">Uploaded Documents ({tenantPdfs.length})</h3>
                  </div>
                  <div className="divide-y divide-gray-50">
                    {tenantPdfs.map((log, i) => {
                      // Extract URL from event_detail string
                      const urlMatch = log.event_detail ? log.event_detail.match(/(?:https?:\/\/|gdrive:\/\/)\S+/) : null;
                      const pdfUrl = urlMatch ? urlMatch[0] : null;
                      const title = log.event_title ? log.event_title.replace('PDF Uploaded: ', '') : `Document ${i + 1}`;
                      return (
                        <div key={log.id} className="px-6 py-4 flex items-center justify-between gap-4 hover:bg-gray-50 transition">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center text-xl flex-shrink-0">📄</div>
                            <div className="min-w-0">
                              <div className="font-medium text-[#294c79] truncate">{title}</div>
                              <div className="text-xs text-gray-400 mt-0.5">{log.created_at ? new Date(log.created_at).toLocaleString('en-NG', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</div>
                            </div>
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            {pdfUrl ? (
                              <>
                                <button
                                  onClick={() => openDoc(pdfUrl)}
                                  className="px-4 py-2 bg-[#294c79] text-white rounded-xl text-xs font-semibold hover:bg-[#1f3a5c] transition"
                                >
                                  👁 View
                                </button>
                                <a
                                  href={pdfUrl.replace('/fl_inline/', '/').replace('/raw/upload/fl_inline/', '/raw/upload/')}
                                  download
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="px-4 py-2 border border-[#294c79] text-[#294c79] rounded-xl text-xs font-semibold hover:bg-[#f3f7fb] transition"
                                >
                                  ⬇ Download
                                </a>
                              </>
                            ) : (
                              <span className="text-xs text-gray-400 italic">URL unavailable</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </section>
          )}

        </div>
      </main>

      {/* FIX #8: Password-confirm modal for sensitive tenant actions. */}
      {sensitiveConfirm && (
        <PasswordConfirmModal
          title={sensitiveConfirm.title}
          message={sensitiveConfirm.message}
          confirmButtonLabel={sensitiveConfirm.confirmButtonLabel}
          authToken={localStorage.getItem('authToken')}
          onConfirm={sensitiveConfirm.onConfirm}
          onCancel={() => setSensitiveConfirm(null)}
        />
      )}
    </div>
  );
}
